/**
 * Risify Ring Builder
 * ============================================================================
 *
 * Filtering pipeline (RSFY-648) — verified against the forge-your-ring collection:
 *
 *   collection (forge-your-ring)
 *     → rb-diamond:yes/no              (diamond fork; RSFY-649)
 *     → rb-metal:{m}                   (metal step; respects hidden_metals)
 *     → rb-caret:{c}                   (gold carat step)
 *     → rb-diamond-setting:{s}         (setting style; diamond path; RSFY-641)
 *     → rb-diamond-crt:{ct}            (diamond carat; diamond path)
 *     → variant Stone Quality          (cut/color/clarity bundled — see _parseStoneQualityString)
 *     → variant Engraving              (None / Inside / Outside / Both)
 *
 * Tag access is via:
 *   - _productTags(product)            — parses pipe-delimited `product.tags`
 *   - _tagValue(product, prefix)       — extracts the suffix after `prefix:`
 *
 * Stone quality parsing:
 *   - _normalizeStoneQuality(str)      — strips spaces/dashes, uppercases
 *   - _parseStoneQualityString(str)    — splits bundled "~0.50ct G/H SI" into
 *                                        { carat, color, clarity }
 *
 * Multi-axis diamond model (RSFY-642):
 *   Each ring carries `hasDiamond`, `diamondCarat`, `diamondCut`, `diamondColor`,
 *   `diamondClarity`. Variant matching parses the bundled "Stone Quality" option
 *   into the same axes and gates on each.
 *
 * Sequential multi-ring (RSFY-651):
 *   Single source of truth for which ring is being edited: `currentRingIndex`.
 *   `getCheckedRings()` returns `[currentRingIndex]`; the per-step
 *   "Apply to Ring 1/2/3" checkbox model has been retired in favour of the
 *   top tab strip + Copy-from-Ring-1 shortcut.
 *
 * Validation (RSFY-652):
 *   `showStepValidationStatus(step, kind, message)` paints inline sage/red
 *   feedback under each step after variant resolution.
 *
 * Robust matching (RSFY-647):
 *   `findOptionIndex(product, keyword)` honours an alias whitelist
 *   (stone/diamond/gemstone, engraving/inscription/engrave, carat/caret/...).
 *   `findMatchingVariant(ring)` records `ring._lastVariantFailReason` for the
 *   validation UI. Toggle `debug_mode` in the section schema to surface decision
 *   logs in the browser console.
 */
if (!customElements.get('risify-ring-builder')) {
  // Stone Quality bundles colour + clarity into a single PDP-facing label.
  // The builder UI surfaces just the two labels below; the legacy
  // diamondColor/diamondClarity state fields are derived from the chosen
  // quality so the existing variant matcher (which still reads colour /
  // clarity from the parsed Stone Quality string) keeps working.
  //
  // `color` / `clarity` are the canonical values written back to ring state
  // (kept for backward compatibility). `colors` / `clarities` enumerate
  // every value the catalog's bundled Stone Quality string carries for
  // that tier — used by availability checks so a card stays enabled when
  // ANY of its colour-or-clarity alternatives exist in the current pool.
  // Catalog tiers: High → "G/H SI" (G or H + SI), Premium → "D/F VS"
  // (D or F + VS). The single `color`/`clarity` values are first-listed
  // alternatives so the variant matcher's exact-axis lookups still pass.
  const QUALITY_MAPPING = {
    'High Quality':    { color: 'G', clarity: 'SI', colors: ['G', 'H'], clarities: ['SI'] },
    'Premium Quality': { color: 'D', clarity: 'VS', colors: ['D', 'F'], clarities: ['VS'] },
  };
  const QUALITY_ORDER = ['High Quality', 'Premium Quality'];

  // RSFY-801 — customer-facing names of the four plain-band width cards.
  // One map for every render site so the labels cannot drift apart again.
  // Display names only (client revision 2026-09-04). The Shopify option
  // values behind these widths are still 'Standard 7mm' … 'Petite 3mm' —
  // STYLING_2D_MAP and the variant lookup match on those strings, and the
  // cart line and order keep them, so nothing here may be renamed to match.
  const WIDTH_LABELS = { '7': 'The Movie Ring 7mm', '5': '“Little One” 5mm', '4': 'Petite 4mm', '3': 'Petite 3mm' };

  // ---------------------------------------------------------------------------
  // SIZE TABLE — canonical state stored as UK letter (e.g. 'M', 'M½'); US /
  // EU / mm equivalents are derived for label rendering + the dimensions info
  // strip beneath the size grid. Catalog variants don't carry a Size option
  // (verified against the Contemporary Engagement Ring PDP), so size never
  // affects variant matching; it ships to cart as a line-item property.
  // Range follows Figma 6013:8615 (H .. Z+1, halves) + Halfdan's free-resize
  // policy ("if between sizes, go half a size up").
  // ---------------------------------------------------------------------------
  const SIZE_TABLE = [
    { uk: 'H',   us: '3¾',  eu: '46',   diameter: '14.9', circumference: '46.8' },
    { uk: 'H½',  us: '4',   eu: '46.5', diameter: '15.1', circumference: '47.4' },
    { uk: 'I',   us: '4¼',  eu: '47',   diameter: '15.3', circumference: '48.0' },
    { uk: 'I½',  us: '4½',  eu: '47.5', diameter: '15.5', circumference: '48.7' },
    { uk: 'J',   us: '4¾',  eu: '48',   diameter: '15.7', circumference: '49.3' },
    { uk: 'J½',  us: '5',   eu: '48.5', diameter: '15.9', circumference: '50.0' },
    { uk: 'K',   us: '5¼',  eu: '49',   diameter: '16.1', circumference: '50.6' },
    { uk: 'K½',  us: '5½',  eu: '49.5', diameter: '16.3', circumference: '51.2' },
    { uk: 'L',   us: '5¾',  eu: '50',   diameter: '16.5', circumference: '51.8' },
    { uk: 'L½',  us: '6',   eu: '50.5', diameter: '16.7', circumference: '52.5' },
    { uk: 'M',   us: '6¼',  eu: '51',   diameter: '16.9', circumference: '53.1' },
    { uk: 'M½',  us: '6½',  eu: '51.5', diameter: '17.0', circumference: '53.4' },
    { uk: 'N',   us: '6¾',  eu: '52',   diameter: '17.3', circumference: '54.4' },
    { uk: 'N½',  us: '7',   eu: '52.5', diameter: '17.5', circumference: '55.0' },
    { uk: 'O',   us: '7¼',  eu: '53',   diameter: '17.7', circumference: '55.6' },
    { uk: 'O½',  us: '7½',  eu: '54',   diameter: '17.9', circumference: '56.2' },
    { uk: 'P',   us: '7¾',  eu: '54.5', diameter: '18.1', circumference: '56.9' },
    { uk: 'P½',  us: '8',   eu: '55',   diameter: '18.3', circumference: '57.5' },
    { uk: 'Q',   us: '8¼',  eu: '55.5', diameter: '18.5', circumference: '58.1' },
    { uk: 'Q½',  us: '8½',  eu: '56',   diameter: '18.7', circumference: '58.7' },
    { uk: 'R',   us: '8¾',  eu: '57',   diameter: '18.9', circumference: '59.4' },
    { uk: 'R½',  us: '9',   eu: '57.5', diameter: '19.1', circumference: '60.0' },
    { uk: 'S',   us: '9¼',  eu: '58',   diameter: '19.4', circumference: '60.6' },
    { uk: 'S½',  us: '9½',  eu: '58.5', diameter: '19.6', circumference: '61.2' },
    { uk: 'T',   us: '9¾',  eu: '59',   diameter: '19.8', circumference: '61.9' },
    { uk: 'T½',  us: '10',  eu: '60',   diameter: '20.0', circumference: '62.5' },
    { uk: 'U',   us: '10¼', eu: '60.5', diameter: '20.2', circumference: '63.1' },
    { uk: 'U½',  us: '10½', eu: '61',   diameter: '20.4', circumference: '63.8' },
    { uk: 'V',   us: '10¾', eu: '61.5', diameter: '20.6', circumference: '64.4' },
    { uk: 'V½',  us: '11',  eu: '62',   diameter: '20.8', circumference: '65.0' },
    { uk: 'W',   us: '11¼', eu: '63',   diameter: '21.0', circumference: '65.6' },
    { uk: 'W½',  us: '11½', eu: '63.5', diameter: '21.2', circumference: '66.3' },
    { uk: 'X',   us: '11¾', eu: '64',   diameter: '21.4', circumference: '66.9' },
    { uk: 'X½',  us: '12',  eu: '64.5', diameter: '21.6', circumference: '67.5' },
    { uk: 'Y',   us: '12¼', eu: '65',   diameter: '21.8', circumference: '68.2' },
    { uk: 'Y½',  us: '12½', eu: '66',   diameter: '22.0', circumference: '68.8' },
    { uk: 'Z',   us: '12¾', eu: '66.5', diameter: '22.2', circumference: '69.4' },
    { uk: 'Z½',  us: '13',  eu: '67',   diameter: '22.4', circumference: '70.1' },
    // Past-Z½ rows used solely for US 13½ and EU 68/69/70 to match the
    // original Artistry Ring Builder lists. UK labels are placeholders —
    // these rows are hidden from the UK tab via `_paintSizeRegion` so the
    // UK list still ends at Z½ (matches the live PDP size chart). The
    // diameter/circumference numbers extend the standard ring-size linear
    // interpolation (Z½ = 22.4mm, then +0.2mm/row).
    { uk: 'Z+1', us: '13¼', eu: '68',   diameter: '22.6', circumference: '70.7' },
    { uk: 'Z+2', us: '13½', eu: '69',   diameter: '22.8', circumference: '71.6' },
    { uk: 'Z+3', us: '13¾', eu: '70',   diameter: '23.0', circumference: '72.3' },
  ];
  const SIZE_TABLE_BY_UK = SIZE_TABLE.reduce((acc, row) => { acc[row.uk] = row; return acc; }, {});

  // ===========================================================================
  // Diamond-weight manifest — drives the Stone step's discrete carat pills.
  // ---------------------------------------------------------------------------
  // The 3D viewer is the single hero preview; the old 2D "Setting detail"
  // reference panel (and its 197-* studio stills) was retired. Only the
  // `role:'diamondWeight'` layers are still consumed — `_styling2dWeightsForSetting`
  // reads each option's `select` weight label ('1/3ct'|'1/2ct'|'1/6ct') to build
  // the per-metal × per-setting carat pill set. The `image` fields, and the
  // `setting`/`bandWidth`/`metalBase` layers, are no longer used by any code
  // path (kept inline only so the weight data stays a single literal). The
  // Sleek "1/6ct" label maps to the real 0.10ct via WEIGHT_TO_CARAT.
  // ===========================================================================
  const STYLING_2D_MAP = [{"z":0,"role":"metalBase","metal":null,"setting":null,"box":{"l":251.08,"t":176.51,"w":300,"h":212.4},"options":[{"select":"Red Gold","image":"197-83753-b8723122d.webp"},{"select":"Yellow Gold","image":"197-83755-02389f9da.webp"},{"select":"White Gold","image":"197-83757-b7b5e09da.webp"},{"select":"Platinum","image":"197-83759-8d0d918d1.webp"}]},{"z":2,"role":"setting","metal":"Red Gold","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Contemporary Setting","image":"197-83727-c8a6f74c0.png"},{"select":"Modern Setting","image":"197-83728-55f94f7ae.webp"},{"select":"Classic Setting","image":"197-83729-d78d36e58.webp"},{"select":"Elegant Setting","image":"197-83730-5538b8b13.webp"},{"select":"Sleek Setting","image":"197-83790-d8ea2c0f7.webp"},{"select":"Petite Setting","image":"197-83731-fd31e6df9.webp"}]},{"z":3,"role":"setting","metal":"Yellow Gold","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Contemporary Setting","image":"197-83761-b2f6a20d0.webp"},{"select":"Modern Setting","image":"197-83765-217531705.webp"},{"select":"Classic Setting","image":"197-83767-573d34cbf.webp"},{"select":"Elegant Setting","image":"197-83769-27a81f1b5.webp"},{"select":"Sleek Setting","image":"197-83789-e9dd0cfed.png"},{"select":"Petite Setting","image":"197-83771-b16c7c18f.webp"}]},{"z":4,"role":"setting","metal":"White Gold","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Contemporary Setting","image":"197-83737-b551fac4f.webp"},{"select":"Modern Setting","image":"197-83738-384640971.webp"},{"select":"Classic Setting","image":"197-83739-681eae78b.webp"},{"select":"Elegant Setting","image":"197-83740-57811d938.webp"},{"select":"Sleek Setting","image":"197-83803-0cee5e908.png"},{"select":"Petite Setting","image":"197-83741-921942ac3.webp"}]},{"z":5,"role":"setting","metal":"Platinum","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Contemporary Setting","image":"197-83742-a55c1c851.webp"},{"select":"Modern Setting","image":"197-83743-be424600d.webp"},{"select":"Classic Setting","image":"197-83744-e17e08a3b.webp"},{"select":"Elegant Setting","image":"197-83745-da28a1bf7.webp"},{"select":"Sleek Setting","image":"197-83803-0cee5e908.png"},{"select":"Petite Setting","image":"197-83746-d602ff732.webp"}]},{"z":6,"role":"diamondWeight","metal":"Red Gold","setting":"Contemporary","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83791-d9f43dcb5.webp"},{"select":"1/2ct","image":"197-83792-21d734462.webp"}]},{"z":7,"role":"diamondWeight","metal":"Red Gold","setting":"Modern","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83793-4854e8de3.webp"},{"select":"1/2ct","image":"197-83794-e2488a96b.webp"}]},{"z":8,"role":"diamondWeight","metal":"Red Gold","setting":"Classic","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/2","image":"197-83795-effe0e850.webp"}]},{"z":9,"role":"diamondWeight","metal":"Red Gold","setting":"Elegant","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83796-bf5c04089.webp"}]},{"z":10,"role":"diamondWeight","metal":"Red Gold","setting":"Sleek","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/6ct","image":"197-83790-d8ea2c0f7.webp"}]},{"z":11,"role":"diamondWeight","metal":"Red Gold","setting":"Petite","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83797-8371c1939.webp"}]},{"z":12,"role":"diamondWeight","metal":"Yellow Gold","setting":"Contemporary","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83773-d7dbd8727.webp"},{"select":"1/2ct","image":"197-83773-d7dbd8727.webp"}]},{"z":13,"role":"diamondWeight","metal":"Yellow Gold","setting":"Modern","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83776-ea9bb7bfd.webp"},{"select":"1/2ct","image":"197-83778-fc62f9cda.webp"}]},{"z":14,"role":"diamondWeight","metal":"Yellow Gold","setting":"Classic","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/2ct","image":"197-83780-041cd908f.webp"}]},{"z":15,"role":"diamondWeight","metal":"Yellow Gold","setting":"Elegant","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83782-e83cd8ea0.webp"}]},{"z":16,"role":"diamondWeight","metal":"Yellow Gold","setting":"Sleek","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/6ct","image":"197-83789-e9dd0cfed.png"}]},{"z":17,"role":"diamondWeight","metal":"Yellow Gold","setting":"Petite","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83784-300babd2e.webp"}]},{"z":18,"role":"diamondWeight","metal":"White Gold","setting":"Contemporary","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83798-c3f9aa966.webp"},{"select":"1/2ct","image":"197-83798-c3f9aa966.webp"}]},{"z":19,"role":"diamondWeight","metal":"White Gold","setting":"Modern","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83799-eb5d532aa.webp"},{"select":"1/2ct","image":"197-83800-79e2d0776.webp"}]},{"z":20,"role":"diamondWeight","metal":"White Gold","setting":"Classic","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/2ct","image":"197-83801-123e54525.webp"}]},{"z":21,"role":"diamondWeight","metal":"White Gold","setting":"Elegant","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83802-404fa55f2.webp"}]},{"z":22,"role":"diamondWeight","metal":"White Gold","setting":"Sleek","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/6ct","image":"197-83803-0cee5e908.png"}]},{"z":23,"role":"diamondWeight","metal":"White Gold","setting":"Petite","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83804-7bd32c1fb.webp"}]},{"z":24,"role":"diamondWeight","metal":"Platinum","setting":"Contemporary","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83805-3620e6ade.webp"},{"select":"1/2ct","image":"197-83805-3620e6ade.webp"}]},{"z":25,"role":"diamondWeight","metal":"Platinum","setting":"Modern","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83806-e2a5ecb12.webp"},{"select":"1/2ct","image":"197-83807-c181b9b14.webp"}]},{"z":26,"role":"diamondWeight","metal":"Platinum","setting":"Classic","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/2ct","image":"197-83808-5b46b4bc5.webp"}]},{"z":27,"role":"diamondWeight","metal":"Platinum","setting":"Elegant","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83809-2d5e64d5f.webp"}]},{"z":28,"role":"diamondWeight","metal":"Platinum","setting":"Sleek","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/6ct","image":"197-83810-997804c38.png"}]},{"z":29,"role":"diamondWeight","metal":"Platinum","setting":"Petite","box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"1/3ct","image":"197-83811-ebe32c1b5.webp"}]},{"z":35,"role":"bandWidth","metal":"Red Gold","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Standard 7mm","image":"197-83815-81ad442c8.webp"},{"select":"Narrow 5mm","image":"197-83814-8f81f83da.webp"},{"select":"Slim 4mm","image":"197-83813-38a29d6cd.webp"},{"select":"Petite 3mm","image":"197-83812-9c436cf4a.webp"}]},{"z":36,"role":"bandWidth","metal":"Yellow Gold","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Standard 7mm","image":"197-83816-028ae2f62.webp"},{"select":"Narrow 5mm","image":"197-83819-81ca9f8e7.webp"},{"select":"Slim 4mm","image":"197-83821-a870b2b09.webp"},{"select":"Petite 3mm","image":"197-83823-0cb95625c.webp"}]},{"z":37,"role":"bandWidth","metal":"White Gold","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Standard 7mm","image":"197-83825-e8546011b.webp"},{"select":"Narrow 5mm","image":"197-83826-5da5a59a6.webp"},{"select":"Slim 4mm","image":"197-83827-a00e53352.webp"},{"select":"Petite 3mm","image":"197-83828-12ebc5bb5.webp"}]},{"z":38,"role":"bandWidth","metal":"Platinum","setting":null,"box":{"l":250,"t":193.8,"w":300,"h":212.4},"options":[{"select":"Standard 7mm","image":"197-83829-5df6f9ebf.webp"},{"select":"Narrow 5mm","image":"197-83830-bec1dddd0.webp"},{"select":"Slim 4mm","image":"197-83831-76709a539.webp"},{"select":"Petite 3mm","image":"197-83833-0735a6d6e.webp"}]}];

  // ===========================================================================
  // Setting × carat → 3D GLB model map (customer-supplied, from the model sheet)
  //
  // The catalogue ships NO 3d_variant_* metafields today, so `get3DModelUrl`
  // used to fall back to a single hardcoded diamond GLB regardless of the
  // customer's setting/carat pick — the preview never changed with the
  // silhouette. This map wires the customer's real per-setting GLBs so the
  // 3D hero reflects the chosen setting (and its carat, where a distinct
  // render exists).
  //
  // Metal is applied at runtime via .pmat, so ONE GLB per setting × carat
  // covers all four metals — no per-metal entries needed. Keyed by
  // `ring.settingStyle` (lowercase) then by the discrete carat value that
  // `ring.diamondCarat` carries (0.33 / 0.50 / 0.10). `default` is the
  // nearest-carat fallback when the exact carat has no distinct model.
  //
  // Coverage note (customer sheet): only these 6 settings have models.
  // Bezel / Crown / Gypsy have NO supplied GLB — those fall through to the
  // legacy hardcoded diamond fallback in `get3DModelUrl` until the customer
  // supplies renders. This is intentional and honest, not silent truncation.
  // ===========================================================================
  const MODEL_CDN = 'https://cdn.shopify.com/s/files/1/0212/3526/files/';
  // Placeholder for a model that lives in the theme's assets/ rather than the
  // store's Files. The map is built at module scope, before any element exists,
  // so the real URL (which only Liquid's asset_url knows) is substituted when a
  // lookup happens — see resolveSettingModelUrl.
  const CONTEMPORARY_PT33_SENTINEL = '__ASSET__contemporary-pt33';

  // ===========================================================================
  // Default engraving state.
  //
  // Client revision 2026-08-11: "Add Engraving" is pre-selected, so the step
  // opens on the engraving path rather than on Skip.
  //
  // Client revision 2026-09-04, reversing the "starts empty" rule that the
  // same client set on 2026-08-11: the step opens with outside engraving on,
  // the language set to Tengwar and the verse already written. Those values
  // are NOT set here — `_applyDefaultEngraving` writes them when the customer
  // first opens the Engraving step. Engraving is a PRICED variant axis
  // (~US$90 a side), and setting them at ring creation put that charge in the
  // total while the Style step's width cards still showed the base price, so
  // the two disagreed before the customer had seen anything about engraving.
  // ===========================================================================
  const DEFAULT_OUTSIDE_VERSE =
    'One ring to show our love, One ring to bind us, ' +
    'One ring to seal our love, And forever to entwine us';

  const makeDefaultEngravingDetails = () => ({
    hasEngraving: true,
    outsideEngraving: false,
    outsideLanguage: null,
    outsideText: '',
    outsideFont: 'Lucida Handwriting',
    insideEngraving: false,
    insideLanguage: null,
    insideText: '',
    insideFont: 'Lucida Handwriting',
    hallmark: true,
  });

  const SETTING_MODEL_MAP = {
    contemporary: {
      // TEMPORARY (client instruction, Halfdan 2026-08-05): 1/3 ct shows the
      // 1/2 ct render until Justin delivers the missing file.
      //
      // Contemporary's renders belong to the `elvish…b4mm` family (Petite is
      // the same family at b3mm — "aka Contemporary Slim"). The 1/3 ct member,
      // E_round_elvish_pt33ct_b4mm_s7.glb, does not exist on the CDN (404), so
      // this entry had been standing in with E_round_4prong_pt33ct_b4mm_s7 —
      // a different setting style, which is why only Contemporary looked wrong.
      // Showing the correct style at the wrong stone size is the better of the
      // two errors, which is what the client asked for.
      //
      // RESOLVED 2026-08-11: the client supplied the missing 1/3 ct render, so
      // 0.33 now has its own model again. It is a THEME ASSET (sentinel below,
      // swapped for the real URL at runtime) rather than another Files URL —
      // the original defect was a Files URL that 404'd, and an asset committed
      // to the repo cannot vanish from under the theme.
      //
      // Delivered as E_round_4prong_… despite Contemporary belonging to the
      // `elvish` family. The name was the earlier stand-in's, so it was
      // checked by rendering rather than trusted: at 1/3 ct it shows the same
      // setting as the 1/2 ct render with a visibly smaller stone, which is
      // what this entry needs. Draco-compressed copies were also supplied and
      // are unusable — the viewer ships no Draco decoder.
      0.33: CONTEMPORARY_PT33_SENTINEL,
      0.50: MODEL_CDN + 'E_round_elvish_pt50ct_b4mm_s7.glb?v=1679936001',
      default: MODEL_CDN + 'E_round_elvish_pt50ct_b4mm_s7.glb?v=1679936001',
    },
    modern: {
      0.33: MODEL_CDN + 'E_round_modern_pt33ct_b4mm_s7_D_3_2.glb?v=1679472149',
      0.50: MODEL_CDN + 'E_round_modern_pt50ct_b4mm_s7_D_2.glb?v=1679472202',
      default: MODEL_CDN + 'E_round_modern_pt33ct_b4mm_s7_D_3_2.glb?v=1679472149',
    },
    elegant: {
      0.33: MODEL_CDN + 'E_round_elegant_pt33ct_b4mm_s7.glb?v=1679935999',
      default: MODEL_CDN + 'E_round_elegant_pt33ct_b4mm_s7.glb?v=1679935999',
    },
    sleek: {
      // Sheet lists Sleek at 1/10ct; the builder carries this as 0.10.
      0.10: MODEL_CDN + 'E_sleek_flush_pt06ct_b4mm_s7_D_3_1.glb?v=1679472432',
      default: MODEL_CDN + 'E_sleek_flush_pt06ct_b4mm_s7_D_3_1.glb?v=1679472432',
    },
    classic: {
      0.50: MODEL_CDN + 'E_round_6prong_pt50ct_b4mm_s7_D__.1v2glb.glb?v=1680583231',
      default: MODEL_CDN + 'E_round_6prong_pt50ct_b4mm_s7_D__.1v2glb.glb?v=1680583231',
    },
    petite: {
      // Petite ships on a 3mm band per the sheet ("aka Contemporary Slim").
      0.33: MODEL_CDN + 'E_round_elvish_pt33ct_b3mm_s7.glb?v=1679936000',
      default: MODEL_CDN + 'E_round_elvish_pt33ct_b3mm_s7.glb?v=1679936000',
    },
  };

  /**
   * Resolve a customer-supplied GLB for the active ring's setting × carat.
   * Returns a URL string, or null when the setting isn't covered by the
   * sheet (Bezel/Crown/Gypsy) so the caller can fall through to its legacy
   * fallback. Carat is matched to the nearest discrete key it has a model
   * for, then `default`.
   */
  function resolveSettingModelUrl(settingStyle, diamondCarat, host) {
    if (!settingStyle) return null;
    // Swap the theme-asset sentinel for the URL the section passed in. Falls
    // back to the 1/2 ct render if the attribute is missing, so a stale
    // template can never leave the preview blank.
    const deSentinel = (url) => {
      if (url !== CONTEMPORARY_PT33_SENTINEL) return url;
      const fromHost = host && host.dataset && host.dataset.modelContemporaryPt33;
      return fromHost || (MODEL_CDN + 'E_round_elvish_pt50ct_b4mm_s7.glb?v=1679936001');
    };
    const bySetting = SETTING_MODEL_MAP[String(settingStyle).toLowerCase()];
    if (!bySetting) return null;
    // Exact carat first (round to 2dp so 0.3300000001 still matches).
    if (diamondCarat != null) {
      const key = Number(diamondCarat).toFixed(2);
      // Keys are numeric literals (0.33, 0.5, 0.1); compare on toFixed(2).
      for (const k of Object.keys(bySetting)) {
        if (k !== 'default' && Number(k).toFixed(2) === key) return deSentinel(bySetting[k]);
      }
      // Nearest available carat when the exact one has no distinct render.
      const numeric = Object.keys(bySetting)
        .filter((k) => k !== 'default')
        .map(Number)
        .sort((a, b) => Math.abs(a - diamondCarat) - Math.abs(b - diamondCarat));
      if (numeric.length) return deSentinel(bySetting[numeric[0]] || bySetting.default) || null;
    }
    return deSentinel(bySetting.default) || null;
  }

  // ===========================================================================
  // Band width → 3D GLB model map (customer-supplied, "Movie Ring" band sheet)
  //
  // Band-only path counterpart to SETTING_MODEL_MAP. Previously the band-only
  // branch of `get3DModelUrl` always returned the single 7mm GLB regardless of
  // the customer's Band Width pick, so the preview never changed on the Band
  // Only path (the diamond path already switched via SETTING_MODEL_MAP). The
  // customer supplied one plain-band GLB per width; metal is applied at runtime
  // via .pmat so a single GLB per width covers all metals. Keyed by
  // `ring.bandWidth` ('7'|'5'|'4'|'3' — the mm value as a string).
  //   7mm → Movie Ring "Original"    5mm → "Little One" (5.5mm GLB)
  //   4mm → Movie Ring "Petite" 4mm  3mm → Movie Ring "Petite" 3mm
  // ===========================================================================
  const BAND_MODEL_MAP = {
    '7': MODEL_CDN + 'W_b7mm_s10_D_3v1.glb?v=1680583846',
    '5': MODEL_CDN + 'W_b5pt5mm_s10__D_2V1.glb?v=1680588555',
    '4': MODEL_CDN + 'W_b4mm_s7__D_12.glb?v=1680588057',
    '3': MODEL_CDN + 'W_b3mm_s7__D_2V1.glb?v=1680588247',
  };

  /**
   * Resolve a customer-supplied plain-band GLB for the active ring's band
   * width. Returns a URL string, or null when the width has no supplied model
   * so the caller can fall through to its legacy 7mm fallback. `bandWidth` is
   * the mm value the builder stores ('7'|'5'|'4'|'3'); accepts number too.
   */
  function resolveBandModelUrl(bandWidth) {
    if (bandWidth == null || bandWidth === '') return null;
    const key = String(parseInt(bandWidth, 10));
    return BAND_MODEL_MAP[key] || null;
  }

  // Map a ring's internal metalType ('Red Gold' | 'Rose Gold' | 'Yellow Gold' |
  // 'White Gold' | 'Platinum' | 'Palladium') to the 2D map's 4 canonical metal
  // keys. Distinct from the 3D METAL_MAP (which collapses White Gold+Platinum).
  const STYLING_2D_METAL = {
    'Red Gold': 'Red Gold', 'Rose Gold': 'Red Gold',
    'Yellow Gold': 'Yellow Gold',
    'White Gold': 'White Gold',
    'Platinum': 'Platinum', 'Palladium': 'Platinum',
  };

  // Capitalised setting key used by the diamondWeight layers ('contemporary'
  // → 'Contemporary'); the setting layers match on the "<X> Setting" select.
  const STYLING_2D_SETTING_TITLE = {
    contemporary: 'Contemporary', modern: 'Modern', classic: 'Classic',
    elegant: 'Elegant', sleek: 'Sleek', petite: 'Petite',
  };

  // Normalise a weight `select` for comparison: drop the trailing "ct" so the
  // source's "1/2" (Red Gold Classic) and "1/2ct" (others) compare equal.
  const _normWeight = (s) => String(s || '').replace(/ct$/i, '').trim();

  class RisifyRingBuilder extends HTMLElement {
    constructor() {
      super();

      // State
      this.currentDesignId = null;
      this.ringCount = 1;
      this.rings = [];
      this.designProducts = {};
      this.config = {};

      // Step management
      this.stepOrder = ['metal', 'diamond', 'engraving', 'size', 'review'];
      this.currentStepIndex = 0;
      this.visitedSteps = new Set(['metal']);

      // Bug D — fit-indicator re-enabled. Engine, paint code, indicator
      // markup, and CSS were already wired; only the gate flag was off.
      // Customer asked the "how much text fits on the ring" UX back in.
      this._fitIndicatorsEnabled = true;
    }

    connectedCallback() {
      this.config = window.ringBuilderConfig || {};
      this.designProducts = window.ringBuilderProducts || {};
      this._debug = !!this.config.debug;

      // Cache DOM refs
      this.landing = this.querySelector('[data-landing]');
      this.editorOverlay = this.querySelector('[data-editor-overlay]');
      this.editorClose = this.querySelector('[data-editor-close]');
      this.editorTitle = this.querySelector('[data-editor-title]');
      this.controlsColumn = this.querySelector('[data-controls-column]');
      this.viewersContainer = this.querySelector('[data-ring-viewers-container]');
      this.ringViewers = [];

      // The 3D viewer is the sole hero preview. `previewMetalLabel` is the
      // live metal chip ("Red Gold") shown over the 3D preview.
      this.previewMetalLabel = this.querySelector('[data-preview-metal-label]');

      // Step pages
      this.stepPages = {};
      this.stepOrder.forEach((step) => {
        this.stepPages[step] = this.querySelector(`[data-step="${step}"]`);
      });

      // Stepper
      this.stepperSteps = this.querySelectorAll('[data-stepper-step]');
      this.stepperLines = this.querySelectorAll('[data-stepper-line]');

      // Navigation
      this.navBack = this.querySelector('[data-nav-back]');
      this.navContinue = this.querySelector('[data-nav-continue]');
      this.navContinueMobile = this.querySelector('[data-nav-continue-mobile]');

      // Summary Rail
      this.railSpecs = this.querySelector('[data-rail-specs]');
      this.railTotal = this.querySelector('[data-rail-total]');
      this.railAfterPay = this.querySelector('[data-rail-afterpay]');

      // Metal step refs
      this.metalTypesContainer = this.querySelector('[data-metal-types]');
      this.caratSection = this.querySelector('[data-carat-section]');
      this.caratPills = this.querySelector('[data-carat-pills]');
      this.metalSelectedLabel = this.querySelector('[data-metal-selected]');
      this.metalSelectedValue = this.querySelector('[data-metal-selected-value]');

      // Diamond step refs
      this.diamondPills = this.querySelector('[data-diamond-pills]');

      // Engraving step refs
      this.engravingCards = this.querySelector('[data-engraving-cards]');
      this.engravingDetails = this.querySelector('[data-engraving-details]');

      // Size step refs
      this.sizeGrid = this.querySelector('[data-size-grid]');
      this.sizeInfo = this.querySelector('[data-size-info]');
      this.widthOptions = this.querySelector('[data-width-options]');

      // Review step refs
      this.cartSummary = this.querySelector('[data-cart-summary]');
      // [data-add-to-cart] used to live inside the review-slip; the inline
      // review actions row was retired (Edit-specs lives in the slip header,
      // Send-to-workshop is the footer Continue button which is wired via
      // goToNextStep -> addToCart, NOT via a direct click listener here).
      // Stays null on the new template — every consumer guards on it.
      this.addToCartBtn = this.querySelector('[data-add-to-cart]');
      this._addToCartBtnIsDedicated = !!this.addToCartBtn;
      this.errorMessage = this.querySelector('[data-error-message]');
      this.reviewEdit = this.querySelector('[data-review-edit]');

      // Footer
      this.footerTotal = this.querySelector('[data-footer-total]');
      this.footerTotalMobile = this.querySelector('[data-footer-total-mobile]');

      this.bindEvents();
    }

    bindEvents() {
      // Card buttons → open editor
      this.querySelectorAll('[data-design-id]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const card = btn.closest('[data-ring-count]');
          const ringCount = parseInt(card?.dataset.ringCount || '1', 10);
          // Pull the card's title (e.g. "The Movie Classic") for the editor header.
          const titleEl = card?.querySelector('.risify-rb__card-title');
          const variantTitle = titleEl ? (titleEl.textContent || '').trim() : '';
          this.startConfigurator(btn.dataset.designId, ringCount, variantTitle);
        });
      });

      // RSFY-678 — landing "Begin in the Ring Builder" CTA: skip the
      // intermediate scroll-to-paths step and drop the customer straight into
      // step 1 of the first path card (The Movie Classic by default).
      this.querySelectorAll('[data-rb-start]').forEach((cta) => {
        cta.addEventListener('click', (e) => {
          const firstCardBtn = this.querySelector('[data-design-id]');
          if (!firstCardBtn) return; // No paths defined — let the anchor scroll fall through.
          e.preventDefault();
          const card = firstCardBtn.closest('[data-ring-count]');
          const ringCount = parseInt(card?.dataset.ringCount || '1', 10);
          const titleEl = card?.querySelector('.risify-rb__card-title');
          const variantTitle = titleEl ? (titleEl.textContent || '').trim() : '';
          this.startConfigurator(firstCardBtn.dataset.designId, ringCount, variantTitle);
        });
      });

      // Close editor
      this.editorClose?.addEventListener('click', () => this.closeEditor());

      // Mobile preview toggle — Figma 6002:8121 (collapsed) + 6002:8134
      // (expanded). One button sits above the footer in both states; tap
      // swaps the controls column for the preview rail and flips the label
      // between "Preview Ring" ↔ "Hide Ring Preview". Header, stepper and
      // footer stay visible in both states. Desktop ignores the toggle.
      const previewToggle = this.querySelector('[data-preview-toggle]');
      const previewToggleLabel = this.querySelector('[data-preview-toggle-label]');
      const summaryRail = this.querySelector('[data-summary-rail]');
      const editorBody = this.querySelector('.risify-rb__editor-body');
      const editorFooter = this.querySelector('[data-editor-footer]');
      const editorHeader = this.querySelector('.risify-rb__editor-header');
      this._setPreviewExpanded = (expanded) => {
        const target = expanded ? 'true' : 'false';
        previewToggle?.setAttribute('data-preview-expanded', target);
        editorBody?.setAttribute('data-preview-expanded', target);
        summaryRail?.setAttribute('data-preview-expanded', target);
        if (previewToggleLabel) {
          previewToggleLabel.textContent = expanded ? 'Hide Ring Preview' : 'Preview Ring';
        }
        previewToggle?.setAttribute('aria-label', expanded ? 'Hide ring preview' : 'Show ring preview');
      };
      previewToggle?.addEventListener('click', () => {
        const current = previewToggle.getAttribute('data-preview-expanded') === 'true';
        this._setPreviewExpanded(!current);
      });

      // Keep CSS variables in sync with the real header / footer heights so
      // the peek tab + bottom-sheet always sit flush against the footer
      // regardless of how the footer wraps on a given viewport.
      this._syncMobileShellMetrics = () => {
        const overlay = this.editorOverlay;
        if (!overlay) return;
        if (editorHeader) {
          overlay.style.setProperty('--rb-mobile-header-h', `${editorHeader.offsetHeight}px`);
        }
        if (editorFooter) {
          overlay.style.setProperty('--rb-mobile-footer-h', `${editorFooter.offsetHeight}px`);
        }
      };
      this._syncMobileShellMetrics();
      window.addEventListener('resize', this._syncMobileShellMetrics);
      // Footer height can change when the continue label text length changes
      // (e.g. "STONE" vs "SEND TO THE WORKSHOP"). Re-measure on each step
      // transition by hooking into the existing showStep call site below.
      this._mobileShellMetricsHook = true;

      // Escape key
      this._onKeyDown = (e) => {
        if (e.key === 'Escape') {
          // Close mobile preview first if open, else close editor.
          if (previewToggle?.getAttribute('data-preview-expanded') === 'true') {
            this._setPreviewExpanded(false);
            return;
          }
          if (this.editorOverlay?.classList.contains('risify-rb__editor-overlay--open')) {
            this.closeEditor();
          }
        }
      };
      document.addEventListener('keydown', this._onKeyDown);

      // Stepper clicks
      this.stepperSteps.forEach((step) => {
        step.addEventListener('click', () => {
          const stepName = step.dataset.stepperStep;
          if (this.visitedSteps.has(stepName) || this.canOpenStep(stepName)) {
            const idx = this.stepOrder.indexOf(stepName);
            if (idx >= 0) {
              this.currentStepIndex = idx;
              this._persistCurrentRingStep && this._persistCurrentRingStep();
              this.showStep(stepName);
            }
          }
        });
      });

      // Navigation buttons
      this.navContinue?.addEventListener('click', () => this.goToNextStep());
      this.navBack?.addEventListener('click', () => this.goToPrevStep());
      this.navContinueMobile?.addEventListener('click', () => this.goToNextStep());

      // Review edit button
      this.reviewEdit?.addEventListener('click', () => {
        this.currentStepIndex = 0;
        this._persistCurrentRingStep && this._persistCurrentRingStep();
        this.showStep('metal');
      });

      // Add to cart
      this.addToCartBtn?.addEventListener('click', () => this.addToCart());

      // Size buttons
      this.sizeGrid?.querySelectorAll('[data-size-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          // RSFY-708 — IDKMS lives inside the size grid as a peer of the
          // letter buttons (rather than a 4th region tab). Treat its click
          // as the legacy `_setSizeRegion('idkms')` path so the existing
          // guidance + cart-property flow keeps working without a parallel
          // code path.
          if (btn.dataset.sizeValue === 'idkms') {
            this._setSizeRegion('idkms');
            return;
          }
          this.selectRingSize(btn.dataset.sizeValue);
        });
      });

      // Width buttons
      this.widthOptions?.querySelectorAll('[data-width-value]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectBandWidth(btn.dataset.widthValue));
      });

      // Region tabs (UK / US / EU / IDKMS) — Figma 6013:7664. Re-runs the
      // size step painter so the size button labels swap regions and the
      // dimensions strip flips its equivalent column.
      this.querySelectorAll('[data-size-region]').forEach((btn) => {
        btn.addEventListener('click', () => this._setSizeRegion(btn.dataset.sizeRegion));
      });
    }

    disconnectedCallback() {
      if (this._onKeyDown) {
        document.removeEventListener('keydown', this._onKeyDown);
      }
    }

    // =========================================================================
    // Core Flow
    // =========================================================================

    startConfigurator(designId, ringCount, variantTitle) {
      this.currentDesignId = designId;
      this.ringCount = ringCount;

      // Header title — append the chosen ring type after "Ring Builder · …".
      // (Branch markup uses a single [data-editor-title] span; we append the
      // variant inline so we don't have to change the markup.)
      if (this.editorTitle) {
        this.editorTitle.textContent = variantTitle ? `Ring Builder · ${variantTitle}` : 'Ring Builder';
      }

      // Init ring states (D3 multi-axis diamond model)
      this.rings = [];
      for (let i = 0; i < ringCount; i++) {
        this.rings.push({
          metalType: null,
          carat: null,
          // Multi-axis diamond — default to "No diamond" (Bug C / Halfdan):
          // most builder users are wedding-band buyers. Stone step paints
          // the No pill selected; customers opt in by clicking "With diamond".
          hasDiamond: false,
          // Setting style (RSFY-641) — one of contemporary/modern/classic/elegant/sleek/petite
          settingStyle: null,
          diamondCarat: 0.50,
          diamondCut: 'round',
          diamondColor: 'E',
          diamondClarity: 'VS',
          // PDP-parity Stone Quality (RSFY-685). Default Premium Quality
          // Default Stone Quality is High (the catalog's base G/H SI tier);
          // Premium triggers an upsell delta in the matcher so making it
          // the default would silently inflate the starting price for every
          // new ring. _syncDiamondQuality + _reconcileDisplayedQuality
          // re-snap to whichever tier is actually available in the pool.
          diamondQuality: 'High Quality',
          // Legacy bundled stone-quality string — mirrors `_setDiamondHas(false)`.
          diamond: 'none',
          engraving: null,
          engravingDetails: makeDefaultEngravingDetails(),
          size: null,
          // Region toggle (UK / US / EU / IDKMS) is cosmetic — `size` always
          // stores the canonical UK letter from SIZE_TABLE. Default is null
          // so neither the region tab nor the size grid is pre-selected on
          // first paint; the customer must explicitly pick a region before
          // the grid (or IDKMS guidance) becomes visible.
          sizeRegion: null,
          // Client revision 2026-09-04: the step opens on "I'll confirm my
          // size later" rather than on no answer at all, so a customer who
          // never opens Size can still reach Review and check out with the
          // size left to settle after the order.
          sizeUnknown: true,
          // RSFY-707 — band width is null by default. When the customer
          // picks "Band Only" in Step 2, they must explicitly choose a
          // width (3/4/5/7mm) before they can advance. The chosen width
          // narrows the product pool via `rb-band-width:{N}mm` tag.
          bandWidth: null,
          selectedProduct: null,
          selectedVariant: null,
          // Bug fix #6 — per-ring step memory so switching tabs returns the
          // customer to the step they last visited for that ring.
          lastStepIndex: 0,
          // Multi-ring flow — whether the customer has landed on this ring yet.
          // Ring 1 is the one shown on open, so it starts visited; the Size
          // step's "CONTINUE TO RING N" walks the customer through the rest in
          // order (even though each opens pre-filled with defaults) before
          // Review. Marked in `_switchToRingForConfig`.
          _visited: i === 0,
        });
      }

      // Reset step tracking
      this.visitedSteps = new Set(['metal']);
      this.currentStepIndex = 0;

      // RSFY-651: sequential multi-ring — current ring index (0-based)
      this.currentRingIndex = 0;
      this._copyPromptShownForRing = new Set();

      // Open editor
      this.editorOverlay.classList.add('risify-rb__editor-overlay--open');
      document.body.style.overflow = 'hidden';

      // Hide landing
      if (this.landing) this.landing.style.display = 'none';

      this.setupRingSelectors();
      this.populateMetalTypes();
      this._setupRingTabs(ringCount);
      this.showStep('metal');
      this.clearStepSelections();
      this.updateRingStatusDisplay();

      // Create dynamic 3D viewers
      this.createRingViewers(ringCount);

      // Bug fix #13 — pre-select a default metal (Yellow Gold + lowest carat)
      // for Ring 1 ONLY, so the 3D preview doesn't open with an empty,
      // unstyled-looking ring. Ring 2/3 stay blank as the customer must
      // configure them deliberately.
      this._applyRing1DefaultMetal();

      this.updateFooterTotal();
      // Only hide if it's the dedicated review-slip button. Footer continue
      // must stay visible on Step 1 so the customer can navigate forward.
      if (this.addToCartBtn && this._addToCartBtnIsDedicated) {
        this.addToCartBtn.style.display = 'none';
      }

      // RSFY-678 — drop keyboard focus on the first interactive control of
      // step 1 so the landing CTA → editor flow works for keyboard users
      // too. Defer one frame so the step page is fully visible first.
      requestAnimationFrame(() => {
        const firstControl = this.stepPages.metal?.querySelector(
          'button:not([disabled]):not([hidden]), [data-metal-type], input:not([disabled])'
        );
        if (firstControl) firstControl.focus({ preventScroll: true });
      });
    }

    /**
     * Bug fix #13 — Apply sensible defaults to EVERY ring so each one opens
     * fully configured: Yellow Gold + 18ct (the recommended "Original Movie
     * Ring gold") + Band Only at Standard 7mm. The customer can change any of
     * these; we just don't want a blank, unstyled ring on first view. Applies
     * to Ring 2/3 in the multi-ring builds too (per PM ask).
     */
    _applyRing1DefaultMetal() {
      if (!this.rings || !this.rings.length) return;
      const previousRingIndex = this.currentRingIndex;

      // The metal list lives on the *rendered cards*, not directly on
      // `window.ringBuilderProducts` (whose keys are design IDs, not metal
      // names). Reading the cards is the safest source of truth — it
      // matches exactly what the customer can see/click.
      const cards = this.metalTypesContainer
        ? Array.from(this.metalTypesContainer.querySelectorAll('[data-metal-type]'))
        : [];
      const availableMetals = cards.map((c) => c.dataset.metalType).filter(Boolean);
      if (availableMetals.length === 0) {
        this.currentRingIndex = previousRingIndex;
        return;
      }
      const defaultMetal = availableMetals.includes('Yellow Gold') ? 'Yellow Gold' : availableMetals[0];

      // Apply the same defaults to each ring. selectMetalType / selectCarat
      // act on `currentRingIndex`, so point it at each ring in turn.
      for (let i = 0; i < this.rings.length; i++) {
        this.currentRingIndex = i;
        this.selectMetalType(defaultMetal);

        // Pre-select the "Recommended" carat (18ct) when the chosen metal
        // offers it; otherwise leave carat for the customer (showCaratsForMetal
        // still auto-selects the single-carat case for metals like Platinum).
        if (defaultMetal === 'Yellow Gold') {
          const has18ct = !!(this.caratPills && this.caratPills.querySelector('[data-carat="18ct"]'));
          if (has18ct) this.selectCarat('18ct');
        }

        // Default Band Only width → Standard 7mm ("Popular"). Set AFTER
        // selectCarat (which resets other axes but not bandWidth) so the
        // Step 2 width card opens highlighted.
        const ring = this.rings[i];
        if (ring && (ring.bandWidth == null || ring.bandWidth === '')) {
          ring.bandWidth = '7';
        }
      }

      // Return to Ring 1 and repaint its Step-1 UI (the loop left the metal
      // cards / carat pills reflecting the last ring).
      this.currentRingIndex = 0;
      this.populateMetalTypes();
      this._restoreMetalStepUI && this._restoreMetalStepUI();
    }

    /**
     * RSFY-651 — Initialise the sequential ring tab strip + Copy from prompt.
     * Visible only for ringCount > 1.
     */
    _setupRingTabs(ringCount) {
      const tabs = this.querySelector('[data-ring-tabs]');
      const prompt = this.querySelector('[data-ring-copy-prompt]');
      if (!tabs) return;

      if (ringCount <= 1) {
        tabs.hidden = true;
        if (prompt) prompt.hidden = true;
        return;
      }

      tabs.hidden = false;

      // Show only as many tabs as we have rings. The flex layout on the
      // parent (`display: flex; flex: 1;` per child) overrides the `hidden`
      // attribute, so we force display:none explicitly.
      tabs.querySelectorAll('[data-ring-tab]').forEach((btn, idx) => {
        const shouldHide = idx >= ringCount;
        btn.hidden = shouldHide;
        btn.style.display = shouldHide ? 'none' : '';
        btn.classList.toggle('risify-rb__ring-tab--active', idx === this.currentRingIndex);
      });

      // Bind once
      if (!tabs.dataset.bound) {
        tabs.dataset.bound = 'true';
        tabs.querySelectorAll('[data-ring-tab]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this._switchToRing(parseInt(btn.dataset.ringTab, 10));
          });
        });
        if (prompt) {
          prompt.querySelectorAll('[data-ring-copy-action]').forEach((btn) => {
            btn.addEventListener('click', () => {
              const action = btn.dataset.ringCopyAction;
              this._handleRingCopyAction(action);
            });
          });
        }
      }

      this._updateRingTabSummaries();
    }

    _switchToRing(idx) {
      if (idx < 0 || idx >= this.ringCount) return;
      if (this.currentRingIndex === idx) return;

      // Bug fix #6 — persist the *outgoing* ring's current step before switching
      // so a later return restores them to the step they were last editing.
      this._persistCurrentRingStep();

      this.currentRingIndex = idx;

      // Highlight active tab
      this.querySelectorAll('[data-ring-tab]').forEach((btn, i) => {
        btn.classList.toggle('risify-rb__ring-tab--active', i === idx);
      });

      // RSFY-651 madde 5 — highlight the active ring's 3D viewer, dim the rest
      this._updateRingViewerActiveState();
      // RSFY-718 — the Setting detail panel reflects the active ring; re-render
      // for the newly-active ring's selections.
      this._renderSettingDetail();

      // Show copy-from prompt for rings > 0 the first time the customer lands on them
      this._maybeShowCopyPrompt(idx);

      // Bug fix #6 — restore the *incoming* ring's last-visited step instead of
      // forcing Step 1. Falls back to 0 for rings that have never been opened.
      const ring = this.rings[idx];
      const targetStepIdx = (ring && Number.isInteger(ring.lastStepIndex))
        ? Math.max(0, Math.min(ring.lastStepIndex, this.stepOrder.length - 1))
        : 0;
      this.currentStepIndex = targetStepIdx;
      const targetStepName = this.stepOrder[targetStepIdx];

      // Re-render metal step's underlying data (cards + carat pills) for this
      // ring's metal selection — needed before _restoreMetalStepUI paints highlights.
      this.populateMetalTypes();
      this._restoreMetalStepUI();

      // showStep will trigger populateDiamondOptions / populateEngravingOptions
      // as applicable; those helpers read ring state and paint accordingly. We
      // call our explicit restore helpers afterwards to make sure DOM matches.
      this.showStep(targetStepName);
      this._restoreStepDomForCurrentRing(targetStepName);

      // Push the incoming ring's engraving state into its 3D viewer.
      // Without this the previous ring's mesh keeps whatever was last
      // pushed (or stays blank if the viewer was never written to), even
      // though the textarea + Elvish preview repaint correctly. Calling
      // sync for both sides covers Outside, Inside, "both", and "none"
      // — the helper no-ops when the matching side is disabled.
      if (ring) {
        this._syncEngravingToViewer('outside', ring);
        this._syncEngravingToViewer('inside', ring);
      }

      this.updateRingStatusDisplay();
      this.updateSummaryRail && this.updateSummaryRail();
    }

    /**
     * Bug fix #6 — Save the current ring's lastStepIndex so a later tab switch
     * back to that ring can restore the step they were editing.
     */
    _persistCurrentRingStep() {
      const ring = this.rings[this.currentRingIndex];
      if (ring) ring.lastStepIndex = this.currentStepIndex;
    }

    /**
     * Bug fix #6 — Dispatch to per-step DOM restore helpers after a ring switch
     * so each step page reflects the active ring's saved state.
     */
    _restoreStepDomForCurrentRing(stepName) {
      const ring = this.rings[this.currentRingIndex];
      if (!ring) return;
      if (stepName === 'metal') {
        this._restoreMetalStepUI();
      } else if (stepName === 'diamond') {
        // populateDiamondOptions (called by showStep) reads ring state via
        // _renderDiamondStep + _renderSettingStyles, so DOM is already correct.
      } else if (stepName === 'engraving') {
        this._restoreEngravingStepUI(ring);
      } else if (stepName === 'size') {
        this._restoreSizeStepUI(ring);
      } else if (stepName === 'review') {
        // updateSummary (called by showStep) re-paints the review block.
      }
    }

    /**
     * Client revision 2026-09-04 — write the client's engraving defaults the
     * first time a ring's Engraving step is opened: outside on, Tengwar, and
     * the verse. One shot per ring, and only while the customer has not
     * answered yet, so switching Outside off and coming back keeps their
     * answer. Returns whether anything was written.
     */
    _applyDefaultEngraving(ring) {
      if (!ring || ring.engravingDefaultsApplied) return false;
      ring.engravingDefaultsApplied = true;
      const d = ring.engravingDetails;
      if (!d || d.outsideEngraving || d.outsideText) return false;
      d.outsideEngraving = true;
      d.outsideLanguage = 'elvish';
      d.outsideText = DEFAULT_OUTSIDE_VERSE;
      return true;
    }

    /**
     * Bug fix #6 — Paint engraving step DOM (cards/pills/textareas/font selects)
     * from the ring's engravingDetails state. Called after _switchToRing.
     */
    _restoreEngravingStepUI(ring) {
      const d = ring && ring.engravingDetails;
      if (!d) return;

      // hasEngraving — top-level Add Engraving / Skip Engraving segmented
      // tabs (Figma 6002:8429 / 6002:8866). Default is "Skip Engraving" so
      // when `hasEngraving === false` the "no" tab paints active. Toggle the
      // legacy `engraving-card--selected` class too so older snapshots keep
      // matching, even though the markup is the segmented-tab pattern now.
      this.querySelectorAll('[data-engraving-choice]').forEach((card) => {
        const isYes = card.dataset.engravingChoice === 'yes';
        const match = (isYes && d.hasEngraving === true) || (!isYes && d.hasEngraving === false);
        card.classList.toggle('risify-rb__segmented-tab--selected', match);
        card.classList.toggle('risify-rb__engraving-card--selected', match);
      });
      const details = this.querySelector('[data-engraving-details]');
      if (details) details.style.display = d.hasEngraving ? '' : 'none';

      // Hallmark — always paint (Bug F + Bug I). Default is "Yes" and applies
      // even when hasEngraving is false because the hallmark is a separate
      // inside marking, not part of the engraving copy. Painted before the
      // early return below so the pill state survives "Skip Engraving" too.
      this._paintHallmarkPills(ring);

      // Accordion headers paint their open/closed state from ring choice;
      // call before the early return so the section starts collapsed even
      // if the customer hasn't picked Add Engraving yet (the details block
      // is hidden so they won't see it, but state stays consistent).
      this._paintEngravingAccordions(ring);

      if (!d.hasEngraving) return;

      // Outside yes/no — Figma 6008:4054 segmented tabs (Milano Red active).
      this.querySelectorAll('[data-outside-choice]').forEach((btn) => {
        const isYes = btn.dataset.outsideChoice === 'yes';
        const match = (isYes && d.outsideEngraving === true) || (!isYes && d.outsideEngraving === false);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
        btn.classList.toggle('risify-rb__pill--selected', match);
      });
      const outsideLangSection = this.querySelector('[data-outside-language-section]');
      if (outsideLangSection) outsideLangSection.style.display = d.outsideEngraving ? '' : 'none';

      // Outside language — Figma 6008:4047 segmented tabs.
      this.querySelectorAll('[data-outside-lang]').forEach((btn) => {
        const match = d.outsideLanguage && btn.dataset.outsideLang === d.outsideLanguage;
        btn.classList.toggle('risify-rb__segmented-tab--selected', !!match);
        btn.classList.toggle('risify-rb__pill--selected', !!match);
      });
      const outsideTextSection = this.querySelector('[data-outside-text-section]');
      if (outsideTextSection) outsideTextSection.style.display = (d.outsideEngraving && d.outsideLanguage) ? '' : 'none';
      const outsideElvish = this.querySelector('[data-outside-elvish-render]');
      if (outsideElvish) outsideElvish.style.display = d.outsideLanguage === 'elvish' ? '' : 'none';
      const outsideFontSection = this.querySelector('[data-outside-font-section]');
      if (outsideFontSection) outsideFontSection.style.display = d.outsideLanguage === 'english' ? '' : 'none';
      const outsideFontPreview = this.querySelector('[data-outside-font-preview]');
      if (outsideFontPreview) outsideFontPreview.style.display = d.outsideLanguage === 'english' ? '' : 'none';

      // Outside text + font
      const outsideText = this.querySelector('[data-outside-text]');
      if (outsideText) outsideText.value = d.outsideText || '';
      const outsideFont = this.querySelector('[data-outside-font]');
      if (outsideFont) outsideFont.value = d.outsideFont || 'Lucida Handwriting';
      if (d.outsideLanguage === 'elvish' && outsideElvish) {
        this.renderElvishPreview(outsideElvish, d.outsideText || '');
      } else if (d.outsideLanguage === 'english') {
        this.updateFontPreview('outside');
      }
      if (d.outsideEngraving) {
        this._updateBuilderFit('outside', d.outsideText || '', ring);
      }

      // Inside yes/no — Figma 6008:4061 segmented tabs.
      this.querySelectorAll('[data-inside-choice]').forEach((btn) => {
        const isYes = btn.dataset.insideChoice === 'yes';
        const match = (isYes && d.insideEngraving === true) || (!isYes && d.insideEngraving === false);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
        btn.classList.toggle('risify-rb__pill--selected', match);
      });
      const insideLangSection = this.querySelector('[data-inside-language-section]');
      if (insideLangSection) insideLangSection.style.display = d.insideEngraving ? '' : 'none';

      // Inside language — mirrors Outside; Tengwar/English picker re-enabled.
      this.querySelectorAll('[data-inside-lang]').forEach((btn) => {
        const match = d.insideLanguage && btn.dataset.insideLang === d.insideLanguage;
        btn.classList.toggle('risify-rb__segmented-tab--selected', !!match);
        btn.classList.toggle('risify-rb__pill--selected', !!match);
      });
      const insideTextSection = this.querySelector('[data-inside-text-section]');
      if (insideTextSection) insideTextSection.style.display = (d.insideEngraving && d.insideLanguage) ? '' : 'none';
      const insideElvish = this.querySelector('[data-inside-elvish-render]');
      if (insideElvish) insideElvish.style.display = d.insideLanguage === 'elvish' ? '' : 'none';
      const insideFontSection = this.querySelector('[data-inside-font-section]');
      if (insideFontSection) insideFontSection.style.display = d.insideLanguage === 'english' ? '' : 'none';
      const insideFontPreview = this.querySelector('[data-inside-font-preview]');
      if (insideFontPreview) insideFontPreview.style.display = d.insideLanguage === 'english' ? '' : 'none';

      // Inside text + font
      const insideText = this.querySelector('[data-inside-text]');
      if (insideText) insideText.value = d.insideText || '';
      const insideFont = this.querySelector('[data-inside-font]');
      if (insideFont) insideFont.value = d.insideFont || 'Lucida Handwriting';
      if (d.insideLanguage === 'elvish' && insideElvish) {
        this.renderElvishPreview(insideElvish, d.insideText || '');
      } else if (d.insideLanguage === 'english') {
        this.updateFontPreview('inside');
      }
      if (d.insideEngraving) {
        this._updateBuilderFit('inside', d.insideText || '', ring);
      }
      // (Hallmark is painted earlier — see the always-paint block above so
      // its default Yes state shows even when hasEngraving is false.)
    }

    /**
     * Bug fix #6 — Paint size step DOM (size grid + band width buttons) from
     * the ring's saved size/bandWidth.
     */
    _restoreSizeStepUI(ring) {
      if (!ring) return;
      this.sizeGrid?.querySelectorAll('[data-size-value]').forEach((btn) => {
        btn.classList.toggle('risify-rb__size-btn--selected', !!ring.size && btn.dataset.sizeValue === ring.size);
      });
      this.widthOptions?.querySelectorAll('[data-width-value]').forEach((btn) => {
        btn.classList.toggle('risify-rb__width-btn--selected', !!ring.bandWidth && btn.dataset.widthValue === ring.bandWidth);
      });
      // Region tabs + dimensions strip — keep the active ring's region
      // selection visible when switching ring tabs.
      this._paintSizeRegion(ring);
      this._paintSizeDimensions(ring);
    }

    /**
     * Restore Step 1 (Metal) DOM to reflect the current ring's state.
     * - Highlight the selected metal card (if any)
     * - Re-render carat pills for that metal + highlight the selected carat
     * - Reset visible labels
     * Called after _switchToRing so a freshly-opened ring tab shows its OWN
     * settings, not the previous ring's stale highlights.
     */
    _restoreMetalStepUI() {
      const ring = this.rings[this.currentRingIndex];
      if (!ring) return;

      // Metal card highlight
      if (this.metalTypesContainer) {
        this.metalTypesContainer.querySelectorAll('.risify-rb__metal-card').forEach((card) => {
          card.classList.toggle(
            'risify-rb__metal-card--selected',
            !!ring.metalType && card.dataset.metalType === ring.metalType
          );
        });
      }

      // Carat pills — repopulate for this ring's metal (or clear if metal not chosen)
      if (ring.metalType) {
        this.showCaratsForMetal(ring.metalType);
        // showCaratsForMetal may auto-select a single-carat case; re-apply
        // selected highlight to whatever the ring actually has.
        if (this.caratPills) {
          this.caratPills.querySelectorAll('.risify-rb__pill').forEach((pill) => {
            pill.classList.toggle('risify-rb__pill--selected', !!ring.carat && pill.dataset.carat === ring.carat);
          });
        }
      } else {
        // No metal yet — clear stale carat pills from the previous ring.
        if (this.caratPills) this.caratPills.innerHTML = '';
        if (this.caratSection) this.caratSection.style.display = 'none';
      }

      // Metal selected label
      this.updateMetalSelectedLabel(ring.metalType, ring.carat);
    }

    /**
     * Bug fix #9 — Show the copy prompt ONLY when:
     *   (a) the customer just switched to a ring that has no selections yet
     *   (b) at least one OTHER ring is fully complete (i.e. reached review)
     * For 3-ring sets where multiple sources are complete, render a button per
     * eligible source so the customer can pick which ring to copy from.
     */
    _maybeShowCopyPrompt(idx) {
      const prompt = this.querySelector('[data-ring-copy-prompt]');
      if (!prompt) return;
      const actions = prompt.querySelector('[data-ring-copy-actions]');

      // Clean any previously-rendered dynamic source buttons.
      if (actions) {
        actions.querySelectorAll('[data-ring-copy-source-idx]').forEach((b) => b.remove());
      }

      // Target ring must be untouched (no selections yet).
      const target = this.rings[idx];
      if (!target) { prompt.hidden = true; return; }
      const targetTouched = !!(target.metalType || target.carat || target.hasDiamond !== null ||
        (target.engravingDetails && target.engravingDetails.hasEngraving !== null) || target.size);
      if (targetTouched) { prompt.hidden = true; return; }

      // Find all OTHER rings that have reached review (fully complete).
      const completeSourceIdx = [];
      for (let i = 0; i < this.ringCount; i++) {
        if (i === idx) continue;
        const isComplete = ['metal', 'diamond', 'engraving', 'size']
          .every((step) => this.isStepCompleteForRing(step, this.rings[i]));
        if (isComplete) completeSourceIdx.push(i);
      }
      if (completeSourceIdx.length === 0) { prompt.hidden = true; return; }

      // Header label
      const targetLabel = prompt.querySelector('[data-ring-copy-target]');
      if (targetLabel) targetLabel.textContent = `Ring ${idx + 1}`;

      // Message — adjust phrasing if we have one vs many sources.
      const msg = prompt.querySelector('[data-ring-copy-message]');
      if (msg) {
        if (completeSourceIdx.length === 1) {
          msg.textContent = `Start fresh, or copy your finished settings from Ring ${completeSourceIdx[0] + 1}?`;
        } else {
          msg.textContent = 'Start fresh, or copy from one of your finished rings?';
        }
      }

      // Render one copy button per eligible source.
      if (actions) {
        completeSourceIdx.forEach((sourceIdx) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'risify-rb__button';
          btn.dataset.ringCopyAction = 'copy';
          btn.dataset.ringCopySourceIdx = String(sourceIdx);
          btn.textContent = completeSourceIdx.length === 1
            ? 'Copy settings →'
            : `Copy from Ring ${sourceIdx + 1} →`;
          btn.addEventListener('click', () => this._handleRingCopyAction('copy', sourceIdx));
          actions.appendChild(btn);
        });
      }

      prompt.hidden = false;
    }

    _handleRingCopyAction(action, sourceIdxArg) {
      const prompt = this.querySelector('[data-ring-copy-prompt]');
      if (prompt) prompt.hidden = true;
      if (action !== 'copy') return;
      const idx = this.currentRingIndex;
      // Bug fix #9 — accept explicit source index (3-ring case). Default to
      // first non-current ring if not supplied (legacy 2-ring case).
      let sourceIdx = (typeof sourceIdxArg === 'number') ? sourceIdxArg : 0;
      if (sourceIdx === idx) sourceIdx = (idx === 0) ? 1 : 0;
      const source = this.rings[sourceIdx];
      if (!source) return;
      if (idx === sourceIdx) return;
      // Deep clone (state only — exclude selectedProduct/Variant references, they'll
      // be re-resolved).
      const cloned = JSON.parse(JSON.stringify({
        metalType: source.metalType,
        carat: source.carat,
        hasDiamond: source.hasDiamond,
        settingStyle: source.settingStyle,
        diamondCarat: source.diamondCarat,
        diamondCut: source.diamondCut,
        diamondColor: source.diamondColor,
        diamondClarity: source.diamondClarity,
        diamondQuality: source.diamondQuality,
        diamond: source.diamond,
        engraving: source.engraving,
        engravingDetails: source.engravingDetails,
        size: source.size,
        bandWidth: source.bandWidth,
      }));
      Object.assign(this.rings[idx], cloned);
      this.resolveProductForRings([idx]);
      this.updateVariantMatchForRings([idx]);
      // Mark all earlier steps visited so the customer can review/jump directly
      ['metal', 'diamond', 'engraving', 'size'].forEach((s) => this.visitedSteps.add(s));
      // Bug fix #6 — start the copied ring on Step 1 (metal) and remember it.
      this.currentStepIndex = 0;
      this.rings[idx].lastStepIndex = 0;
      this.showStep('metal');
      this.populateMetalTypes();
      // Bug fix #4 — paint the copied metal/carat highlights in the DOM so the
      // step UI matches the cloned state (otherwise cards look unselected).
      this._restoreMetalStepUI();
      this.updateRingStatusDisplay();
      this._updateRingTabSummaries();
    }

    _updateRingTabSummaries() {
      this.querySelectorAll('[data-ring-tab]').forEach((btn, idx) => {
        const summary = btn.querySelector('[data-ring-tab-summary]');
        if (!summary) return;
        const ring = this.rings[idx];
        if (!ring) { summary.textContent = ''; return; }
        const parts = [];
        // Metal + carat
        if (ring.metalType) parts.push(ring.carat ? `${ring.carat} ${ring.metalType}` : ring.metalType);
        // Stone
        if (ring.hasDiamond === true) parts.push(this._caratDisplayLabel(ring.diamondCarat));
        else if (ring.hasDiamond === false) parts.push('plain');
        // Bug fix #7 — engraving summary
        const d = ring.engravingDetails;
        if (d && d.hasEngraving === false) {
          parts.push('no engraving');
        } else if (d && d.hasEngraving === true) {
          const sides = [];
          if (d.outsideEngraving) sides.push('outside');
          if (d.insideEngraving) sides.push('inside');
          if (sides.length) {
            const lang = d.outsideLanguage || d.insideLanguage;
            const langLabel = lang === 'elvish' ? 'elvish' : (lang === 'english' ? 'english' : '');
            parts.push(langLabel ? `${sides.join(' + ')} ${langLabel}` : sides.join(' + '));
          }
          if (d.hallmark === true) parts.push('+ hallmark');
        }
        // Bug fix #7 — size + band width summary (effective width: a diamond
        // ring's band is fixed by its setting, not the plain-band picker).
        if (ring.size) {
          const w = this._effectiveBandWidth(ring);
          parts.push(w ? `${ring.size} · ${w}mm` : `size ${ring.size}`);
        }
        summary.textContent = parts.join(' · ');
      });
    }

    closeEditor() {
      this.editorOverlay.classList.remove('risify-rb__editor-overlay--open');
      document.body.style.overflow = '';
      if (this.landing) this.landing.style.display = '';
      // Reset the variant suffix so the next open starts clean.
      if (this.editorTitle) this.editorTitle.textContent = 'Ring Builder';
      // Bug fix #146 — wipe all selections so the next open starts fresh.
      // Without this, ring state survives in this.rings (engraving, size,
      // diamond, etc.) and the customer sees their previous configuration.
      // We rely on startConfigurator() to rebuild this.rings on reopen, so
      // here we simply null it out plus reset the navigational counters.
      this.rings = [];
      this.currentStepIndex = 0;
      this.currentRingIndex = 0;
      this.visitedSteps = new Set();
      this._copyPromptShownForRing = new Set();
      // Folio number is per-session — generate a fresh one on the next visit
      // so a closed-and-reopened builder doesn't reuse the old workshop slip.
      // Drop any pending 3D engraving / footer warning state too.
      if (this.clearStepWarning) this.clearStepWarning();
    }

    // =========================================================================
    // Step Navigation (Page-Based)
    // =========================================================================

    showStep(stepName) {
      // Clear any leftover footer warning when moving between steps — the
      // pill is step-specific (e.g. "Pick a carat to continue.") and would
      // confuse customers if it lingered into the Stone or Engraving step.
      if (this.clearStepWarning) this.clearStepWarning();

      // Hide all step pages
      this.stepOrder.forEach((s) => {
        const page = this.stepPages[s];
        if (page) {
          page.classList.remove('risify-rb__step-page--active');
        }
      });

      // Show target step
      const target = this.stepPages[stepName];
      if (target) {
        target.classList.add('risify-rb__step-page--active');
      }

      // Tag the host element with the current step so mobile-only styles can
      // surface the rail viewer on the review step (CSS :has() fallback).
      this.dataset.currentStep = stepName;

      // Populate step content
      if (stepName === 'diamond') {
        this.populateDiamondOptions();
      } else if (stepName === 'engraving') {
        const seeded = this._applyDefaultEngraving(this.rings[this.currentRingIndex]);
        this.populateEngravingOptions();
        // Resolve the priced variant only after the step's DOM is bound, so
        // the total moves with the reason for it on screen.
        if (seeded) this.updateEngravingVariant();
      } else if (stepName === 'size') {
        // Bug fix #145 — paint the region tab + dimensions strip on every
        // entry to Size, not just on ring-tab switch. Without this, the
        // default UK region button never picks up its --selected highlight
        // when the customer arrives via the standard Continue flow.
        const ring = this.rings[this.currentRingIndex];
        if (ring) {
          this._paintSizeRegion(ring);
          this._paintSizeDimensions(ring);
        }
      } else if (stepName === 'review') {
        this.updateSummary();
      }

      // Update stepper
      this.updateStepper();

      // Update navigation buttons
      this.updateNavButtons();

      // Update summary rail
      this.updateSummaryRail();

      // RSFY-718 — the Setting detail panel is gated on the current step; show
      // or hide it whenever the step changes.
      this._renderSettingDetail();

      // Re-measure header/footer because the continue label text length can
      // change between steps (e.g. "STONE" → "SEND TO THE WORKSHOP"), which
      // alters the footer height and therefore where the mobile peek tab
      // needs to anchor.
      if (this._syncMobileShellMetrics) {
        requestAnimationFrame(() => this._syncMobileShellMetrics());
      }

      // Scroll controls to top
      if (this.controlsColumn) {
        this.controlsColumn.scrollTop = 0;
      }
    }

    goToNextStep() {
      const currentStep = this.stepOrder[this.currentStepIndex];

      // On review step, the "continue" triggers add to cart
      if (currentStep === 'review') {
        this.addToCart();
        return;
      }

      // Bug fix #8 — Validate only the *active* ring's step. Cross-ring
      // completeness is checked at add-to-cart time, not at every Continue.
      if (!this.isStepCompleteForCurrentRing(currentStep)) {
        this.showStepWarning(this.stepPages[currentStep]);
        return;
      }

      // Mark current step as visited
      this.visitedSteps.add(currentStep);

      // Multi-ring flow (RSFY) — the Size step is the last per-ring step
      // before Review. In a 2- or 3-ring build we walk the customer through
      // every ring (Ring 1 → Ring 2 → Ring 3) before Review, so instead of
      // advancing Size → Review we jump to the next ring they haven't visited
      // yet. Only once all sibling rings have been walked does Size → Review.
      if (currentStep === 'size' && this.ringCount > 1) {
        const nextRing = this._nextUnvisitedRingIndex();
        if (nextRing !== -1) {
          this._persistCurrentRingStep && this._persistCurrentRingStep();
          this._switchToRingForConfig(nextRing);
          return;
        }
        // else: every ring has been walked → fall through to advance to Review.
      }

      // Advance
      this.currentStepIndex = Math.min(this.currentStepIndex + 1, this.stepOrder.length - 1);
      const nextStep = this.stepOrder[this.currentStepIndex];
      this.visitedSteps.add(nextStep);
      // Bug fix #6 — persist the new step against the active ring for tab-switch restore.
      this._persistCurrentRingStep && this._persistCurrentRingStep();
      this.showStep(nextStep);
    }

    /**
     * Multi-ring helper — return the index of the next ring the customer
     * hasn't walked through yet, or -1 when they've all been visited. Every
     * ring opens pre-filled with defaults, so "not yet visited" (rather than
     * "incomplete") is what gates the Size → Review hand-off: the customer is
     * walked Ring 1 → Ring 2 → Ring 3 so they see and can tweak each one
     * before Review. Scans in order starting AFTER the active ring.
     */
    _nextUnvisitedRingIndex() {
      const start = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      for (let n = 1; n <= this.ringCount; n++) {
        const i = (start + n) % this.ringCount;
        if (i === start) continue;
        if (!this.rings[i] || !this.rings[i]._visited) return i;
      }
      return -1;
    }

    /**
     * Switch to a sibling ring to walk through it. Marks it visited and, for a
     * ring that hasn't been opened before, lands on the Metal step so the
     * customer reviews it front-to-back (unlike the ring-tab `_switchToRing`,
     * which restores the ring's last-visited step).
     */
    _switchToRingForConfig(idx) {
      if (idx < 0 || idx >= this.ringCount) return;
      const ring = this.rings[idx];
      if (ring) {
        // First visit → start at Metal; a re-visit keeps its remembered step.
        if (!ring._visited) ring.lastStepIndex = 0;
        ring._visited = true;
      }
      this._switchToRing(idx);
    }

    goToPrevStep() {
      this.currentStepIndex = Math.max(this.currentStepIndex - 1, 0);
      // Bug fix #6 — persist for tab-switch restore.
      this._persistCurrentRingStep && this._persistCurrentRingStep();
      this.showStep(this.stepOrder[this.currentStepIndex]);
    }

    updateStepper() {
      const currentStep = this.stepOrder[this.currentStepIndex];

      this.stepperSteps.forEach((el) => {
        const stepName = el.dataset.stepperStep;
        const idx = this.stepOrder.indexOf(stepName);
        const isDone = idx < this.currentStepIndex && this.visitedSteps.has(stepName);
        const isActive = stepName === currentStep;
        const isLocked = !this.visitedSteps.has(stepName) && !isActive;

        el.classList.toggle('risify-rb__stepper-step--done', isDone);
        el.classList.toggle('risify-rb__stepper-step--active', isActive);
        el.classList.toggle('risify-rb__stepper-step--locked', isLocked);

        // No text content — the Figma checked variant draws the tick via
        // CSS `::after` (background-image SVG), so leaving textContent
        // empty avoids a stray "✓" glyph from showing up next to the
        // illustration.
        const circle = el.querySelector('[data-stepper-circle]');
        if (circle) circle.textContent = '';
      });

      // Update connecting lines
      const lines = this.querySelectorAll('[data-stepper-line]');
      lines.forEach((line, i) => {
        line.classList.toggle('risify-rb__stepper-line--done', i < this.currentStepIndex);
      });
    }

    updateNavButtons() {
      const currentStep = this.stepOrder[this.currentStepIndex];
      const isFirst = this.currentStepIndex === 0;
      const isReview = currentStep === 'review';

      // Back button — Figma redesign uses just a chevron-left icon, kept
      // small + secondary so the primary CTA dominates the footer. The
      // previous step name is set as aria-label for accessibility.
      if (this.navBack) {
        this.navBack.style.display = isFirst ? 'none' : '';
        if (!isFirst) {
          const prevStep = this.stepOrder[this.currentStepIndex - 1];
          const stepLabels = { metal: 'Metal', diamond: 'Style', engraving: 'Engraving', size: 'Size', review: 'Review' };
          this.navBack.setAttribute('aria-label', `Back to ${stepLabels[prevStep] || prevStep}`);
          // Figma 6002:7017 — desktop back button shows the previous step
          // name next to the arrow (e.g. "← METAL"). The label is hidden on
          // mobile via CSS so the button collapses to an icon-only square.
          const backLabelEl = this.navBack.querySelector('[data-nav-back-label]');
          if (backLabelEl) {
            backLabelEl.textContent = (stepLabels[prevStep] || prevStep || '').toUpperCase();
          }
        }
      }

      // Continue button — same control on desktop and mobile (RSFY-643
      // partial). On the Review step it flips to "Send to the workshop" and
      // triggers the add-to-cart flow via goToNextStep (review handler).
      // Writes into `data-nav-continue-label` so the inline chevron SVG
      // baked into the Figma-redesigned button stays intact.
      if (this.navContinue) {
        this.navContinue.style.display = '';
        const labelEl = this.navContinue.querySelector('[data-nav-continue-label]') || this.navContinue;
        if (isReview) {
          labelEl.textContent = 'SEND TO THE WORKSHOP';
        } else if (currentStep === 'size' && this.ringCount > 1 && this._nextUnvisitedRingIndex() !== -1) {
          // Multi-ring: the Size step is the last per-ring step, so instead of
          // "CONTINUE TO REVIEW" it points at the next ring the customer hasn't
          // walked through yet ("CONTINUE TO RING 2" / "RING 3"). Once every
          // ring has been visited this falls through to the normal "CONTINUE
          // TO REVIEW" label below.
          const nextRing = this._nextUnvisitedRingIndex();
          labelEl.textContent = `CONTINUE TO RING ${nextRing + 1}`;
        } else {
          const nextStep = this.stepOrder[this.currentStepIndex + 1];
          // Verb-first / full-label overrides that bypass the "CONTINUE TO …"
          // prefix. `engraving` → "ADD YOUR ENGRAVING" (Figma 6002:7017);
          // `diamond` → the full "CONTINUE" label the
          // Metal step's footer button uses (the back button + other surfaces
          // still say "STONE" via stepLabels below).
          const overrides = {
            engraving: 'ADD YOUR ENGRAVING',
            diamond: 'CONTINUE',
          };
          const stepLabels = { metal: 'METAL', diamond: 'STYLE', engraving: 'ENGRAVING', size: 'SIZE', review: 'REVIEW' };
          if (overrides[nextStep]) {
            labelEl.textContent = overrides[nextStep];
          } else {
            labelEl.textContent = `CONTINUE TO ${stepLabels[nextStep] || (nextStep || '').toUpperCase()}`;
          }
        }
      }

      // Legacy: mobile-only Continue button no longer exists (footer is
      // unified across breakpoints). Keep the guard so older builds don't
      // crash if the element ever comes back.
      if (this.navContinueMobile) {
        const labelEl = this.navContinueMobile.querySelector('[data-nav-continue-label]') || this.navContinueMobile;
        if (isReview) {
          labelEl.textContent = 'SEND TO THE WORKSHOP';
        } else {
          const nextStep = this.stepOrder[this.currentStepIndex + 1];
          const stepLabels = { diamond: 'STONE', engraving: 'ENGRAVING', size: 'SIZE', review: 'REVIEW' };
          labelEl.textContent = `${stepLabels[nextStep] || (nextStep || '').toUpperCase()}`;
        }
      }
    }

    // =========================================================================
    // Summary Rail (Live-Updating Right Sidebar)
    // =========================================================================

    updateSummaryRail() {
      if (!this.railSpecs) return;

      const ring = this.rings[0]; // Show first ring's summary (multi-ring can be expanded later)
      const rows = [];

      if (ring.metalType) {
        rows.push(['Metal', ring.carat ? `${ring.carat} ${ring.metalType}` : ring.metalType]);
      }
      if (ring.hasDiamond === true && ring.settingStyle) {
        const styleLabel = ring.settingStyle.replace(/^\w/, (c) => c.toUpperCase());
        rows.push(['Setting', styleLabel]);
      }
      if (ring.hasDiamond === true) {
        // RSFY-647 P1(g) — only show cut in the summary when the catalogue
        // actually carries cut data; otherwise the value is symbolic.
        const parts = [this._caratDisplayLabel(ring.diamondCarat)];
        if (this._hasCutData(ring) && ring.diamondCut) {
          parts.push(ring.diamondCut.replace(/^\w/, (c) => c.toUpperCase()));
        }
        parts.push(ring.diamondColor, ring.diamondClarity);
        rows.push(['Diamond', parts.join(' · ')]);
      } else if (ring.hasDiamond === false) {
        rows.push(['Diamond', 'Plain band']);
      } else if (ring.diamond && ring.diamond !== 'none') {
        rows.push(['Diamond', ring.diamond]);
      }
      if (ring.engravingDetails.hasEngraving) {
        if (ring.engravingDetails.outsideEngraving && ring.engravingDetails.outsideText) {
          const lang = ring.engravingDetails.outsideLanguage === 'elvish' ? 'Tengwar' : 'English';
          rows.push(['Outside', `${lang} — "${ring.engravingDetails.outsideText.substring(0, 30)}…"`]);
        }
        if (ring.engravingDetails.insideEngraving && ring.engravingDetails.insideText) {
          const lang = ring.engravingDetails.insideLanguage === 'elvish' ? 'Tengwar' : 'English';
          rows.push(['Inside', `${lang} — "${ring.engravingDetails.insideText.substring(0, 30)}…"`]);
        }
      } else if (ring.engravingDetails.hasEngraving === false) {
        rows.push(['Engraving', 'None']);
      }
      // IDKMS ("I don't know my size") is a valid committed state — render
      // the row even when ring.size is null so the rail mirrors the slip.
      if (ring.size || (ring.sizeUnknown || ring.sizeRegion === 'idkms')) {
        rows.push(['Size', this._formatSizeForReview(ring)]);
      }

      let html = '';
      rows.forEach(([label, value]) => {
        html += `
          <div class="risify-rb__summary-rail-row">
            <span class="risify-rb__summary-rail-label">${label}</span>
            <span class="risify-rb__summary-rail-value">${value}</span>
          </div>
        `;
      });

      this.railSpecs.innerHTML = html;

      // Update afterpay
      if (this.railAfterPay) {
        const variant = ring.selectedVariant;
        if (variant && variant.price) {
          const installment = Math.round(variant.price / 400);
          this.railAfterPay.textContent = `or 4 × ${this.formatMoney(variant.price / 4)} interest-free`;
        } else {
          this.railAfterPay.textContent = '';
        }
      }

      // Bug fix #7 — Keep ring tab summaries in lock-step with the rail so
      // every selection (including engraving inputs) is reflected at the top.
      this._updateRingTabSummaries && this._updateRingTabSummaries();
    }

    // =========================================================================
    // Ring Selectors (checkboxes per step)
    // =========================================================================

    /**
     * RSFY-651 madde 7 — Per-step "Apply to Ring 1/2/3" checkbox model is
     * retired. Multi-ring is now driven by `currentRingIndex` + top tabs.
     * Method kept as a no-op so existing call sites stay valid while the
     * status container visibility (one of its old side effects) is preserved.
     */
    setupRingSelectors() {
      // No-op — RSFY-651 sequential multi-ring uses top tabs only.
      // Legacy [data-ring-status] strip removed from markup.
    }

    /**
     * RSFY-651: Sequential multi-ring model.
     * Returns the current ring index as a single-element array. Previously
     * this read per-step "Apply to Ring 1/2/3" checkboxes; the new design
     * is sequential — one ring at a time, navigated via top tabs.
     */
    getCheckedRings(stepName) {
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      return [idx];
    }

    requireCheckedRings(stepName) {
      const checked = this.getCheckedRings(stepName);
      if (this.ringCount > 1 && checked.length === 0) {
        this.showNoRingSelectedWarning(stepName);
        return null;
      }
      return checked;
    }

    /**
     * Legacy guard from the old "Apply to Ring 1/2/3" checkbox model.
     * In sequential mode `getCheckedRings` always returns one ring, so this
     * never fires — kept as a no-op to keep `requireCheckedRings` callable
     * without conditional checks.
     */
    showNoRingSelectedWarning(stepName) {
      // no-op (RSFY-651 sequential mode)
    }

    // =========================================================================
    // Ring Status Display
    // Legacy [data-ring-status] strip retired — RSFY-651 tabs surface this
    // info. Method kept as a thin wrapper around _updateRingTabSummaries so
    // existing call sites stay valid.
    // =========================================================================

    updateRingStatusDisplay() {
      if (typeof this._updateRingTabSummaries === 'function') {
        this._updateRingTabSummaries();
      }
    }

    // =========================================================================
    // Metal Type Selection
    // =========================================================================

    populateMetalTypes() {
      const products = this.designProducts[this.currentDesignId] || [];
      const metalMap = new Map();

      // Default to the current ring's diamond state so the thumbnails
      // reflect what the customer will actually see in 3D. Builder boots
      // in Band Only (hasDiamond === false), so plain-band photos lead.
      // The cards re-render when `_setDiamondHas` flips this flag.
      const ringIdx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = this.rings && this.rings[ringIdx];
      const preferPlain = !ring || ring.hasDiamond !== true;

      products.forEach((product) => {
        const metalType = this.extractMetalType(product);
        if (!metalType) return;
        const isPlain = this._tagValue(product, 'rb-diamond') === 'no';
        const existing = metalMap.get(metalType);
        if (!existing) {
          metalMap.set(metalType, product);
          return;
        }
        // Replace the existing pick if the new product matches the preferred
        // diamond state better than the one we already stored.
        const existingIsPlain = this._tagValue(existing, 'rb-diamond') === 'no';
        if (preferPlain && isPlain && !existingIsPlain) {
          metalMap.set(metalType, product);
        } else if (!preferPlain && !isPlain && existingIsPlain) {
          metalMap.set(metalType, product);
        }
      });

      this.metalTypesContainer.innerHTML = '';

      if (metalMap.size === 0) {
        this.metalTypesContainer.innerHTML =
          '<p class="risify-rb__no-products">No products found for this design. Please configure product tags or handles in the section settings.</p>';
        return;
      }

      const metalNotes = {
        'Yellow Gold': 'warm, classic, the original',
        'White Gold': 'cool, contemporary, restrained',
        'Red Gold': 'soft, romantic, feminine',
        'Rose Gold': 'soft, romantic, feminine',
        'Platinum': 'the rarest — heirloom-grade',
      };

      // Pre-bundled plain-band thumbnails by metal — used when the customer
      // is on the Band Only path so cards consistently show the metal
      // colour without depending on whether the catalogue's band SKU has
      // an image attached. Asset URLs are injected by the Liquid host as
      // `data-band-thumb-<slug>` attributes on <risify-ring-builder>.
      const bandThumbForMetal = (metalType) => {
        if (!preferPlain || !metalType) return '';
        // Browser dataset rule: `data-band-thumb-red-gold` ⇒ `dataset.bandThumbRedGold`
        // ("each `-letter` becomes upper-cased letter"). Build the same
        // shape from the metal type name.
        const slug = String(metalType).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const camel = slug.split('-').map((part, i) => i === 0 ? part : (part.charAt(0).toUpperCase() + part.slice(1))).join('');
        const key = `bandThumb${camel.charAt(0).toUpperCase() + camel.slice(1)}`;
        return this.dataset[key] || '';
      };

      metalMap.forEach((product, typeName) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'risify-rb__metal-card';
        card.dataset.metalType = typeName;

        const productImg = product.featuredImage || (product.images && product.images[0]) || '';
        const bandImg = bandThumbForMetal(typeName);
        // Prefer the bundled band asset when the customer is on Band Only;
        // fall through to the product image (covers diamond path + future
        // metals without a band asset).
        const imgSrc = (preferPlain && bandImg) || productImg || bandImg || '';
        const note = metalNotes[typeName] || '';

        // Figma 6002:6416 / 6002:6420 — image 72×72 white panel, label +
        // tagline middle, 32×32 check icon (white bg, red tick) right when
        // selected. The check uses an inline SVG so its colour can flip with
        // the active modifier without re-rendering the card.
        card.innerHTML = `
          <div class="risify-rb__metal-card-image">
            ${imgSrc ? `<img src="${imgSrc}" alt="${typeName}" loading="lazy">` : ''}
          </div>
          <div class="risify-rb__metal-card-info">
            <div class="risify-rb__metal-card-label">${typeName}</div>
          </div>
          <div class="risify-rb__metal-card-check" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M4 10.5l4 4 8-9" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        `;

        card.addEventListener('click', () => this.selectMetalType(typeName));
        this.metalTypesContainer.appendChild(card);
      });
    }

    /**
     * Parse a product's pipe-delimited `tags` field (emitted by the liquid
     * as `{{ product.tags | join: '|' | json }}`) into a lowercased array.
     * Empty / missing returns []. Used by tag-driven helpers below.
     */
    _productTags(product) {
      if (!product) return [];
      const raw = product.tags;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.map(t => String(t).toLowerCase().trim()).filter(Boolean);
      return String(raw).toLowerCase().split('|').map(t => t.trim()).filter(Boolean);
    }

    /**
     * Extract the suffix after `prefix:` from product tags. Example:
     *   _tagValue(p, 'rb-diamond') → 'yes' / 'no' / null
     */
    _tagValue(product, prefix) {
      const needle = prefix.endsWith(':') ? prefix : (prefix + ':');
      const t = this._productTags(product).find(t => t.startsWith(needle));
      return t ? t.slice(needle.length) : null;
    }

    /**
     * Like _tagValue but returns ALL suffixes matching the prefix. Products
     * with overlapping axes (e.g. a Modern engagement ring tagged with both
     * `rb-diamond-setting:bezel` and `rb-diamond-setting:modern`) need this
     * so the setting-style availability map sees every value, not just the
     * first one the catalog declares.
     */
    _tagValues(product, prefix) {
      const needle = prefix.endsWith(':') ? prefix : (prefix + ':');
      return this._productTags(product)
        .filter((t) => t.startsWith(needle))
        .map((t) => t.slice(needle.length));
    }

    /**
     * Normalise stone-quality strings so "VS-1", "VS 1", "vs1" all compare
     * equal. Used for fallback matching when bundled strings don't fully
     * parse via _parseStoneQualityString.
     */
    _normalizeStoneQuality(s) {
      return String(s || '').replace(/[\s\-_]/g, '').toUpperCase();
    }

    /**
     * Parse the bundled "Stone Quality" variant string our catalog uses
     * (e.g. "~0.50ct G/H SI Diamond / Outside Only") into discrete axes
     * the D3 UI surfaces. Cut is intentionally absent — the catalog has
     * no per-cut data.
     */
    _parseStoneQualityString(s) {
      if (!s) return null;
      const str = String(s);
      const caratM = str.match(/~?\s*(\d*\.?\d+)\s*ct/i);
      // RSFY-647 P0(b) — capture BOTH letters of a colour range like "G/H",
      // "G-H", "G H". Previously the second letter was dropped.
      const colorM = str.match(/\b([D-K])(?:\s*[\/\-]\s*([D-K]))?\b/);
      // RSFY-647 P0(b) — same for clarity, normalised to grade family (VS, SI, …).
      const clarityM = str.match(/\b(FL|IF|VVS\d?|VS\d?|SI\d?|I[1-3]?)(?:\s*[\/\-]\s*(FL|IF|VVS\d?|VS\d?|SI\d?|I[1-3]?))?\b/i);
      const carat = caratM ? parseFloat(caratM[1]) : null;

      const colors = [];
      if (colorM) {
        if (colorM[1]) colors.push(colorM[1].toUpperCase());
        if (colorM[2] && colorM[2].toUpperCase() !== colorM[1].toUpperCase()) {
          colors.push(colorM[2].toUpperCase());
        }
      }

      const normaliseClarity = (raw) => {
        if (!raw) return null;
        return raw.toUpperCase().replace(/[123]$/, '');
      };
      const clarities = [];
      if (clarityM) {
        const a = normaliseClarity(clarityM[1]);
        const b = normaliseClarity(clarityM[2]);
        if (a) clarities.push(a);
        if (b && b !== a) clarities.push(b);
      }

      // Legacy scalar fields kept for callers that haven't migrated to arrays.
      return {
        carat,
        color: colors[0] || null,
        clarity: clarities[0] || null,
        colors,
        clarities,
      };
    }

    extractMetalType(product) {
      const hiddenMetals = (this.config.hiddenMetals || []).map(m => m.toLowerCase().trim()).filter(Boolean);
      const isHidden = (str) => hiddenMetals.some(h => str.includes(h));

      // Canonical source of truth — the `rb-metal:` catalog tag. Handles and
      // titles drift over time (a Platinum product whose handle still ends
      // with `-yellow-gold` from an earlier rename will mis-classify if we
      // trust handle text). The tag is the explicit operator-set value.
      // Falls back to handle/title only when no tag is present.
      const metalTags = this._tagValues(product, 'rb-metal');
      if (metalTags.length > 0) {
        // Hidden-metal filter must beat everything — if any tag matches a
        // hidden metal name, exclude the product.
        if (metalTags.some(isHidden)) return null;
        const canon = (raw) => {
          const t = raw.toLowerCase();
          if (t.includes('red') || t.includes('rose')) return 'Red Gold';
          if (t.includes('yellow')) return 'Yellow Gold';
          if (t.includes('white')) return 'White Gold';
          if (t.includes('platinum')) return 'Platinum';
          return null;
        };
        const canons = [...new Set(metalTags.map(canon).filter(Boolean))];
        if (canons.length === 1) return canons[0];
        if (canons.length > 1) {
          // Contradictory tags — a ring is one metal, but the catalogue can
          // carry a stray tag ("Modern ~.33ct 14ct White Gold" is tagged both
          // rb-metal:red AND rb-metal:white). First-tag order classified it as
          // Red Gold, so White Gold 14ct Modern lost its 1/3 ct option and the
          // red pool gained a white product.
          //
          // Tie-break by TITLE ONLY. Not the handle: that same product's
          // handle is "...-red-gold" — it was cloned from the red ring and
          // never re-handled, which is exactly the drift the comment above
          // warns about (and how the stray tag got there in the first place).
          // The title is customer-facing and maintained. Handle is a last
          // resort, then tag order.
          const title = (product.title || '').toLowerCase();
          const byTitle = canons.find((c) => title.includes(c.split(' ')[0].toLowerCase()));
          if (byTitle) return byTitle;
          const handle = (product.handle || '').toLowerCase();
          const byHandle = canons.find((c) => handle.includes(c.split(' ')[0].toLowerCase()));
          if (byHandle) return byHandle;
          return canons[0];
        }
        // Unrecognised tag — return the first raw value as-is.
        return metalTags[0];
      }

      const meta = product.metalType;
      if (meta) {
        const raw = Array.isArray(meta) ? meta[0] : typeof meta === 'string' ? meta : '';
        const cleaned = String(raw).replace(/[\[\]"]/g, '').trim().toLowerCase();
        if (isHidden(cleaned)) return null;
        if (cleaned.includes('red') || cleaned.includes('rose')) return 'Red Gold';
        if (cleaned.includes('yellow')) return 'Yellow Gold';
        if (cleaned.includes('white')) return 'White Gold';
        if (cleaned.includes('platinum')) return 'Platinum';
        if (cleaned) return String(raw).replace(/[\[\]"]/g, '').trim();
      }

      const h = (product.handle || '').toLowerCase();
      if (isHidden(h)) return null;
      if (h.includes('red-gold') || h.includes('rose-gold')) return 'Red Gold';
      if (h.includes('yellow-gold')) return 'Yellow Gold';
      if (h.includes('white-gold')) return 'White Gold';
      if (h.includes('platinum')) return 'Platinum';

      const t = (product.title || '').toLowerCase();
      if (isHidden(t)) return null;
      if (t.includes('red gold') || t.includes('rose gold')) return 'Red Gold';
      if (t.includes('yellow gold')) return 'Yellow Gold';
      if (t.includes('white gold')) return 'White Gold';
      if (t.includes('platinum')) return 'Platinum';

      return null;
    }

    extractCarat(product) {
      // Canonical source — the `rb-caret:` tag (the catalog stores both
      // `rb-caret:14crt` and `rb-caret:14ct`; pick the first numeric one).
      // Handle/title fallbacks come second because some products were
      // renamed without updating handles (e.g. a Platinum ring whose
      // handle still ends `-14ct-yellow-gold` from an earlier draft).
      const caretTags = this._tagValues(product, 'rb-caret');
      for (const raw of caretTags) {
        const m = String(raw).match(/^(\d+)\s*c(t|rt)$/i);
        if (m) {
          const num = parseInt(m[1], 10);
          if ([9, 14, 18, 22].includes(num)) return num + 'ct';
        }
      }

      // Tag for platinum is `rb-metal:platinum` (no `rb-caret`), so check
      // the metal tag directly before falling back to text scanning.
      const metalTags = this._tagValues(product, 'rb-metal');
      if (metalTags.some((t) => /^platinum$/i.test(t))) return 'Platinum';

      const title = (product.title || '');
      const handle = (product.handle || '');

      const matches = title.match(/(?:^|[\s,\-])(\d+)\s*ct\b/gi);
      if (matches) {
        for (const m of matches) {
          const numMatch = m.match(/(\d+)\s*ct/i);
          if (numMatch) {
            const num = parseInt(numMatch[1], 10);
            if ([9, 14, 18, 22].includes(num)) return num + 'ct';
          }
        }
      }

      const handleMatches = handle.match(/(?:^|-)(\d+)ct(?:-|$)/gi);
      if (handleMatches) {
        for (const m of handleMatches) {
          const numMatch = m.match(/(\d+)ct/i);
          if (numMatch) {
            const num = parseInt(numMatch[1], 10);
            if ([9, 14, 18, 22].includes(num)) return num + 'ct';
          }
        }
      }

      const text = (title + ' ' + handle).toLowerCase();
      if (text.includes('platinum')) return 'Platinum';

      return null;
    }

    selectMetalType(typeName) {
      const checkedRings = this.requireCheckedRings('metal');
      if (!checkedRings) return;

      // Picking a metal resets the carat — drop any leftover "Pick a carat
      // to continue." pill so the customer doesn't see it for the new pool.
      // It'll come back if they hit Continue without choosing.
      if (this.clearStepWarning) this.clearStepWarning();

      checkedRings.forEach((i) => {
        this.rings[i].metalType = typeName;
        this.rings[i].carat = null;
        this.rings[i].hasDiamond = false;
        this.rings[i].settingStyle = null;
        this.rings[i].diamondCarat = 0.50;
        this.rings[i].diamondCut = 'round';
        this.rings[i].diamondColor = 'G';
        this.rings[i].diamondClarity = 'SI';
        this.rings[i].diamondQuality = 'High Quality';
        this.rings[i].diamond = null;
        this.rings[i].engraving = null;
        this.rings[i].engravingDetails = makeDefaultEngravingDetails();
        this.rings[i].size = null;
        this.rings[i].selectedProduct = null;
        this.rings[i].selectedVariant = null;
      });

      // Highlight card
      this.metalTypesContainer.querySelectorAll('.risify-rb__metal-card').forEach((card) => {
        card.classList.toggle('risify-rb__metal-card--selected', card.dataset.metalType === typeName);
      });

      this.showCaratsForMetal(typeName);
      this.updateMetalSelectedLabel(typeName, null);

      const products = this.getProductsForMetal(typeName);
      if (products.length > 0) {
        // Prefer the plain-band variant for the preview image — hasDiamond
        // was just reset to `false` above, so the customer should see the
        // Movie Ring (no diamond) right away when they pick a metal. Without
        // this filter we'd grab whatever happened to sort first (often a
        // diamond product), which loads the diamond GLB and the customer
        // sees the prong/setting before they've opted in.
        const plainFirst = products.find((p) => this._tagValue(p, 'rb-diamond') === 'no') || products[0];
        this.updateProductImage(plainFirst, null, checkedRings);
      }

      this.updateRingStatusDisplay();
      this.updateSummaryRail();
    }

    /**
     * Map a metal type to its icon "tone": yellow / red / white. Drives both
     * the carat sphere colour (CSS `--rb-carat-sphere-*` via a tone class) and
     * which band-width photo set to show (Figma nlI3JviDaoH7dWDQ4Afz2x). Red &
     * Rose Gold share the red set; White Gold, Platinum & Palladium share the
     * white (silver) set; everything gold-ish falls back to yellow.
     */
    _metalTone(metalType) {
      const m = String(metalType || '').toLowerCase();
      if (m.includes('red') || m.includes('rose')) return 'red';
      if (m.includes('white') || m.includes('platinum') || m.includes('palladium')) return 'white';
      return 'yellow';
    }

    /**
     * Point the band-width cards' photos at the set matching the current
     * metal's tone. Called whenever the metal changes (and on width-picker
     * reveal) so step-2 Band Only widths show e.g. rose-gold rings for Red
     * Gold. URLs come from `data-bandwidth-{tone}-{width}` on the host element.
     */
    _applyBandWidthIcons(metalType) {
      const tone = this._metalTone(metalType);
      const imgs = this.querySelectorAll('[data-width-img]');
      imgs.forEach((img) => {
        const width = img.dataset.widthImg; // standard | narrow | slim | petite
        const camel = `bandwidth${tone.charAt(0).toUpperCase() + tone.slice(1)}${width.charAt(0).toUpperCase() + width.slice(1)}`;
        const url = this.dataset[camel];
        if (url && img.getAttribute('src') !== url) img.setAttribute('src', url);
      });
    }

    showCaratsForMetal(typeName) {
      const products = this.getProductsForMetal(typeName);
      const caratSet = new Map();
      const caratTone = this._metalTone(typeName);
      // Keep the band-width photos in sync with the chosen metal too — the
      // width picker lives in step 2 but its images key off the step-1 metal.
      this._applyBandWidthIcons(typeName);

      // Prefer the flagship Movie Ring (handle prefix `movie-ring-` without
      // `little-`/`petite-`) for each carat's card so the price tag mirrors
      // the live Ring Builder. Falls back to whatever the catalog returns
      // first if no flagship is present.
      const flagshipHandle = /^movie-ring-/i;
      products.forEach((p) => {
        const carat = this.extractCarat(p);
        if (!carat) return;
        const isFlagship = flagshipHandle.test(p.handle || '');
        if (!caratSet.has(carat) || (isFlagship && !flagshipHandle.test(caratSet.get(carat).handle || ''))) {
          caratSet.set(carat, p);
        }
      });

      this.caratPills.innerHTML = '';

      // Figma — each carat card carries a two-line colour/character description
      // (not a one-word tier), and the wording is per-metal. Kept as a single
      // string with a newline; the template splits it into two lines. Defined
      // up here (before the Platinum branch) so both paths can read it.
      //   Yellow Gold 18977:9685  Red Gold 18977:7388  White Gold 18977:9054
      //   Platinum 18977:10288 (single disabled card → 'default' description)
      // Yellow Gold 9ct/14ct first lines are client wording (RSFY-801) and
      // intentionally differ from the Figma node — don't "fix" them back.
      const caratSublabelsByMetal = {
        'Yellow Gold': {
          '9ct': 'Soft sandy yellow tone.\nAn affordable entry point.',
          '14ct': 'Light straw yellow tone.\nA popular mid-range gold.',
          '18ct': 'Classic golden colour.\nOriginal Movie Ring gold.',
          '22ct': 'Deep, rich golden colour.\nNear-pure gold.',
        },
        'Red Gold': {
          '9ct': 'Lighter rose colour red gold.\nAn affordable entry point.',
          '14ct': 'Classic red-rose colour.\nA popular mid-range choice.',
          '18ct': 'Rich deep colour.\nThe premium red gold.',
        },
        'White Gold': {
          '9ct': 'Pale, cool off white.\nAn affordable entry price.',
          '14ct': 'Mid-grey off-white.\nA popular mid-range choice.',
          '18ct': 'Warm quicksilver tone.\nSubstantial weight and character.',
        },
        'Platinum': {
          'default': 'Pure, weighty, enduring white platinum:\nthe global luxury benchmark.',
        },
      };
      const caratSublabels = caratSublabelsByMetal[typeName] || caratSublabelsByMetal['Yellow Gold'];

      // Platinum has a single, fixed carat, so there is nothing to choose.
      // Figma 18977:10288 shows the CARAT section is still present but renders
      // one DISABLED card (no carat label, no price — just the metal
      // description). We still auto-select the underlying carat so the variant
      // matcher + 3D viewer stay fully defined.
      const isPlatinum = typeName === 'Platinum';
      if (isPlatinum) {
        if (caratSet.size >= 1) {
          const [carat] = caratSet.keys();
          this.autoSelectCarat(carat);
        }
        this._renderDisabledCaratCard(caratTone, caratSublabels['default'], '950 grade');
        this.caratSection.style.display = '';
        return;
      }

      if (caratSet.size <= 1) {
        this.caratSection.style.display = 'none';
        if (caratSet.size === 1) {
          const [carat] = caratSet.keys();
          this.autoSelectCarat(carat);
        }
        return;
      }

      const sortedCarats = [...caratSet.keys()].sort((a, b) => {
        const numA = parseInt(a, 10) || 999;
        const numB = parseInt(b, 10) || 999;
        return numA - numB;
      });

      // Figma 18977:9685 — 18ct is the "Recommended" carat (highlighted card
      // border + a "Recommended" badge pinned under the card): it's the
      // original Movie Ring gold. This only applies to Yellow Gold — the other
      // metals (Red/White Gold, Platinum) carry no recommendation.
      const recommendedCarat = '18ct';
      const metalIsRecommendable = typeName === 'Yellow Gold';

      sortedCarats.forEach((carat) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        const isRecommended = metalIsRecommendable && carat === recommendedCarat;
        pill.className = `risify-rb__pill risify-rb__pill--tone-${caratTone}${isRecommended ? ' risify-rb__pill--recommended' : ''}`;
        pill.dataset.carat = carat;

        const sublabel = caratSublabels[carat] || '';
        // Two-line description → separate <span>s so each line wraps cleanly.
        const sublabelHtml = sublabel
          ? sublabel.split('\n').map((line) => `<span class="risify-rb__pill-sublabel-line">${line}</span>`).join('')
          : '';
        // Card price = the plain-band starting price for this carat, i.e. the
        // cheapest variant (no engraving add-on). This keeps the card label
        // in sync with the footer total, which starts from the same plain
        // band. (Previously the card used the "Both Outside & Inside"
        // engraving variant, which read ~$180 higher than the footer and
        // looked like a pricing bug.)
        const sourceProduct = caratSet.get(carat);
        let priceText = '';
        if (sourceProduct && Array.isArray(sourceProduct.variants)) {
          const refVariant = sourceProduct.variants
            .filter((v) => typeof v.price === 'number')
            .sort((a, b) => a.price - b.price)[0];
          if (refVariant) {
            priceText = this._formatVariantPrice
              ? this._formatVariantPrice({ price: refVariant.price })
              : `$${Math.round(refVariant.price / 100).toLocaleString()}`;
          }
        }

        // Figma carat card: circular ring thumbnail + carat / tagline /
        // price stacked centred. When the card is selected it switches to
        // brand-red bg and the price line is replaced by a "Selected"
        // label (Figma 2026 refresh — node 6002:6415). Both spans are
        // always rendered; CSS toggles their visibility based on the
        // `.risify-rb__pill--selected` modifier.
        // Sphere icon (Figma "metal-quality-*" 24px): a real glossy metal-ball
        // PNG per tone, served from the host's data-carat-{tone} URL. Falls back
        // to no <img> (CSS keeps the empty circle) if the asset URL is missing.
        const caratIconUrl = this.dataset[`carat${caratTone.charAt(0).toUpperCase() + caratTone.slice(1)}`];
        const caratThumb = caratIconUrl
          ? `<span class="risify-rb__pill-thumb" aria-hidden="true"><img class="risify-rb__pill-thumb-img" src="${caratIconUrl}" alt="" width="24" height="24" loading="lazy"></span>`
          : `<span class="risify-rb__pill-thumb" aria-hidden="true"></span>`;

        pill.innerHTML = `
          ${caratThumb}
          <span class="risify-rb__pill-label">${carat}</span>
          ${sublabelHtml ? `<span class="risify-rb__pill-sublabel">${sublabelHtml}</span>` : ''}
          ${priceText ? `<span class="risify-rb__pill-price">${priceText}</span>` : ''}
          <span class="risify-rb__pill-selected-label">Selected</span>
          ${isRecommended ? '<span class="risify-rb__pill-recommended-badge">Recommended</span>' : ''}
        `;

        pill.addEventListener('click', () => this.selectCarat(carat));
        this.caratPills.appendChild(pill);
      });

      this.caratSection.style.display = '';
    }

    /**
     * Figma 18977:10288 — Platinum's CARAT section shows a single, non-
     * interactive card: metal sphere icon + the two-line description only (no
     * carat label, no price, not clickable). The metal's carat is auto-selected
     * separately so the ring stays fully defined. `sublabel` is the two-line
     * "\n"-joined description; `tone` picks the sphere icon.
     */
    _renderDisabledCaratCard(tone, sublabel, label) {
      if (!this.caratPills) return;
      const caratIconUrl = this.dataset[`carat${tone.charAt(0).toUpperCase() + tone.slice(1)}`];
      const caratThumb = caratIconUrl
        ? `<span class="risify-rb__pill-thumb" aria-hidden="true"><img class="risify-rb__pill-thumb-img" src="${caratIconUrl}" alt="" width="24" height="24" loading="lazy"></span>`
        : `<span class="risify-rb__pill-thumb" aria-hidden="true"></span>`;
      const sublabelHtml = sublabel
        ? sublabel.split('\n').map((line) => `<span class="risify-rb__pill-sublabel-line">${line}</span>`).join('')
        : '';

      // `--selected` gives it the chosen (highlighted) look so it reads as
      // "already selected" rather than an inert, greyed-out card — there is
      // only one Platinum carat, so it IS the selection. `--disabled` keeps it
      // non-interactive (no pointer/hover), while `--selected` supplies the
      // active styling.
      const card = document.createElement('div');
      card.className = `risify-rb__pill risify-rb__pill--tone-${tone} risify-rb__pill--disabled risify-rb__pill--selected`;
      card.setAttribute('aria-disabled', 'true');
      // Platinum has one fixed grade, so this card had no heading while the
      // gold cards all show a bold carat ("9ct", "18ct"). Client asked for the
      // grade to read the same way, so the label slot is filled with "950
      // grade" using the same markup as a normal carat pill.
      card.innerHTML = `
        ${caratThumb}
        <span class="risify-rb__pill-label">${label || ''}</span>
        ${sublabelHtml ? `<span class="risify-rb__pill-sublabel">${sublabelHtml}</span>` : ''}
      `;
      this.caratPills.appendChild(card);
    }

    autoSelectCarat(carat) {
      const checkedRings = this.requireCheckedRings('metal');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        this.rings[i].carat = carat;
      });
      this.resolveProductForRings(checkedRings);
      // Resolve a concrete variant immediately so the footer total reflects
      // the picked carat (including engravingDetails defaults like "Both
      // Outside & Inside"). Without this the footer keeps the previous
      // metal's variant or shows the catalog `variants[0]` which doesn't
      // respect engraving state.
      this.updateVariantMatchForRings(checkedRings);
      this.updateRingStatusDisplay();
      this.updateSummaryRail();
    }

    selectCarat(carat) {
      const checkedRings = this.requireCheckedRings('metal');
      if (!checkedRings) return;

      checkedRings.forEach((i) => {
        this.rings[i].carat = carat;
        this.rings[i].hasDiamond = false;
        this.rings[i].settingStyle = null;
        this.rings[i].diamondCarat = 0.50;
        this.rings[i].diamondCut = 'round';
        this.rings[i].diamondColor = 'G';
        this.rings[i].diamondClarity = 'SI';
        this.rings[i].diamondQuality = 'High Quality';
        this.rings[i].diamond = null;
        this.rings[i].engraving = null;
        this.rings[i].engravingDetails = makeDefaultEngravingDetails();
        this.rings[i].size = null;
        this.rings[i].selectedProduct = null;
        this.rings[i].selectedVariant = null;
      });

      this.caratPills.querySelectorAll('.risify-rb__pill').forEach((pill) => {
        pill.classList.toggle('risify-rb__pill--selected', pill.dataset.carat === carat);
      });

      // Dismiss any footer warning that was nagging the customer to pick a carat.
      this.clearStepWarning();

      const firstRing = this.rings[checkedRings[0]];
      this.updateMetalSelectedLabel(firstRing.metalType, carat);

      this.resolveProductForRings(checkedRings);
      // Match autoSelectCarat: resolve the concrete variant so the footer
      // total honours engravingDetails (defaults to the None variant — the
      // engraving charge is opt-in; see deriveEngravingValue for the product
      // decision and the deliberate divergence from the live builder).
      this.updateVariantMatchForRings(checkedRings);
      this.updateRingStatusDisplay();
      this.updateSummaryRail();
    }

    resolveProductForRings(ringIndices) {
      ringIndices.forEach((i) => {
        const ring = this.rings[i];
        if (!ring.metalType || !ring.carat) return;

        let products = this.getProductsForMetal(ring.metalType);

        // RSFY-676 — when the customer hasn't asked for a diamond (either the
        // toggle is still untouched or they picked "No diamond"), prefer the
        // Movie Ring / plain-band products (rb-diamond:no) over the diamond
        // ones. Halfdan's call: most builder users are wedding-band buyers, so
        // the default imagery should be the plain band — the diamond ring only
        // appears once they explicitly switch the diamond toggle on.
        if (ring.hasDiamond !== true) {
          const plainFiltered = products.filter((p) => this._tagValue(p, 'rb-diamond') === 'no');
          if (plainFiltered.length > 0) products = plainFiltered;
          // RSFY-707 — band-width is now a real product axis (Standard 7mm,
          // Narrow 5mm, Slim 4mm, Petite 3mm). Each width maps to a distinct
          // product (`movie-ring-*` / `little-moive-ring-*` / `petite-movie-
          // ring-{N}mm-*`) carrying its own price. Filter by the catalog tag
          // `rb-band-width:{N}mm` if the customer has picked a width; this
          // replaces the previous "flagship-first" sort. When width is still
          // null (customer hasn't picked yet in Step 2), fall back to the
          // flagship 7mm so the price/image stays sensible until they choose.
          if (ring.bandWidth) {
            const wantTag = `rb-band-width:${ring.bandWidth}mm`;
            const widthFiltered = products.filter((p) => this._tagValues(p, 'rb-band-width').includes(`${ring.bandWidth}mm`));
            if (widthFiltered.length > 0) products = widthFiltered;
          } else {
            const flagshipHandle = /^movie-ring-/i;
            products = [...products].sort((a, b) => {
              const aFlag = flagshipHandle.test(a.handle || '') ? 0 : 1;
              const bFlag = flagshipHandle.test(b.handle || '') ? 0 : 1;
              return aFlag - bFlag;
            });
          }
        }

        // RSFY-641: further filter by setting style if customer picked one.
        // Mirror availability map: read EVERY `rb-diamond-setting:` tag, not
        // just the first. Without this the matcher fails to find any
        // product when the customer picks `elegant`/`modern`/`sleek`/
        // `petite` on products that also carry a primary axis tag like
        // `bezel`/`crown`/`gypsy` (the original Artistry catalog pattern).
        if (ring.hasDiamond === true && ring.settingStyle) {
          const styleFiltered = products.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
          if (styleFiltered.length > 0) products = styleFiltered;
        }
        // RSFY-642 cut tag support — opt-in. Only narrow the pool if the catalog
        // carries `rb-diamond-cut:{cut}` tags for the current cut; otherwise cut
        // stays symbolic (no filter, all products keep matching).
        if (ring.hasDiamond === true && ring.diamondCut) {
          const cutFiltered = products.filter((p) => this._tagValue(p, 'rb-diamond-cut') === ring.diamondCut);
          if (cutFiltered.length > 0) products = cutFiltered;
        }
        // Pick the product whose diamond carat matches the customer's choice.
        // Catalog `rb-diamond-crt:` tags use the same leading-zero-less format
        // as variant titles (`50ct` = 0.50). When the carat tag is missing, we
        // fall back to whichever product the metal+style filter yielded.
        if (ring.hasDiamond === true && ring.diamondCarat != null) {
          const target = ring.diamondCarat;
          const caratFiltered = products.filter((p) => {
            const tag = this._tagValue(p, 'rb-diamond-crt');
            if (!tag) return false;
            const cleaned = String(tag).replace(/[^\d.]/g, '');
            if (!cleaned) return false;
            let num = parseFloat(cleaned);
            if (isNaN(num) || num <= 0) return false;
            if (!/\./.test(cleaned) && num >= 5) num = num / 100;
            return Math.abs(num - target) < 0.005;
          });
          if (caratFiltered.length > 0) products = caratFiltered;
        }
        const match = products.find((p) => this.extractCarat(p) === ring.carat);
        if (match) {
          ring.selectedProduct = match;
          if (match.variants && match.variants.length > 0) {
            ring.selectedVariant = match.variants[0];
          }
        }
      });

      ringIndices.forEach((i) => {
        const ring = this.rings[i];
        if (ring && ring.selectedProduct) {
          this.updateProductImage(ring.selectedProduct, ring.selectedVariant, [i]);
        }
      });

      const displayRing = ringIndices.map((i) => this.rings[i]).find((r) => r.selectedProduct) || this.rings[ringIndices[0]];
      if (displayRing) {
        this.updatePriceDisplay(displayRing.selectedVariant);
      }
    }

    updateMetalSelectedLabel(metalType, carat) {
      if (!this.metalSelectedLabel) return;
      if (metalType) {
        this.metalSelectedLabel.style.display = '';
        this.metalSelectedValue.textContent = carat ? `${metalType} - ${carat}` : metalType;
      } else {
        this.metalSelectedLabel.style.display = 'none';
      }
    }

    getProductsForMetal(typeName) {
      return (this.designProducts[this.currentDesignId] || []).filter(
        (p) => this.extractMetalType(p) === typeName
      );
    }

    // =========================================================================
    // Diamond (Stone Quality) Selection
    // =========================================================================

    /**
     * RSFY-647 P0(a) — available diamond axes for the **filtered product
     * pool** (metal + carat + settingStyle), not just selectedProduct. With
     * the old behaviour an empty selectedProduct or a multi-product pool
     * left every chip clickable; now the chips reflect the current funnel.
     *
     * Accepts a ring object so it can derive the pool. Legacy callers that
     * still pass a single product fall back to that product.
     */
    _availableDiamondAxes(ringOrProduct) {
      const axes = { carats: new Set(), colors: new Set(), clarities: new Set() };

      // Build the candidate product list.
      let pool = [];
      if (!ringOrProduct) {
        // No context — use every loaded product as a permissive default.
        pool = Object.values(window.ringBuilderProducts || {}).flat();
      } else if (Array.isArray(ringOrProduct.variants)) {
        // Legacy: a product was passed.
        pool = [ringOrProduct];
      } else {
        const ring = ringOrProduct;
        if (ring.metalType) {
          pool = this.getProductsForMetal(ring.metalType);
          if (ring.carat) {
            pool = pool.filter((p) => this.extractCarat(p) === ring.carat);
          }
          if (ring.settingStyle) {
            // Products carry multiple `rb-diamond-setting:` tags (primary
            // axis like `bezel`/`crown`/`gypsy` plus the friendlier
            // `elegant`/`modern`/`sleek`/`petite`). Match against every
            // value so a product tagged `bezel|elegant` is correctly
            // included in the Elegant pool.
            const filteredByStyle = pool.filter(
              (p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle)
            );
            // Only narrow if the setting style actually exists in the pool;
            // otherwise keep the broader pool so chips don't all disappear.
            if (filteredByStyle.length > 0) pool = filteredByStyle;
          }
        }
        // Fallback: if we couldn't derive anything, use the ring's product.
        if (pool.length === 0 && ring.selectedProduct) pool = [ring.selectedProduct];
      }

      pool.forEach((product) => {
        if (!product || !Array.isArray(product.variants)) return;
        const stoneIndex = this.findOptionIndex(product, 'stone');
        if (stoneIndex < 0) return;
        const key = `option${stoneIndex + 1}`;
        product.variants.forEach((v) => {
          const parsed = this._parseStoneQualityString(v[key]);
          if (!parsed) return;
          if (parsed.carat != null) axes.carats.add(parsed.carat);
          (parsed.colors || []).forEach((c) => axes.colors.add(c));
          (parsed.clarities || []).forEach((c) => axes.clarities.add(c));
        });
      });

      return axes;
    }

    /**
     * Cross-axis availability — return the set of values that are still
     * selectable on `field` given the ring's other axes. Used to disable
     * chips that, while present in the catalogue, would result in an
     * impossible combination (e.g. colour D with clarity SI when the only
     * D-bearing variants are D/F VS and the only SI-bearing variants are
     * G/H SI). Returns null when no constraint can be derived (so callers
     * should fall back to the permissive `_availableDiamondAxes` set).
     */
    _availableValuesForAxis(ring, field) {
      if (!ring || !ring.metalType || ring.hasDiamond !== true) return null;
      let pool = this.getProductsForMetal(ring.metalType);
      if (ring.carat) pool = pool.filter((p) => this.extractCarat(p) === ring.carat);
      if (ring.settingStyle) {
        // _tagValues, not _tagValue: products carry multiple
        // `rb-diamond-setting:` tags and the FIRST is often not the UI name
        // (Modern ~.50ct 22ct is ['bezel','modern'] while Modern ~.33ct 22ct
        // is just ['modern']). First-tag matching kept only the .33 ring, so
        // the available-carat set collapsed to {0.33} and picking Premium on
        // a 1/2 ct Modern snapped the carat back and failed the variant
        // match ("No variant matches 0.33ct · D · VS"). Same defect class as
        // the delta functions fixed earlier; converted everywhere.
        const narrowed = pool.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
        if (narrowed.length > 0) pool = narrowed;
      }
      if (pool.length === 0) return null;

      const out = new Set();
      pool.forEach((product) => {
        if (!Array.isArray(product.variants)) return;
        const stoneIndex = this.findOptionIndex(product, 'stone');
        if (stoneIndex < 0) return;
        const stoneKey = `option${stoneIndex + 1}`;
        product.variants.forEach((v) => {
          const parsed = this._parseStoneQualityString(v[stoneKey]);
          if (!parsed) return;
          // Apply the ring's other axes as filters — skip variants that
          // disagree with anything we've already pinned.
          if (field !== 'diamondCarat' && ring.diamondCarat != null
              && parsed.carat != null && parsed.carat !== ring.diamondCarat) return;
          if (field !== 'diamondColor' && ring.diamondColor) {
            const cs = parsed.colors && parsed.colors.length ? parsed.colors : (parsed.color ? [parsed.color] : []);
            if (cs.length && !cs.includes(ring.diamondColor)) return;
          }
          if (field !== 'diamondClarity' && ring.diamondClarity) {
            const ks = parsed.clarities && parsed.clarities.length ? parsed.clarities : (parsed.clarity ? [parsed.clarity] : []);
            if (ks.length && !ks.includes(ring.diamondClarity)) return;
          }
          // Variant survives — record its value(s) for the requested axis.
          if (field === 'diamondCarat' && parsed.carat != null) out.add(parsed.carat);
          else if (field === 'diamondColor') (parsed.colors || []).forEach((c) => out.add(c));
          else if (field === 'diamondClarity') (parsed.clarities || []).forEach((c) => out.add(c));
        });
      });
      return out;
    }

    populateDiamondOptions() {
      const root = this.querySelector('[data-step="diamond"]');
      if (!root) {
        if (this._debug) console.warn('[RB] populateDiamondOptions: [data-step="diamond"] not found in DOM');
        return;
      }
      if (this._debug) console.log('[RB] populateDiamondOptions', { rings: this.rings.length, ringIndex: this.currentRingIndex || 0 });

      // Bind once — markup is static; we only repaint state.
      if (!root.dataset.diamondBound) {
        root.dataset.diamondBound = 'true';

        root.querySelectorAll('[data-diamond-carat]').forEach((stop) => {
          stop.addEventListener('click', () => {
            this._setDiamondAxis('diamondCarat', parseFloat(stop.dataset.diamondCarat));
          });
        });
        root.querySelectorAll('[data-diamond-cut]').forEach((card) => {
          card.addEventListener('click', () => {
            this._setDiamondAxis('diamondCut', card.dataset.diamondCut);
          });
        });
        // RSFY-685 — bundled Stone Quality replaces standalone colour /
        // clarity chips. Underlying ring.diamondColor / diamondClarity are
        // derived from the chosen quality so the variant matcher still works.
        root.querySelectorAll('[data-diamond-quality]').forEach((card) => {
          card.addEventListener('click', () => {
            this._setDiamondQuality(card.dataset.diamondQuality);
          });
        });
        // RSFY-641: setting style cards. RSFY-801 — with the Band Only /
        // With Diamond fork gone, clicking a setting card IS choosing the
        // diamond path. The clicked style is written BEFORE the mode flip:
        // _setDiamondHas(true) only seeds a style when none is set, and both
        // it and _setSettingStyle fire un-awaited model loads — if they
        // resolved different styles in the same tick, the viewer's
        // append-only loader would stack both GLBs (dispose runs before the
        // first addFromPath settles). Same-URL loads dedup instead.
        root.querySelectorAll('[data-setting-style]').forEach((card) => {
          card.addEventListener('click', () => {
            const ring = this.rings[this.currentRingIndex || 0];
            if (ring && ring.hasDiamond !== true) {
              ring.settingStyle = card.dataset.settingStyle;
              this._setDiamondHas(true);
            }
            this._setSettingStyle(card.dataset.settingStyle);
          });
        });
        if (this._debug) console.log('[RB] diamond step handlers bound');
      }

      this._renderDiamondStep();
      this._renderSettingStyles();
    }

    /**
     * RSFY-641 — Setting style selection (Contemporary / Modern / Classic /
     * Elegant / Sleek / Petite). Filters products by `rb-diamond-setting:{style}`.
     */
    _setSettingStyle(style) {
      const ringIndex = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = this.rings[ringIndex];
      if (!ring) return;
      ring.settingStyle = style;

      // Resolve to the new style's product FIRST. The previous selectedProduct
      // (e.g. Contemporary .50ct) may not exist in the new style (e.g. Elegant
      // only ships in .33ct), so snapping against the stale product would
      // preserve an invalid carat and leave selectedVariant null. Re-resolve,
      // THEN snap the downstream axes against the new product's actual axes.
      this.resolveProductForRings([ringIndex]);

      // RSFY-647 P1(f) — narrowing the pool may invalidate the currently-
      // chosen carat/colour/clarity. Snap each downstream axis to the nearest
      // available value before re-rendering so we never leave an invalid
      // selection on screen.
      ring.diamondCarat = this._snapToNearestAxisValue(ring, 'diamondCarat', ring.diamondCarat);
      ring.diamondColor = this._snapToNearestAxisValue(ring, 'diamondColor', ring.diamondColor);
      ring.diamondClarity = this._snapToNearestAxisValue(ring, 'diamondClarity', ring.diamondClarity);
      // RSFY-715 — each setting only ships a fixed set of diamond weights
      // (e.g. Sleek = 1/10 ct only, Classic = 1/2 ct only). If the carried-over
      // carat isn't one of them, snap to the setting's smallest weight so the
      // pill, the 2D composite, and the variant all agree (and Sleek shows
      // "1/10 ct", not the stale "1/2 ct").
      const mapWeights = this._styling2dWeightsForSetting(ring.metalType, style);
      if (mapWeights.length && !mapWeights.some((w) => Math.abs(w - ring.diamondCarat) < 0.005)) {
        ring.diamondCarat = Math.min(...mapWeights);
      }
      // RSFY-685 — reconcile quality + re-snap colour/clarity to its mapping
      // BEFORE the variant matcher runs (see _setDiamondHas for why).
      this._syncDiamondQuality(ring);

      this.updateVariantMatchForRings([ringIndex]);
      this._renderSettingStyles();
      this._renderDiamondStep();
      this.updateRingStatusDisplay();
      if (this.updateSummaryRail) this.updateSummaryRail();
    }

    /**
     * Render the 6 setting style cards. Show only when ring.hasDiamond === true.
     * Cards available are determined by the current metal+carat filtered
     * product set's rb-diamond-setting:* tags.
     */
    _renderSettingStyles() {
      const root = this.querySelector('[data-step="diamond"]');
      if (!root) return;
      const container = root.querySelector('[data-setting-styles]');
      if (!container) return;

      const ringIndex = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = this.rings[ringIndex] || null;
      // RSFY-801 — the section is always visible now (no fork). Selection
      // highlight only applies in diamond mode; availability dimming and
      // thumbnails paint in both modes so the grid never looks broken.
      container.hidden = false;
      if (!ring) return;
      const inDiamondMode = ring.hasDiamond === true;

      // Determine which styles have products in the current metal+carat filter
      const available = new Set();
      const candidatePool = ring.metalType
        ? this.getProductsForMetal(ring.metalType).filter((p) => !ring.carat || this.extractCarat(p) === ring.carat)
        : [];
      candidatePool.forEach((p) => {
        // Products often carry multiple `rb-diamond-setting:` tags — the
        // catalog encodes a primary "setting axis" (bezel / crown / gypsy)
        // alongside a friendlier style name (elegant / modern / sleek /
        // petite). Reading only the first tag misses the friendly name and
        // hides legitimate options the catalog actually supports. Collect
        // every value so the availability map mirrors the original
        // Artistry behaviour where each tagged style enables its card.
        this._tagValues(p, 'rb-diamond-setting').forEach((style) => available.add(style));
      });

      container.querySelectorAll('[data-setting-style]').forEach((card) => {
        const key = card.dataset.settingStyle;
        const selected = inDiamondMode && ring.settingStyle === key;
        const enabled = available.size === 0 || available.has(key);
        card.classList.toggle('risify-rb__setting-style-card--selected', selected);
        card.style.opacity = enabled ? '' : '0.35';
        card.disabled = !enabled;

        // Populate thumbnail with first matching product image (lazy).
        // If the merchant set an image via the `setting_style` theme editor
        // block (RSFY-641 madde 7), the Liquid renders the override as an
        // inline `background-image` + `data-thumb-override` — leave it alone.
        //
        // The thumbnail is metal-specific (candidatePool is filtered by
        // ring.metalType), so cache it PER METAL — re-populate whenever the
        // customer switches metal. Keying `populated` on the metal (instead of
        // a one-shot 'true') fixes the bug where the cards kept showing the
        // first-viewed metal's photos (e.g. Red Gold) after switching to
        // Yellow/White Gold/Platinum.
        const thumb = card.querySelector('[data-setting-style-thumb]');
        const metalKey = ring.metalType || '';
        if (thumb && thumb.dataset.populated !== metalKey && !thumb.dataset.thumbOverride) {
          const match = candidatePool.find((p) => this._tagValues(p, 'rb-diamond-setting').includes(key));
          const img = match ? (match.featuredImage || (match.images && match.images[0]) || '') : '';
          if (img) {
            thumb.style.backgroundImage = `url('${img}')`;
            thumb.dataset.populated = metalKey;
          }
        }
      });

      // Band width info (replaces the old "choose the silhouette" disclaimer).
      // Every diamond setting is built on a 4mm band except Petite (3mm).
      // RSFY-801 — the line lives inside the always-visible settings section
      // now, so it hides itself outside diamond mode; the fixed-band note
      // under the quality cards mirrors the same width.
      const settingWidth = ring.settingStyle === 'petite' ? '3mm' : '4mm';
      const bandInfoEl = container.querySelector('[data-setting-band-width]');
      if (bandInfoEl) {
        bandInfoEl.textContent = settingWidth;
        const wrap = bandInfoEl.closest('[data-setting-band-info]');
        if (wrap) wrap.hidden = !inDiamondMode;
      }
      const noteWidthEl = root.querySelector('[data-setting-note-width]');
      if (noteWidthEl) noteWidthEl.textContent = settingWidth;
    }

    /**
     * RSFY-801 — the width the ring will actually be made on. Diamond
     * settings ship on a fixed band (4mm; Petite 3mm) regardless of the
     * plain-band picker's lingering value, which deliberately survives a
     * mode switch so the width card re-highlights on the way back. Every
     * reader that tells a human the width (cart properties, ring tab
     * summaries, the engraving fit meter) goes through here so none of
     * them can quote the picker's stale width for a diamond ring.
     */
    _effectiveBandWidth(ring) {
      if (!ring) return null;
      if (ring.hasDiamond === true) return ring.settingStyle === 'petite' ? '3' : '4';
      return ring.bandWidth || null;
    }

    /**
     * RSFY-801 — absolute price on each PLAIN BAND card, from the same
     * catalogue pool the band path resolves against: the metal+carat
     * filtered products carrying `rb-band-width:{N}mm`. The cheapest
     * variant of the width's product is its base (engraving "None")
     * price. Cards with no product for the current pool clear their price.
     */
    _paintWidthPrices(widthPicker, ring) {
      const priceEls = widthPicker.querySelectorAll('[data-width-price]');
      if (!priceEls.length) return;
      if (!ring || !ring.metalType) { priceEls.forEach((el) => { el.textContent = ''; }); return; }
      const pool = this.getProductsForMetal(ring.metalType)
        .filter((p) => !ring.carat || this.extractCarat(p) === ring.carat);
      // Mirror resolveProductForRings' preference: diamond products carry
      // rb-band-width tags too (a Contemporary is a 4mm band), so without
      // narrowing to rb-diamond:no the 4mm/3mm cards would price against
      // diamond rings whenever those happen to be cheapest.
      const plain = pool.filter((p) => this._tagValue(p, 'rb-diamond') === 'no');
      const source = plain.length ? plain : pool;
      priceEls.forEach((el) => {
        const w = String(el.dataset.widthPrice);
        const prods = source.filter((p) => this._tagValues(p, 'rb-band-width').includes(`${w}mm`));
        let best = null;
        prods.forEach((p) => (p.variants || []).forEach((v) => {
          const price = parseFloat(v.price);
          if (Number.isFinite(price) && (best === null || price < parseFloat(best.price))) best = v;
        }));
        el.textContent = best ? this._formatVariantPrice(best) : '';
      });
    }

    _renderDiamondStep() {
      const root = this.querySelector('[data-step="diamond"]');
      if (!root) return;
      // Pick the current ring (or fall back to ring[0]). When state isn't
      // ready yet we still want the toggle to be visible — only the
      // fields/summary should stay hidden until the customer picks one.
      const ringIndex = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = (this.rings && this.rings[ringIndex]) || null;
      const hasDiamond = ring ? ring.hasDiamond : null;

      const fields = root.querySelector('[data-diamond-fields]');
      const summary = root.querySelector('[data-diamond-summary]');
      const settingNote = root.querySelector('[data-setting-note]');
      const widthPicker = root.querySelector('[data-width-picker]');
      if (fields) fields.hidden = hasDiamond !== true;
      if (settingNote) settingNote.hidden = hasDiamond !== true;
      if (summary) summary.hidden = hasDiamond === null;
      // RSFY-801 — the width picker is always visible (the fork is gone,
      // PLAIN BAND and WITH A DIAMOND sit stacked on one screen). Selection
      // highlight and the "Selected:" caption only apply in band mode so a
      // diamond pick visibly releases the band cards; the lingering
      // ring.bandWidth default is deliberate state, not a selection.
      if (widthPicker) {
        widthPicker.hidden = false;
        // Make sure the width photos match this ring's metal (the picker may
        // be revealed after the metal was chosen, or for a different ring).
        if (ring && ring.metalType) this._applyBandWidthIcons(ring.metalType);
        const inBandMode = !!ring && ring.hasDiamond === false;
        const selLabel = widthPicker.querySelector('[data-width-selected-label]');
        if (selLabel) {
          selLabel.textContent = (inBandMode && ring.bandWidth)
            ? `Selected: ${WIDTH_LABELS[String(ring.bandWidth)] || ring.bandWidth + 'mm'}`
            : 'Choose a band width';
        }
        widthPicker.querySelectorAll('[data-width-value]').forEach((btn) => {
          const isMatch = inBandMode && ring.bandWidth != null && String(btn.dataset.widthValue) === String(ring.bandWidth);
          btn.classList.toggle('risify-rb__width-card--selected', isMatch);
          btn.classList.toggle('risify-rb__width-btn--selected', isMatch);
          btn.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
        });
        this._paintWidthPrices(widthPicker, ring);
      }

      // If we don't have a ring yet, stop here — toggle is rendered, that's enough.
      if (!ring) return;

      if (ring.hasDiamond !== true) {
        if (ring.hasDiamond === false && summary) {
          // The "Your selection" box is a diamond-mode element (RSFY-801):
          // in band mode the width card + caption carry the selection, so
          // keep the box hidden here.
          summary.hidden = true;
        }
        return;
      }

      // RSFY-647 P0(a) — derive axes from the filtered pool, not selectedProduct.
      const axes = this._availableDiamondAxes(ring);

      // RSFY-647 P0(c) — rebuild the carat slider from the catalogue's actual
      // carat values when they're available. Falls back to the static stops
      // when the pool has no stone-quality data.
      this._renderCaratSlider(root, ring, axes);

      // Highlight + enable/disable each stop based on availability.
      root.querySelectorAll('[data-diamond-carat]').forEach((stop) => {
        const v = parseFloat(stop.dataset.diamondCarat);
        stop.classList.toggle('risify-rb__carat-stop--selected', v === ring.diamondCarat);
        const enabled = axes.carats.size === 0 || axes.carats.has(v);
        stop.style.opacity = enabled ? '' : '0.35';
        stop.disabled = !enabled;
      });

      const stops = this._currentCaratStops || ['0.25', '0.50', '0.75', '1.00', '1.50', '2.00'];
      const idx = stops.indexOf(ring.diamondCarat.toFixed(2));
      const trackActive = root.querySelector('[data-diamond-carat-active]');
      if (trackActive) trackActive.style.width = idx >= 0 && stops.length > 1
        ? `${(idx / (stops.length - 1)) * 100}%`
        : '0%';
      // RSFY-715 — surface the fractional weight label ("1/3 ct" / "1/2 ct" /
      // "1/10 ct") to match the Artistry builder and the weight pills above.
      const caratValueEl = root.querySelector('[data-diamond-carat-value]');
      if (caratValueEl) caratValueEl.textContent = this._caratDisplayLabel(ring.diamondCarat);

      // Available cuts come from product tags (`rb-diamond-cut:{cut}`).
      // If the catalog has no cut tags at all, every cut stays available
      // (symbolic mode). Otherwise, dim cuts that no product supports.
      const availableCuts = new Set();
      if (ring.metalType && ring.carat) {
        let pool = this.getProductsForMetal(ring.metalType).filter((p) => this.extractCarat(p) === ring.carat);
        if (ring.settingStyle) {
          const styleFiltered = pool.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
          if (styleFiltered.length > 0) pool = styleFiltered;
        }
        pool.forEach((p) => {
          const cut = this._tagValue(p, 'rb-diamond-cut');
          if (cut) availableCuts.add(cut);
        });
      }
      // RSFY-647 P1(e) — hide the whole cut block when the catalogue exposes
      // no cut data. Picking a cut today is symbolic (matcher ignores it),
      // and showing 6 chips that don't constrain anything is misleading.
      const cutBlock = root.querySelector('[data-diamond-cut-block]');
      if (cutBlock) cutBlock.hidden = availableCuts.size === 0;

      root.querySelectorAll('[data-diamond-cut]').forEach((card) => {
        const v = card.dataset.diamondCut;
        card.classList.toggle('risify-rb__cut-card--selected', v === ring.diamondCut);
        const enabled = availableCuts.size === 0 || availableCuts.has(v);
        card.style.opacity = enabled ? '' : '0.35';
        card.disabled = !enabled;
        if (!enabled) {
          card.setAttribute('title', this._buildUnavailableTooltip(ring, 'cut', v));
        } else {
          card.removeAttribute('title');
        }
      });
      const cutDisclaimerEl = root.querySelector('[data-diamond-cut-disclaimer]');
      if (cutDisclaimerEl) {
        cutDisclaimerEl.textContent = availableCuts.size > 0
          ? `${availableCuts.size} cut${availableCuts.size === 1 ? '' : 's'} available`
          : '';
      }

      // RSFY-685 — paint the three Stone Quality cards. A card is enabled
      // only when the catalogue actually carries a variant matching the
      // quality's mapped colour + clarity for this ring's current pool. The
      // price delta is computed against the cheapest enabled quality.
      //
      // The displayed-quality reconcile already ran in the upstream caller
      // (`_setDiamondHas`, `_setSettingStyle`, `_setDiamondAxis`, …) so the
      // ring's quality/color/clarity are guaranteed in sync with the current
      // pool by the time we paint here.
      const qualityDeltas = this._qualityPriceDeltas(ring);
      // NOTE: an earlier pass disabled the Premium card for Red Gold +
      // Classic to "mirror" the live builder, whose price bar does not move
      // between qualities on that combo. That bar behaviour has since been
      // shown to be a display bug — live's CART charges the real variant it
      // resolves, and the catalogue prices Red Gold Classic D/F above G/H at
      // every gold carat (9ct: 3990 -> 5490). The disable blocked a genuine,
      // purchasable upsell, so the special case is removed; availability is
      // decided by the pool like every other combination.
      root.querySelectorAll('[data-diamond-quality]').forEach((card) => {
        const label = card.dataset.diamondQuality;
        const map = QUALITY_MAPPING[label];
        if (!map) return;
        // Catalog Stone Quality strings carry colour pairs (`G/H`, `D/F`) and
        // a single clarity. A tier is "available" when ANY of its mapped
        // colours plus any of its clarities exist in the current pool — not
        // every catalog SKU lists the first-canonical colour. Fall back to
        // the single map.color/map.clarity for legacy mappings without the
        // array fields.
        const mapColors = map.colors && map.colors.length ? map.colors : [map.color];
        const mapClarities = map.clarities && map.clarities.length ? map.clarities : [map.clarity];
        const colourOk = axes.colors.size === 0 || mapColors.some((c) => axes.colors.has(c));
        const clarityOk = axes.clarities.size === 0 || mapClarities.some((c) => axes.clarities.has(c));
        const enabled = colourOk && clarityOk;
        card.classList.toggle('risify-rb__quality-card--selected', label === ring.diamondQuality);
        card.style.opacity = enabled ? '' : '0.35';
        card.disabled = !enabled;
        this._paintQualityDelta(card, qualityDeltas, label, enabled);
        if (!enabled) {
          card.setAttribute('title', 'Not available for the current setting');
        } else {
          card.removeAttribute('title');
        }
      });

      const specEl = root.querySelector('[data-diamond-summary-spec]');
      const priceEl = root.querySelector('[data-diamond-summary-price]');
      if (specEl) {
        // RSFY-685 — surface the bundled Stone Quality label rather than the
        // raw D-K / FL-I axes, matching PDP wording. "GIA-certified" tail kept
        // so customers still see the certification cue from the legacy copy.
        // Weight goes through _caratDisplayLabel like the pill and the summary
        // rail — this box previously printed the raw numeric ("0.10ct") next
        // to a pill saying "1/6 ct", contradicting itself on one screen. One
        // label source everywhere; when the client settles the true figure,
        // _caratDisplayLabel is the single place to change.
        // RSFY-801 — "Your selection" reads like the Figma line:
        //   "18ct Red Gold · Contemporary 4mm · 1/3 ct High · GIA-certified"
        // Metal names come from the catalogue verbatim (Red Gold, not the
        // design's "rose gold" — one vocabulary everywhere, the RSFY-782
        // lesson). The "GIA-certified" tail carries over from the legacy
        // summary — it has been customer-facing since launch.
        const specParts = [];
        if (ring.carat && ring.metalType) specParts.push(`${ring.carat} ${ring.metalType}`);
        if (ring.settingStyle) {
          const styleName = ring.settingStyle.replace(/^\w/, (c) => c.toUpperCase());
          specParts.push(`${styleName} ${ring.settingStyle === 'petite' ? '3mm' : '4mm'}`);
        }
        const stone = [this._caratDisplayLabel(ring.diamondCarat)];
        if (this._hasCutData(ring) && ring.diamondCut) {
          stone.push(ring.diamondCut.replace(/^\w/, (c) => c.toUpperCase()));
        }
        if (ring.diamondQuality) stone.push(ring.diamondQuality.split(' ')[0]);
        specParts.push(stone.join(' '));
        specParts.push('GIA-certified');
        specEl.textContent = specParts.join(' · ');
      }
      if (priceEl) {
        priceEl.textContent = ring.selectedVariant ? this._formatVariantPrice(ring.selectedVariant) : '';
      }
    }

    /**
     * RSFY-647 P1(g) — true only when the catalogue actually carries
     * `rb-diamond-cut:` tags for this ring's filtered pool. Callers use this
     * to decide whether to show the cut value in review/status summaries.
     */
    _hasCutData(ring) {
      if (!ring || !ring.metalType || !ring.carat) return false;
      let pool = this.getProductsForMetal(ring.metalType).filter((p) => this.extractCarat(p) === ring.carat);
      if (ring.settingStyle) {
        const narrowed = pool.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
        if (narrowed.length > 0) pool = narrowed;
      }
      return pool.some((p) => !!this._tagValue(p, 'rb-diamond-cut'));
    }

    /**
     * RSFY-647 P2(h) — disabled chip tooltip. Tells the customer **why** a
     * value isn't selectable based on the current filter context.
     */
    _buildUnavailableTooltip(ring, axis, value) {
      const context = [];
      if (ring && ring.metalType && ring.carat) context.push(`${ring.carat} ${ring.metalType}`);
      if (ring && ring.settingStyle) {
        context.push(`${ring.settingStyle.replace(/^\w/, (c) => c.toUpperCase())} setting`);
      }
      const ctxStr = context.length ? context.join(' + ') : 'this configuration';
      return `Not available with ${ctxStr}.`;
    }

    /**
     * RSFY-647 P2(i) — compute the per-value price delta against the cheapest
     * available variant on the given axis. Returns a Map of value → cents.
     */
    _axisPriceDeltas(ring, field) {
      const deltas = new Map();
      if (!ring || ring.hasDiamond !== true) return deltas;

      let pool = [];
      if (ring.metalType && ring.carat) {
        pool = this.getProductsForMetal(ring.metalType).filter((p) => this.extractCarat(p) === ring.carat);
        if (ring.settingStyle) {
          // RSFY — products carry MULTIPLE `rb-diamond-setting:` tags (the
          // Elegant .33ct rings are tagged both `bezel` and `elegant`), so
          // `_tagValue`, which returns only the FIRST, never matched. The
          // narrow then found nothing and the pool silently stayed as every
          // product for the metal + gold carat, so `min` below was taken
          // across all of them: the badge quoted whichever product was
          // cheapest rather than the selected one. Yellow Gold 9ct Elegant
          // showed "+US$400" (Petite Contemporary's 2190 -> 2590) beside its
          // own correct 2490 total; the real upgrade is +US$500. `_tagValues`
          // is the existing multi-tag reader the sibling call sites use.
          const narrowed = pool.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
          if (narrowed.length > 0) pool = narrowed;
        }
      }
      if (pool.length === 0) return deltas;

      // For each axis value, find the minimum variant price whose Stone Quality
      // string includes that value and matches the ring's other (already-fixed)
      // axes.
      const lowestByValue = new Map();
      pool.forEach((product) => {
        const stoneIndex = this.findOptionIndex(product, 'stone');
        if (stoneIndex < 0) return;
        const stoneKey = `option${stoneIndex + 1}`;
        product.variants.forEach((v) => {
          const parsed = this._parseStoneQualityString(v[stoneKey]);
          if (!parsed || v.price == null) return;
          if (ring.diamondCarat != null && parsed.carat != null && parsed.carat !== ring.diamondCarat) return;
          const values = field === 'diamondColor'
            ? (parsed.colors || (parsed.color ? [parsed.color] : []))
            : (parsed.clarities || (parsed.clarity ? [parsed.clarity] : []));
          values.forEach((val) => {
            const cur = lowestByValue.get(val);
            if (cur == null || v.price < cur) lowestByValue.set(val, v.price);
          });
        });
      });

      if (lowestByValue.size === 0) return deltas;
      const min = Math.min(...lowestByValue.values());
      lowestByValue.forEach((price, val) => deltas.set(val, price - min));
      return deltas;
    }

    /**
     * RSFY-647 P2(i) — paint the price delta onto a chip. Hidden when the
     * chip is disabled (no point teasing a price you can't pick) or when the
     * delta is zero (the baseline option).
     */
    _paintAxisDelta(chip, deltas, value, enabled) {
      // Find or create the delta span inside the chip.
      let span = chip.querySelector('.risify-rb__scale-chip-delta');
      const delta = deltas.get(value);
      const show = enabled && delta != null && delta > 0;
      if (show) {
        if (!span) {
          span = document.createElement('span');
          span.className = 'risify-rb__scale-chip-delta';
          chip.appendChild(span);
        }
        span.textContent = `+${this._formatPriceDelta(delta)}`;
      } else if (span) {
        span.remove();
      }
    }

    /**
     * RSFY-685 — for each Stone Quality label, return the minimum variant
     * price whose parsed colour AND clarity satisfy that quality's mapping.
     * Result is a Map<label, deltaCents> against the cheapest available
     * quality so the Premium card never shows "+$0" against itself.
     */
    _qualityPriceDeltas(ring) {
      const deltas = new Map();
      if (!ring || ring.hasDiamond !== true) return deltas;

      let pool = [];
      if (ring.metalType && ring.carat) {
        pool = this.getProductsForMetal(ring.metalType).filter((p) => this.extractCarat(p) === ring.carat);
        if (ring.settingStyle) {
          // RSFY — products carry MULTIPLE `rb-diamond-setting:` tags (the
          // Elegant .33ct rings are tagged both `bezel` and `elegant`), so
          // `_tagValue`, which returns only the FIRST, never matched. The
          // narrow then found nothing and the pool silently stayed as every
          // product for the metal + gold carat, so `min` below was taken
          // across all of them: the badge quoted whichever product was
          // cheapest rather than the selected one. Yellow Gold 9ct Elegant
          // showed "+US$400" (Petite Contemporary's 2190 -> 2590) beside its
          // own correct 2490 total; the real upgrade is +US$500. `_tagValues`
          // is the existing multi-tag reader the sibling call sites use.
          const narrowed = pool.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
          if (narrowed.length > 0) pool = narrowed;
        }
      }
      if (pool.length === 0) return deltas;

      const lowestByLabel = new Map();
      pool.forEach((product) => {
        const stoneIndex = this.findOptionIndex(product, 'stone');
        if (stoneIndex < 0) return;
        const stoneKey = `option${stoneIndex + 1}`;
        product.variants.forEach((v) => {
          const parsed = this._parseStoneQualityString(v[stoneKey]);
          if (!parsed || v.price == null) return;
          if (ring.diamondCarat != null && parsed.carat != null && parsed.carat !== ring.diamondCarat) return;
          const colors = parsed.colors && parsed.colors.length ? parsed.colors : (parsed.color ? [parsed.color] : []);
          const clarities = parsed.clarities && parsed.clarities.length ? parsed.clarities : (parsed.clarity ? [parsed.clarity] : []);
          for (const label of QUALITY_ORDER) {
            const map = QUALITY_MAPPING[label];
            if (colors.length && !colors.includes(map.color)) continue;
            if (clarities.length && !clarities.includes(map.clarity)) continue;
            const cur = lowestByLabel.get(label);
            if (cur == null || v.price < cur) lowestByLabel.set(label, v.price);
          }
        });
      });

      if (lowestByLabel.size === 0) return deltas;
      const min = Math.min(...lowestByLabel.values());
      lowestByLabel.forEach((price, label) => deltas.set(label, price - min));
      return deltas;
    }

    _paintQualityDelta(card, deltas, label, enabled) {
      // The delta line is always present so every card keeps four rows and
      // the grid never shifts on selection; the baseline card (no extra
      // cost) shows a blank line, the selected card shows "Selected" instead
      // (the delta is hidden by CSS on the selected card).
      let span = card.querySelector('.risify-rb__quality-card-delta');
      const delta = deltas.get(label);
      const show = enabled && delta != null && delta > 0;
      if (!span) {
        span = document.createElement('span');
        span.className = 'risify-rb__quality-card-delta';
        card.appendChild(span);
      }
      span.textContent = show ? `+${this._formatPriceDelta(delta)}` : '\u00A0';
    }

    _formatPriceDelta(cents) {
      const fmt = (this.config && this.config.moneyFormat) || '${{amount}}';
      const dollars = (cents / 100).toFixed(0);
      return fmt.replace(/\{\{\s*amount\s*\}\}/g, dollars).replace(/\{\{[^}]+\}\}/g, dollars);
    }

    /**
     * RSFY-715 — Map a diamondCarat float to the weight label shown in the UI
     * ("1/3 ct", "1/2 ct", ".06ct"). Falls back to "N.NN ct" for any value not
     * in the map. Every weight display (pill, summary rail, selected-stone box)
     * reads from here, so this is the single place to change a label.
     */
    _caratDisplayLabel(carat) {
      // Sleek's stone weight was declared three different ways across the
      // store: "1/6 ct" in the builders and the styling sheet, "~.10ct" in the
      // product titles and variants, and "~2.5mm (~.06ct)" in the product
      // descriptions. The client settled it (Halfdan, 2026-08-05): the stone
      // actually set is 2.5mm round — .06ct, or 1/15ct — and he chose the
      // fractional form for the UI ("1/15ct will probably look nicer than
      // .06ct"), asking that the change be made in this one place only.
      //
      // This IS that one place: the pill, the summary rail, the ring-tab
      // summary, the step status label and the review workshop slip all read
      // their weight from here, and no product data is touched.
      //
      // OPEN, client side: the product titles and variant names still say
      // ~.10ct, so the cart line and order confirmation continue to show that
      // figure. Renaming those products in Shopify admin is what closes the
      // last gap.
      const FRAC = { '0.33': '1/3 ct', '0.50': '1/2 ct', '0.10': '1/15 ct' };
      const key = Number(carat).toFixed(2);
      return FRAC[key] || `${key} ct`;
    }

    /**
     * RSFY-715 — Given a ring's diamondCarat float, return the styling-map
     * `select` string for the diamondWeight layer of the current setting, so
     * the 2D compositor can find the right overlay. Maps the canonical catalog
     * carats to the map's labels (note: Sleek's map select is "1/6ct" even
     * though the real weight is 0.10 — match the layer, ignore the bad label).
     */
    _weightLabelForCarat(carat, ring) {
      const c = Number(carat).toFixed(2);
      const settingStyle = ring && ring.settingStyle;
      // Sleek has a single weight option labelled "1/6ct" in the map.
      if (settingStyle === 'sleek') return '1/6ct';
      const BY_CARAT = { '0.33': '1/3ct', '0.50': '1/2ct' };
      return BY_CARAT[c] || `${c}ct`;
    }

    /**
     * RSFY-715 — the discrete diamond-weight floats the styling map offers for
     * a given metal × settingStyle. Maps the map's weight `select` strings to
     * canonical carats: "1/3ct" → 0.33, "1/2ct"/"1/2" → 0.50, "1/6ct" → 0.10
     * (Sleek; the "1/6" label is wrong, the real weight is 1/10 ct per S1).
     */
    _styling2dWeightsForSetting(metalType, settingStyle) {
      const metalKey = STYLING_2D_METAL[metalType];
      const settingTitle = STYLING_2D_SETTING_TITLE[settingStyle];
      if (!metalKey || !settingTitle) return [];
      const layer = STYLING_2D_MAP.find((L) =>
        L.role === 'diamondWeight' && L.metal === metalKey && L.setting === settingTitle);
      if (!layer) return [];
      const WEIGHT_TO_CARAT = { '1/3': 0.33, '1/2': 0.50, '1/6': 0.10 };
      const out = new Set();
      layer.options.forEach((o) => {
        const v = WEIGHT_TO_CARAT[_normWeight(o.select)];
        if (v != null) out.add(v);
      });
      return Array.from(out);
    }

    /**
     * RSFY-647 P0(c) — render carat slider stops from the actual catalogue
     * data (rb-diamond-crt: tags + parsed Stone Quality strings) instead of
     * the hardcoded 0.25/0.50/…/2.00 list. Re-renders only when the stop set
     * changes so we don't churn the DOM on every state update.
     */
    _renderCaratSlider(root, ring, axes) {
      const slider = root.querySelector('[data-diamond-carat-slider]');
      if (!slider) return;

      // Build the stop set: start with parsed Stone Quality carats, augment
      // with `rb-diamond-crt:` product tags if present.
      const stopSet = new Set();
      axes.carats.forEach((c) => stopSet.add(c));

      if (ring && ring.metalType && ring.carat) {
        let pool = this.getProductsForMetal(ring.metalType).filter((p) => this.extractCarat(p) === ring.carat);
        if (ring.settingStyle) {
          const narrowed = pool.filter((p) => this._tagValues(p, 'rb-diamond-setting').includes(ring.settingStyle));
          if (narrowed.length > 0) pool = narrowed;
        }
        pool.forEach((p) => {
          const tagVal = this._tagValue(p, 'rb-diamond-crt');
          if (!tagVal) return;
          // Catalog tags are written without a leading zero — `50ct` / `50crt`
          // means 0.50ct, `33ct` means 0.33ct, `10ct` means 0.10ct. Treat any
          // integer-only value < ~5 as already-decimal, anything >= 5 as
          // hundredths (50 → 0.50, 33 → 0.33, …). Decimal values pass through
          // unchanged so `0.50ct` and future `1.5ct` style tags still work.
          const cleaned = String(tagVal).replace(/[^\d.]/g, '');
          if (!cleaned) return;
          let num = parseFloat(cleaned);
          if (isNaN(num) || num <= 0) return;
          if (!/\./.test(cleaned) && num >= 5) num = num / 100;
          stopSet.add(num);
        });
      }

      // RSFY-715 — source the discrete weight stops from the styling map for
      // the current metal × setting (1–2 values). The Artistry data is the
      // canonical set of purchasable weights per setting; seeding from it means
      // the pills are correct even when the catalogue pool isn't yet resolved.
      // Sleek's map label is "1/6ct" but the real weight is 0.10 (1/10 ct) — S1.
      if (ring && ring.hasDiamond === true && ring.metalType && ring.settingStyle) {
        const mapWeights = this._styling2dWeightsForSetting(ring.metalType, ring.settingStyle);
        mapWeights.forEach((n) => stopSet.add(n));
      }

      // Permissive fallback when the catalogue gives us nothing — keep the
      // legacy stop list so the slider stays usable in symbolic mode.
      let sorted = stopSet.size > 0
        ? Array.from(stopSet).sort((a, b) => a - b)
        : [0.25, 0.50, 0.75, 1.00, 1.50, 2.00];

      // Only render carat weights that are actually available for the current
      // metal × setting. Previously every seeded weight rendered and the
      // unavailable ones were merely dimmed/disabled — the customer still saw
      // greyed-out chips (e.g. Elegant showed 1/10 + 1/2 disabled next to the
      // one real 1/3 option). `axes.carats` is the authoritative available set;
      // when it's non-empty we drop everything outside it so unavailable
      // weights simply don't appear. (Empty = symbolic mode → show all.)
      if (axes && axes.carats && axes.carats.size > 0) {
        const avail = axes.carats;
        const filtered = sorted.filter((n) => {
          // Match on 2dp so 0.3300001 vs 0.33 still compares equal to the set.
          const key = Number(n).toFixed(2);
          for (const a of avail) {
            if (Number(a).toFixed(2) === key) return true;
          }
          return false;
        });
        // Guard: never blank the picker if the intersection is somehow empty.
        if (filtered.length > 0) sorted = filtered;
      }

      const formatted = sorted.map((n) => Number(n).toFixed(2));
      const signature = formatted.join('|');
      this._currentCaratStops = formatted;

      // Only rebuild the DOM when the set changes — preserves event bindings
      // and keeps the selected state untouched in the common case.
      if (slider.dataset.stopsSignature === signature) return;
      slider.dataset.stopsSignature = signature;

      // RSFY (Stone-step Figma 6002:7437) — Diamond Weight renders as discrete
      // button cards ("0.33 ct" / "0.50 ct"), not a dotted slider. We keep the
      // same `[data-diamond-carat]` hooks + selected class + click handler so
      // `_renderDiamondAxes` and the variant matcher work unchanged.
      slider.querySelectorAll('[data-diamond-carat]').forEach((el) => el.remove());

      formatted.forEach((value) => {
        // RSFY-715 — show the Artistry fractional label ("1/3 ct", "1/2 ct",
        // "1/10 ct") instead of the decimal. The `data-diamond-carat` value
        // stays the canonical float so the variant matcher + selected-state
        // logic are unchanged.
        const label = this._caratDisplayLabel(value);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'risify-rb__carat-stop';
        btn.dataset.diamondCarat = value;
        btn.setAttribute('aria-label', `${label.replace(' ct', ' carat')}`);
        btn.innerHTML = `<span class="risify-rb__carat-label">${label}</span>`;
        btn.addEventListener('click', () => {
          this._setDiamondAxis('diamondCarat', parseFloat(value));
        });
        slider.appendChild(btn);
      });
    }

    _formatVariantPrice(variant) {
      if (!variant || variant.price == null) return '';
      const fmt = (this.config && this.config.moneyFormat) || '${{amount}}';
      const dollars = (variant.price / 100).toFixed(2);
      return fmt.replace(/\{\{\s*amount\s*\}\}/g, dollars).replace(/\{\{[^}]+\}\}/g, dollars);
    }

    _setDiamondHas(on) {
      const checkedRings = this.requireCheckedRings('diamond');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        const r = this.rings[i];
        r.hasDiamond = !!on;
        if (!on) {
          r.diamond = 'none';
          r.settingStyle = null; // not relevant for plain bands
          // Default the plain-band width to Standard 7mm (the "Popular" pick)
          // so Band Only opens with a selection rather than an empty picker.
          // Only when the customer hasn't already chosen a width.
          if (r.bandWidth == null || r.bandWidth === '') {
            r.bandWidth = '7';
          }
        } else {
          r.diamond = null;
          // Auto-pick the first available setting style if none chosen yet.
          // Walk the UI display order (Contemporary → Modern → Classic →
          // Elegant → Sleek → Petite) and pick the first style that the
          // catalog actually supports for this metal+carat combo. Without
          // this we'd grab the catalog's first-tagged style — which is
          // often Classic — even though Contemporary appears first in the
          // UI grid.
          if (!r.settingStyle && r.metalType && r.carat) {
            const products = this.getProductsForMetal(r.metalType).filter((p) => this.extractCarat(p) === r.carat);
            const available = new Set();
            products.forEach((p) => this._tagValues(p, 'rb-diamond-setting').forEach((s) => available.add(s)));
            const uiOrder = ['contemporary', 'modern', 'classic', 'elegant', 'sleek', 'petite'];
            const firstStyle = uiOrder.find((s) => available.has(s));
            if (firstStyle) r.settingStyle = firstStyle;
          }
          // Default diamond carat = the SMALLEST stop available in the
          // current pool (matches the original Artistry behaviour where
          // picking a setting auto-selects the cheapest variant). Falls
          // back to the standard snap when the axes set is empty.
          const axes = this._availableDiamondAxes(r);
          if (axes.carats.size > 0) {
            r.diamondCarat = Math.min(...axes.carats);
          } else {
            r.diamondCarat = this._snapToNearestAxisValue(r, 'diamondCarat', r.diamondCarat);
          }
          r.diamondColor   = this._snapToNearestAxisValue(r, 'diamondColor', r.diamondColor);
          r.diamondClarity = this._snapToNearestAxisValue(r, 'diamondClarity', r.diamondClarity);
          // RSFY-685 — reconcile the displayed Stone Quality (and re-snap
          // colour/clarity to its mapping if the current pair doesn't match
          // any quality label exactly) BEFORE we ask the variant matcher to
          // resolve a SKU. Otherwise the matcher uses the just-snapped
          // colour/clarity while the UI shows a different quality label —
          // customer sees "High Quality" but the cart line is Premium.
          this._syncDiamondQuality(r);
        }
        r.engraving = null;
      });
      // Re-resolve product because settingStyle may have changed.
      this.resolveProductForRings(checkedRings);
      this.updateVariantMatchForRings(checkedRings);
      this._renderDiamondStep();
      this._renderSettingStyles();
      // Re-render Metal step cards so their thumbnails reflect the new
      // diamond state (plain band photos when off, diamond-ring photos
      // when on). Pure DOM rebuild — keeps the currently selected metal
      // highlighted via _restoreMetalStepUI right after.
      this.populateMetalTypes();
      this._restoreMetalStepUI();
      this.updateRingStatusDisplay();
      this.updateSummaryRail && this.updateSummaryRail();
      // RSFY-718 — repaint the reference panel immediately on the diamond
      // toggle (the async 3D fan-out also repaints, but later).
      this._renderSettingDetail();
    }

    _setDiamondAxis(field, value) {
      const checkedRings = this.requireCheckedRings('diamond');
      if (!checkedRings) return;

      // RSFY-647 P0(d) — snap to nearest available value rather than writing
      // an invalid one. Each checked ring is considered separately so its own
      // filtered pool decides what's valid.
      checkedRings.forEach((i) => {
        const ring = this.rings[i];
        const snapped = this._snapToNearestAxisValue(ring, field, value);
        ring[field] = (snapped !== undefined) ? snapped : value;
        // Cross-axis snap: picking colour D may invalidate clarity SI (and vice
        // versa). After writing the new value, push the *other* axes back to
        // their nearest cross-compatible value so the UI never leaves an
        // unreachable combination selected.
        this._reconcileOtherAxes(ring, field);
        // Keep the bundled Stone Quality label in sync whenever colour or
        // clarity changes underneath us (e.g. after a setting-style snap).
        if (field === 'diamondColor' || field === 'diamondClarity') {
          this._syncDiamondQuality(ring);
        }
      });
      // Diamond carat is tied to which product holds the matching variants
      // (e.g. Contemporary 0.33ct vs Contemporary 0.50ct are two distinct
      // products). Re-resolve before variant match so the matcher sees the
      // right product.
      if (field === 'diamondCarat') {
        this.resolveProductForRings(checkedRings);
      }
      this.updateVariantMatchForRings(checkedRings);
      this._renderDiamondStep();
      this.updateRingStatusDisplay();
      this.updateSummaryRail && this.updateSummaryRail();
    }

    /**
     * RSFY-685 — bundled Stone Quality (High/Premium/Rare) replaces the
     * standalone Color + Clarity pickers. Writes the quality label plus the
     * mapped colour and clarity in one shot, then routes through the same
     * cross-axis reconciliation path the legacy chips used so price deltas
     * and variant matching stay accurate.
     */
    _setDiamondQuality(value) {
      const map = QUALITY_MAPPING[value];
      if (!map) return;
      const checkedRings = this.requireCheckedRings('diamond');
      if (!checkedRings) return;

      checkedRings.forEach((i) => {
        const ring = this.rings[i];
        // Write the user's pick verbatim — `_reconcileDisplayedQuality`
        // during paint will downgrade to a derived label only if the pool
        // genuinely lacks the requested combo. Without this assignment a
        // snap that lands off-map (e.g. requested D but pool only has E/F)
        // would silently swap the label back via the reconcile path.
        ring.diamondQuality = value;
        ring.diamondColor   = this._snapToNearestAxisValue(ring, 'diamondColor',   map.color);
        ring.diamondClarity = this._snapToNearestAxisValue(ring, 'diamondClarity', map.clarity);
        // Carat can fall off when narrowing — keep _reconcileOtherAxes
        // semantics by walking the remaining axes once we've written both.
        this._reconcileOtherAxes(ring, 'diamondColor');
      });

      this.updateVariantMatchForRings(checkedRings);
      this._renderDiamondStep();
      this.updateRingStatusDisplay();
      this.updateSummaryRail && this.updateSummaryRail();
    }

    /**
     * Reverse mapping — pick the quality label whose canonical colour/clarity
     * best matches the ring's currently snapped axes. Used when state mutates
     * outside of `_setDiamondQuality` (setting style swap, copy-from-ring,
     * carat change) so the Quality UI stays accurate without the caller
     * having to remember which label belongs where.
     */
    _qualityFromColorClarity(color, clarity) {
      if (!color && !clarity) return 'Premium Quality';
      // Score each label by how many axes match exactly; tie-break by
      // preference order (Premium first — it's our default).
      let best = 'Premium Quality';
      let bestScore = -1;
      for (const label of QUALITY_ORDER) {
        const map = QUALITY_MAPPING[label];
        let score = 0;
        if (color && map.color === color) score++;
        if (clarity && map.clarity === clarity) score++;
        if (score > bestScore) { best = label; bestScore = score; }
      }
      return best;
    }

    /**
     * RSFY-685 — pick the Quality label the UI should actually show as
     * selected. Three layers:
     *   1. If the current `ring.diamondQuality` is enabled in the current
     *      pool, keep it (don't yank the user's pick away).
     *   2. Otherwise, derive from the ring's already-snapped colour/clarity
     *      (e.g. setting-style swap landed on G/SI → "High Quality").
     *   3. As a last resort, fall back to the first enabled label, snapping
     *      colour/clarity to that label's mapping so downstream variant
     *      lookups don't carry the previous (invalid) values.
     */
    /**
     * Resolve the ring's current pool axes and run `_reconcileDisplayedQuality`
     * in one shot. Call sites use this before `updateVariantMatchForRings` so
     * the matcher reads the freshly-reconciled colour/clarity instead of a
     * stale init default.
     */
    _syncDiamondQuality(ring) {
      if (!ring || ring.hasDiamond !== true) return;
      const axes = this._availableDiamondAxes(ring);
      const reconciled = this._reconcileDisplayedQuality(ring, axes);
      if (reconciled) ring.diamondQuality = reconciled;
    }

    _reconcileDisplayedQuality(ring, axes) {
      if (!ring || ring.hasDiamond !== true) return ring && ring.diamondQuality;
      const isEnabled = (label) => {
        const map = QUALITY_MAPPING[label];
        if (!map) return false;
        const mapColors = map.colors && map.colors.length ? map.colors : [map.color];
        const mapClarities = map.clarities && map.clarities.length ? map.clarities : [map.clarity];
        const colourOk  = axes.colors.size === 0 || mapColors.some((c) => axes.colors.has(c));
        const clarityOk = axes.clarities.size === 0 || mapClarities.some((c) => axes.clarities.has(c));
        return colourOk && clarityOk;
      };

      if (ring.diamondQuality && isEnabled(ring.diamondQuality)) {
        const map = QUALITY_MAPPING[ring.diamondQuality];
        if (ring.diamondColor === map.color && ring.diamondClarity === map.clarity) {
          return ring.diamondQuality;
        }
      }
      const derived = this._qualityFromColorClarity(ring.diamondColor, ring.diamondClarity);
      if (isEnabled(derived)) return derived;

      for (const label of QUALITY_ORDER) {
        if (isEnabled(label)) {
          const map = QUALITY_MAPPING[label];
          // Direct assignment — `isEnabled` already verified both axes exist
          // in the current pool, so no further snapping is needed. Going
          // through `_snapToNearestAxisValue` here would re-introduce the
          // off-map drift the reconcile is supposed to eliminate.
          ring.diamondColor   = map.color;
          ring.diamondClarity = map.clarity;
          return label;
        }
      }
      return ring.diamondQuality || 'Premium Quality';
    }

    /**
     * After changing one axis, walk the others and re-snap any value that the
     * new cross-axis pool no longer permits. Uses `_availableValuesForAxis`
     * so the snap respects the just-written field.
     */
    _reconcileOtherAxes(ring, justChangedField) {
      const fields = ['diamondCarat', 'diamondColor', 'diamondClarity'];
      fields.forEach((f) => {
        if (f === justChangedField) return;
        const cur = ring[f];
        if (cur == null) return;
        const allowed = this._availableValuesForAxis(ring, f);
        if (!allowed || allowed.size === 0 || allowed.has(cur)) return;
        // Pick a replacement from the constrained set using the same
        // distance heuristics as the original snap helper.
        const requested = cur;
        const order = f === 'diamondColor'   ? ['D','E','F','G','H','I','J','K']
                    : f === 'diamondClarity' ? ['FL','IF','VVS','VS','SI','I']
                    : null;
        if (f === 'diamondCarat') {
          const values = Array.from(allowed);
          let best = values[0], bestDelta = Math.abs(best - requested);
          for (let k = 1; k < values.length; k++) {
            const d = Math.abs(values[k] - requested);
            if (d < bestDelta) { best = values[k]; bestDelta = d; }
          }
          ring[f] = best;
        } else if (order) {
          const target = order.indexOf(requested);
          let best = null, bestDelta = Infinity;
          allowed.forEach((c) => {
            const d = target >= 0 ? Math.abs(order.indexOf(c) - target) : 0;
            if (d < bestDelta) { best = c; bestDelta = d; }
          });
          if (best) ring[f] = best;
        }
      });
    }

    /**
     * RSFY-647 P0(d) — given a ring and an axis field (diamondCarat, diamondColor,
     * diamondClarity, diamondCut), return the nearest **available** value
     * based on the ring's current filtered pool. Returns the input if it's
     * already valid, or if no axes data exists (symbolic mode).
     */
    _snapToNearestAxisValue(ring, field, requested) {
      if (!ring || requested == null) return requested;
      const axes = this._availableDiamondAxes(ring);

      if (field === 'diamondCarat') {
        if (axes.carats.size === 0) return requested;
        const values = Array.from(axes.carats);
        if (values.includes(requested)) return requested;
        // Pick the numerically closest value.
        let best = values[0];
        let bestDelta = Math.abs(best - requested);
        for (let i = 1; i < values.length; i++) {
          const d = Math.abs(values[i] - requested);
          if (d < bestDelta) { best = values[i]; bestDelta = d; }
        }
        return best;
      }
      if (field === 'diamondColor') {
        if (axes.colors.size === 0) return requested;
        if (axes.colors.has(requested)) return requested;
        // Pick the alphabetically closest available colour (D=0, E=1, … K=7).
        const order = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];
        const target = order.indexOf(requested);
        if (target < 0) return Array.from(axes.colors)[0];
        let best = null, bestDelta = Infinity;
        axes.colors.forEach((c) => {
          const d = Math.abs(order.indexOf(c) - target);
          if (d < bestDelta) { best = c; bestDelta = d; }
        });
        return best || requested;
      }
      if (field === 'diamondClarity') {
        if (axes.clarities.size === 0) return requested;
        if (axes.clarities.has(requested)) return requested;
        const order = ['FL', 'IF', 'VVS', 'VS', 'SI', 'I'];
        const target = order.indexOf(requested);
        if (target < 0) return Array.from(axes.clarities)[0];
        let best = null, bestDelta = Infinity;
        axes.clarities.forEach((c) => {
          const d = Math.abs(order.indexOf(c) - target);
          if (d < bestDelta) { best = c; bestDelta = d; }
        });
        return best || requested;
      }
      // diamondCut not constrained — catalogue has no cut data today.
      return requested;
    }

    /** Legacy entrypoint kept so external callers (e.g. populate flow) work. */
    selectDiamond(value) {
      this._setDiamondHas(value !== 'none');
    }

    /** No-op kept for callers that previously toggled pill UI. */
    _legacySelectDiamondPill(value) {
      const checkedRings = this.requireCheckedRings('diamond');
      if (!checkedRings) return;

      checkedRings.forEach((i) => {
        this.rings[i].diamond = value;
        this.rings[i].engraving = null;
        this.rings[i].engravingDetails = makeDefaultEngravingDetails();
      });

      if (this.diamondPills) {
        this.diamondPills.querySelectorAll('.risify-rb__pill').forEach((pill) => {
          pill.classList.toggle('risify-rb__pill--selected', pill.dataset.diamond === value);
        });
      }

      this.updateVariantMatchForRings(checkedRings);
      this.updateRingStatusDisplay();
      if (this.updateSummaryRail) this.updateSummaryRail();
    }

    // =========================================================================
    // Engraving Selection
    // =========================================================================

    populateEngravingOptions() {
      const addImageEl = this.querySelector('[data-engraving-card-image-add]');
      if (addImageEl) {
        const ring = this.rings[this.currentRingIndex || 0];
        let imgSrc = '';
        if (ring && ring.selectedProduct) {
          imgSrc = ring.selectedProduct.featuredImage || (ring.selectedProduct.images && ring.selectedProduct.images[0]) || '';
        }
        addImageEl.style.backgroundImage = imgSrc ? `url('${imgSrc}')` : '';
      }

      this.resetEngravingUI();

      this.querySelectorAll('[data-engraving-choice]').forEach((card) => {
        card.replaceWith(card.cloneNode(true));
      });
      this.querySelectorAll('[data-engraving-choice]').forEach((card) => {
        card.addEventListener('click', () => this.selectEngravingChoice(card.dataset.engravingChoice === 'yes'));
      });

      this.querySelectorAll('[data-outside-choice]').forEach((btn) => {
        btn.replaceWith(btn.cloneNode(true));
      });
      this.querySelectorAll('[data-outside-choice]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectOutsideEngraving(btn.dataset.outsideChoice === 'yes'));
      });

      this.querySelectorAll('[data-outside-lang]').forEach((btn) => {
        btn.replaceWith(btn.cloneNode(true));
      });
      this.querySelectorAll('[data-outside-lang]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectOutsideLanguage(btn.dataset.outsideLang));
      });

      this.querySelectorAll('[data-inside-choice]').forEach((btn) => {
        btn.replaceWith(btn.cloneNode(true));
      });
      this.querySelectorAll('[data-inside-choice]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectInsideEngraving(btn.dataset.insideChoice === 'yes'));
      });

      this.querySelectorAll('[data-inside-lang]').forEach((btn) => {
        btn.replaceWith(btn.cloneNode(true));
      });
      this.querySelectorAll('[data-inside-lang]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectInsideLanguage(btn.dataset.insideLang));
      });

      this.querySelectorAll('[data-hallmark-choice]').forEach((btn) => {
        btn.replaceWith(btn.cloneNode(true));
      });
      this.querySelectorAll('[data-hallmark-choice]').forEach((btn) => {
        btn.addEventListener('click', () => this.selectHallmark(btn.dataset.hallmarkChoice === 'yes'));
      });

      // Accordion headers — clicking the Outside/Inside title bar toggles
      // its `data-engraving-accordion-open` attribute so the customer can
      // collapse the section back down after picking Yes/No.
      this.querySelectorAll('[data-engraving-accordion-toggle]').forEach((btn) => {
        btn.replaceWith(btn.cloneNode(true));
      });
      this.querySelectorAll('[data-engraving-accordion-toggle]').forEach((btn) => {
        btn.addEventListener('click', () => this._toggleEngravingAccordion(btn.dataset.engravingAccordionToggle));
      });

      // Text input listeners — bind on multiple events so paste, drag-drop,
      // and programmatic value changes all reach the 3D viewer + fit engine.
      // `input` alone misses some Safari paste-then-blur cases (RSFY-709 #3)
      // and never fires for `.value = ''` resets, so we also listen on
      // `change` (commit on blur) and `paste` (next-tick after the browser
      // has merged the pasted content into `.value`).
      const wireEngravingTextarea = (selector, side) => {
        const ta = this.querySelector(selector);
        if (!ta) return;
        const clone = ta.cloneNode(true);
        ta.replaceWith(clone);
        const apply = () => {
          const checkedRings = this.requireCheckedRings('engraving');
          if (!checkedRings) return;
          checkedRings.forEach((i) => {
            this.rings[i].engravingDetails[side === 'inside' ? 'insideText' : 'outsideText'] = clone.value;
          });
          const ring = this.rings[checkedRings[0]];
          const lang = ring && ring.engravingDetails[side === 'inside' ? 'insideLanguage' : 'outsideLanguage'];
          if (lang === 'elvish') {
            this.renderElvishPreview(
              this.querySelector(side === 'inside' ? '[data-inside-elvish-render]' : '[data-outside-elvish-render]'),
              clone.value
            );
          }
          if (lang === 'english') {
            this.updateFontPreview(side);
          }
          checkedRings.forEach((i) => this._scheduleViewerSync(side, this.rings[i]));
          this._scheduleFitUpdate(side, clone.value, ring);
          this.updateSummaryRail();
          if (clone.value.trim()) this.clearStepWarning();
        };
        clone.addEventListener('input', apply);
        clone.addEventListener('change', apply);
        // RSFY-709 #3 — paste delivers content via `clipboardData`, but the
        // textarea's `.value` is only updated AFTER the paste event has
        // fired. Defer one frame so `clone.value` reflects the merged text.
        clone.addEventListener('paste', () => setTimeout(apply, 0));
        // Native drag-and-drop also bypasses `input` on some browsers.
        clone.addEventListener('drop', () => setTimeout(apply, 0));
      };
      wireEngravingTextarea('[data-outside-text]', 'outside');
      wireEngravingTextarea('[data-inside-text]', 'inside');

      // Initialise fit indicators (lazy — only when EngravingFitEngine loaded)
      this._initBuilderFitIndicators();

      const outsideFontSelect = this.querySelector('[data-outside-font]');
      if (outsideFontSelect) {
        const newSelect = outsideFontSelect.cloneNode(true);
        outsideFontSelect.replaceWith(newSelect);
        newSelect.addEventListener('change', () => {
          const checkedRings = this.requireCheckedRings('engraving');
          if (!checkedRings) return;
          checkedRings.forEach((i) => {
            this.rings[i].engravingDetails.outsideFont = newSelect.value;
          });
          this.updateFontPreview('outside');
          checkedRings.forEach((i) => this._syncEngravingToViewer('outside', this.rings[i]));
          const ringForFit = this.rings[checkedRings[0]];
          const ta = this.querySelector('[data-outside-text]');
          if (ta) this._updateBuilderFit('outside', ta.value, ringForFit);
        });
      }

      const insideFontSelect = this.querySelector('[data-inside-font]');
      if (insideFontSelect) {
        const newSelect = insideFontSelect.cloneNode(true);
        insideFontSelect.replaceWith(newSelect);
        newSelect.addEventListener('change', () => {
          const checkedRings = this.requireCheckedRings('engraving');
          if (!checkedRings) return;
          checkedRings.forEach((i) => {
            this.rings[i].engravingDetails.insideFont = newSelect.value;
          });
          this.updateFontPreview('inside');
          checkedRings.forEach((i) => this._syncEngravingToViewer('inside', this.rings[i]));
          const ringForFit = this.rings[checkedRings[0]];
          const ta = this.querySelector('[data-inside-text]');
          if (ta) this._updateBuilderFit('inside', ta.value, ringForFit);
        });
      }

      // Bug fix #6 — after binding all engraving event handlers, paint the
      // current ring's saved engraving state onto the DOM so a ring tab
      // switch restores everything the customer had configured.
      const currentRing = this.rings[this.currentRingIndex || 0];
      if (currentRing && currentRing.engravingDetails && currentRing.engravingDetails.hasEngraving !== null) {
        this._restoreEngravingStepUI(currentRing);
      }
      // Bug I — paint hallmark default explicitly. The hallmark toggle now
      // lives outside the engraving-details block, so it must reflect ring
      // state regardless of whether the customer ever touched engraving.
      this._paintHallmarkPills(currentRing);

      // Push the ring's engraving to the 3D preview on ENTRY to the step, not
      // only on a ring-tab switch (the sibling sync in
      // _restoreStepDomForCurrentRing covers that case). Nothing did this on
      // first arrival, which was invisible while engraving defaulted to empty
      // — there was no text to show. With the step now opening on a filled-in
      // verse, the textareas read as engraved while the ring rendered bare.
      // _syncEngravingToViewer waits for the viewer if it is still booting.
      if (currentRing) {
        this._syncEngravingToViewer('outside', currentRing);
        this._syncEngravingToViewer('inside', currentRing);
      }
    }

    /**
     * Bug I — paint just the hallmark Yes/No toggle from ring state.
     * Called on every engraving-step paint so the default (Yes) survives
     * resets and ring-tab switches.
     */
    _paintHallmarkPills(ring) {
      const hallmarkOn = !ring || ring.engravingDetails?.hallmark !== false;
      this.querySelectorAll('[data-hallmark-choice]').forEach((btn) => {
        const isYes = btn.dataset.hallmarkChoice === 'yes';
        const match = (isYes && hallmarkOn) || (!isYes && !hallmarkOn);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
        btn.classList.toggle('risify-rb__pill--selected', match);
      });
    }

    /**
     * Outside / Inside accordion painter — keeps each section's open state
     * + Yes/No badge label in sync with the ring's engraving details.
     * Default: collapsed when nothing is picked; opens automatically when
     * the customer flips Yes (so they don't have to click the header to
     * reveal the body they just summoned), but a manual click always wins.
     */
    _paintEngravingAccordions(ring) {
      const d = ring && ring.engravingDetails;
      ['outside', 'inside'].forEach((side) => {
        const section = this.querySelector(`[data-engraving-accordion="${side}"]`);
        if (!section) return;
        const chose = d ? (side === 'outside' ? d.outsideEngraving : d.insideEngraving) : null;
        const badgeEl = section.querySelector('[data-engraving-accordion-badge]');
        if (badgeEl) {
          if (chose === true) {
            badgeEl.textContent = 'Yes';
            badgeEl.hidden = false;
          } else if (chose === false) {
            badgeEl.textContent = 'No';
            badgeEl.hidden = false;
          } else {
            badgeEl.textContent = '';
            badgeEl.hidden = true;
          }
        }
        // Open the accordion when (a) the customer has not made a choice yet
        // (so Yes/No are reachable), or (b) they picked Yes (so the body /
        // language picker is visible). Collapse only after an explicit No.
        const shouldOpen = chose === null || chose === undefined || chose === true;
        section.setAttribute('data-engraving-accordion-open', shouldOpen ? 'true' : 'false');
        const toggleBtn = section.querySelector('[data-engraving-accordion-toggle]');
        if (toggleBtn) toggleBtn.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
      });
    }

    _toggleEngravingAccordion(side) {
      const section = this.querySelector(`[data-engraving-accordion="${side}"]`);
      if (!section) return;
      const open = section.getAttribute('data-engraving-accordion-open') === 'true';
      section.setAttribute('data-engraving-accordion-open', open ? 'false' : 'true');
      const toggleBtn = section.querySelector('[data-engraving-accordion-toggle]');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', open ? 'false' : 'true');
    }

    resetEngravingUI() {
      // Textareas are cleared on reset — the placeholder attribute carries
      // the example text (Bug A: no value attribute means no default-text-
      // not-applied-to-ring confusion).
      this.querySelectorAll('[data-engraving-choice]').forEach((c) => {
        c.classList.remove('risify-rb__engraving-card--selected');
        c.classList.remove('risify-rb__segmented-tab--selected');
      });
      const details = this.querySelector('[data-engraving-details]');
      if (details) details.style.display = 'none';
      // Strip selected state from outside/inside engraving choice + language
      // pills. Bug J — DO NOT touch hallmark buttons; hallmark lives outside
      // the engraving-details block and its default Yes must survive an
      // engraving reset.
      this.querySelectorAll(
        '[data-outside-choice], [data-inside-choice], [data-outside-lang], [data-inside-lang]'
      ).forEach((btn) => {
        btn.classList.remove('risify-rb__pill--selected');
        btn.classList.remove('risify-rb__segmented-tab--selected');
      });
      const outsideLang = this.querySelector('[data-outside-language-section]');
      if (outsideLang) outsideLang.style.display = 'none';
      const insideLang = this.querySelector('[data-inside-language-section]');
      if (insideLang) insideLang.style.display = 'none';
      this.querySelectorAll('[data-outside-text-section], [data-inside-text-section]').forEach((el) => {
        el.style.display = 'none';
      });
      this.querySelectorAll('[data-outside-elvish-render], [data-inside-elvish-render]').forEach((el) => {
        el.style.display = 'none';
      });
      const outsideText = this.querySelector('[data-outside-text]');
      if (outsideText) outsideText.value = '';
      const insideText = this.querySelector('[data-inside-text]');
      if (insideText) insideText.value = '';
      this.querySelectorAll('[data-outside-font], [data-inside-font]').forEach((sel) => {
        sel.value = 'Lucida Handwriting';
      });
      this.querySelectorAll('[data-outside-font-section], [data-inside-font-section], [data-outside-font-preview], [data-inside-font-preview]').forEach((el) => {
        el.style.display = 'none';
      });
    }

    selectEngravingChoice(hasEngraving) {
      const checkedRings = this.requireCheckedRings('engraving');
      if (!checkedRings) return;

      checkedRings.forEach((i) => {
        const d = this.rings[i].engravingDetails;
        d.hasEngraving = hasEngraving;
        if (!hasEngraving) {
          d.outsideEngraving = false;
          d.insideEngraving = false;
          d.outsideLanguage = null;
          d.outsideText = '';
          d.outsideFont = 'Lucida Handwriting';
          d.insideLanguage = null;
          d.insideText = '';
          d.insideFont = 'Lucida Handwriting';
          // Bug J — DO NOT reset d.hallmark here. Hallmark lives outside the
          // engraving copy now; "No engraving" doesn't imply "no hallmark".
          // The customer's hallmark choice survives switching engraving off.
          this._syncEngravingToViewer('outside', this.rings[i]);
          this._syncEngravingToViewer('inside', this.rings[i]);
        }
      });

      this.querySelectorAll('[data-engraving-choice]').forEach((card) => {
        const isYes = card.dataset.engravingChoice === 'yes';
        const match = isYes === hasEngraving;
        card.classList.toggle('risify-rb__engraving-card--selected', match);
        card.classList.toggle('risify-rb__segmented-tab--selected', match);
      });

      const details = this.querySelector('[data-engraving-details]');
      if (details) details.style.display = hasEngraving ? '' : 'none';

      if (!hasEngraving) {
        this.resetEngravingUI();
        this.querySelectorAll('[data-engraving-choice="no"]').forEach((c) => {
          c.classList.add('risify-rb__engraving-card--selected');
          c.classList.add('risify-rb__segmented-tab--selected');
        });
      }

      this._paintEngravingAccordions(this.rings[checkedRings[0]]);

      this.updateEngravingVariant();
      this.updateSummaryRail();
    }

    // Clearing engraving state for ONE side is not the same as
    // `resetEngravingUI()`, which wipes both sides and the top-level choice.
    // Answering "No" to Outside must leave an Inside engraving standing, so
    // this scrubs only that side: the textarea DOM (the state flag alone left
    // stale text visible and still rendered on the 3D ring), the font select,
    // the language-dependent panels, the fit indicator, and the viewer meshes.
    _clearEngravingSideUI(side, ringIndices) {
      const textarea = this.querySelector(`[data-${side}-text]`);
      if (textarea) textarea.value = '';
      const fontSelect = this.querySelector(`[data-${side}-font]`);
      if (fontSelect) fontSelect.value = 'Lucida Handwriting';
      this.querySelectorAll(
        `[data-${side}-elvish-render], [data-${side}-font-section], [data-${side}-font-preview]`
      ).forEach((el) => {
        el.style.display = 'none';
      });
      ringIndices.forEach((i) => {
        this._updateBuilderFit(side, '', this.rings[i]);
        this._syncEngravingToViewer(side, this.rings[i]);
      });
    }

    selectOutsideEngraving(yes) {
      const checkedRings = this.requireCheckedRings('engraving');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        this.rings[i].engravingDetails.outsideEngraving = yes;
        if (!yes) {
          this.rings[i].engravingDetails.outsideLanguage = null;
          this.rings[i].engravingDetails.outsideText = '';
          this.rings[i].engravingDetails.outsideFont = 'Lucida Handwriting';
        }
      });
      if (!yes) this._clearEngravingSideUI('outside', checkedRings);

      this.querySelectorAll('[data-outside-choice]').forEach((btn) => {
        const isYes = btn.dataset.outsideChoice === 'yes';
        const match = isYes === yes;
        btn.classList.toggle('risify-rb__pill--selected', match);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
      });

      const langSection = this.querySelector('[data-outside-language-section]');
      if (langSection) langSection.style.display = yes ? '' : 'none';
      if (!yes) {
        const textSection = this.querySelector('[data-outside-text-section]');
        if (textSection) textSection.style.display = 'none';
        this.querySelectorAll('[data-outside-lang]').forEach((b) => {
          b.classList.remove('risify-rb__pill--selected');
          b.classList.remove('risify-rb__segmented-tab--selected');
        });
      }

      this._paintEngravingAccordions(this.rings[checkedRings[0]]);

      this.updateEngravingVariant();
      this.updateSummaryRail();
    }

    selectOutsideLanguage(lang) {
      const checkedRings = this.requireCheckedRings('engraving');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        this.rings[i].engravingDetails.outsideLanguage = lang;
      });

      this.querySelectorAll('[data-outside-lang]').forEach((btn) => {
        const match = btn.dataset.outsideLang === lang;
        btn.classList.toggle('risify-rb__pill--selected', match);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
      });

      const textSection = this.querySelector('[data-outside-text-section]');
      const elvishRender = this.querySelector('[data-outside-elvish-render]');
      const fontSection = this.querySelector('[data-outside-font-section]');
      const fontPreview = this.querySelector('[data-outside-font-preview]');
      if (textSection) textSection.style.display = '';
      if (elvishRender) elvishRender.style.display = lang === 'elvish' ? '' : 'none';
      if (fontSection) fontSection.style.display = lang === 'english' ? '' : 'none';
      if (fontPreview) fontPreview.style.display = lang === 'english' ? '' : 'none';

      const outsideTextarea = this.querySelector('[data-outside-text]');
      if (outsideTextarea && outsideTextarea.value.trim()) {
        checkedRings.forEach((i) => {
          this.rings[i].engravingDetails.outsideText = outsideTextarea.value;
        });
        checkedRings.forEach((i) => this._syncEngravingToViewer('outside', this.rings[i]));
      }

      if (lang === 'elvish') {
        this.renderElvishPreview(elvishRender, outsideTextarea ? outsideTextarea.value : '');
      } else {
        this.updateFontPreview('outside');
      }

      // Bug D — language change flips the active font (Elvish vs the selected
      // English font), which changes character widths → recompute the fit.
      const ring0 = this.rings[checkedRings[0]];
      this._scheduleFitUpdate('outside', outsideTextarea ? outsideTextarea.value : '', ring0);

      this.updateSummaryRail();
    }

    selectInsideEngraving(yes) {
      const checkedRings = this.requireCheckedRings('engraving');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        this.rings[i].engravingDetails.insideEngraving = yes;
        if (!yes) {
          this.rings[i].engravingDetails.insideLanguage = null;
          this.rings[i].engravingDetails.insideText = '';
          this.rings[i].engravingDetails.insideFont = 'Lucida Handwriting';
        }
      });
      if (!yes) this._clearEngravingSideUI('inside', checkedRings);

      this.querySelectorAll('[data-inside-choice]').forEach((btn) => {
        const isYes = btn.dataset.insideChoice === 'yes';
        const match = isYes === yes;
        btn.classList.toggle('risify-rb__pill--selected', match);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
      });

      const langSection = this.querySelector('[data-inside-language-section]');
      if (langSection) langSection.style.display = yes ? '' : 'none';
      if (!yes) {
        const textSection = this.querySelector('[data-inside-text-section]');
        if (textSection) textSection.style.display = 'none';
        this.querySelectorAll('[data-inside-lang]').forEach((b) => {
          b.classList.remove('risify-rb__pill--selected');
          b.classList.remove('risify-rb__segmented-tab--selected');
        });
      }

      this._paintEngravingAccordions(this.rings[checkedRings[0]]);

      this.updateEngravingVariant();
      this.updateSummaryRail();
    }

    selectInsideLanguage(lang) {
      const checkedRings = this.requireCheckedRings('engraving');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        this.rings[i].engravingDetails.insideLanguage = lang;
      });

      this.querySelectorAll('[data-inside-lang]').forEach((btn) => {
        const match = btn.dataset.insideLang === lang;
        btn.classList.toggle('risify-rb__pill--selected', match);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
      });

      const textSection = this.querySelector('[data-inside-text-section]');
      const elvishRender = this.querySelector('[data-inside-elvish-render]');
      const fontSection = this.querySelector('[data-inside-font-section]');
      const fontPreview = this.querySelector('[data-inside-font-preview]');
      if (textSection) textSection.style.display = '';
      if (elvishRender) elvishRender.style.display = lang === 'elvish' ? '' : 'none';
      if (fontSection) fontSection.style.display = lang === 'english' ? '' : 'none';
      if (fontPreview) fontPreview.style.display = lang === 'english' ? '' : 'none';

      const insideTextarea = this.querySelector('[data-inside-text]');
      if (insideTextarea && insideTextarea.value.trim()) {
        checkedRings.forEach((i) => {
          this.rings[i].engravingDetails.insideText = insideTextarea.value;
        });
        checkedRings.forEach((i) => this._syncEngravingToViewer('inside', this.rings[i]));
      }

      if (lang === 'elvish') {
        this.renderElvishPreview(elvishRender, insideTextarea ? insideTextarea.value : '');
      } else {
        this.updateFontPreview('inside');
      }

      // Bug D — language change flips active font; recompute fit.
      const ring0 = this.rings[checkedRings[0]];
      this._scheduleFitUpdate('inside', insideTextarea ? insideTextarea.value : '', ring0);

      this.updateSummaryRail();
    }

    selectHallmark(yes) {
      const checkedRings = this.requireCheckedRings('engraving');
      if (!checkedRings) return;
      checkedRings.forEach((i) => {
        this.rings[i].engravingDetails.hallmark = yes;
      });

      this.querySelectorAll('[data-hallmark-choice]').forEach((btn) => {
        const isYes = btn.dataset.hallmarkChoice === 'yes';
        const match = isYes === yes;
        btn.classList.toggle('risify-rb__pill--selected', match);
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
      });

      // Bug B — actually toggle the hallmark mesh on the 3D viewer. Without
      // this the button just updated state and the engraved hallmark stayed
      // visible regardless of the customer's choice.
      checkedRings.forEach((i) => {
        const viewer = this.ringViewers?.[i];
        if (viewer && typeof viewer.setHallmark === 'function') {
          viewer.setHallmark(yes);
        }
      });

      // Bug D — hallmark eats ~5-9mm of the inside circumference, so toggling
      // it changes the inside fit calculation. Re-paint the inside indicator.
      const insideTa = this.querySelector('[data-inside-text]');
      const ring0 = this.rings[checkedRings[0]];
      this._scheduleFitUpdate('inside', insideTa ? insideTa.value : '', ring0);

      this.updateEngravingVariant();
      this.updateSummaryRail();
    }

    renderElvishPreview(container, text) {
      if (!container) return;
      if (!text) { container.textContent = ''; return; }
      // Match the canonical translator's options exactly. The website's
      // ElvishTranslator.translate calls:
      //   transcribe(text, { font: fonts.annatar, noAchLaut: true,
      //                      medialOre: true, language: 'english' })
      // Without these flags 'dates' (and other English digraphs) render with
      // a different tehtar combination, which Halfdan flagged as a parity
      // mismatch with the live translator page.
      let out = text;
      if (typeof window.transcribe === 'function') {
        try {
          out = window.transcribe(text, {
            noAchLaut: true,
            medialOre: true,
            language: 'english',
          });
        } catch (e) { /* fall back to raw text */ }
      }
      // When transcribe encounters a character it cannot render (e.g. ö, ş,
      // any non-Latin diacritic), it wraps the placeholder in
      // `<abbr class="error" title="…">.</abbr>` so the live translator
      // page can render a styled error glyph via innerHTML — the browser
      // default `<abbr[title]>` rule paints a dotted underline beneath
      // the placeholder, which is the small "tehta" mark below each o
      // that Halfdan flagged as missing.
      //
      // Reproduce the canonical behaviour: parse the transcribe output,
      // keep plain Tengwar runs as text nodes and rebuild each `<abbr>`
      // as a sanitised element so the underline + tooltip survive without
      // exposing us to raw innerHTML on the entire string.
      this._renderTranscribedTextInto(container, out);
    }

    /**
     * Reproduce the canonical translator's render behaviour while keeping
     * us safe from arbitrary innerHTML. We split the transcribe output
     * on `<abbr …>X</abbr>` chunks, write plain runs as text nodes, and
     * rebuild each abbr as a fresh element (textContent on title + inner)
     * so the browser's default underline-dotted decoration paints the
     * tehta-like dot below each placeholder, matching the live page.
     */
    // The transliteration separates lines with <br>; the app writes its output as
    // HTML, so each line of a verse sits on its own row. Written as text, the
    // markers showed up as extra Tengwar glyphs and the lines ran together
    // (RSFY-826). Real line breaks, still no HTML injection.
    _appendTextWithBreaks(container, text) {
      const parts = String(text).split(/<br\s*\/?>/i);
      parts.forEach((part, i) => {
        if (i > 0) container.appendChild(document.createElement('br'));
        if (part) container.appendChild(document.createTextNode(part));
      });
    }

    _renderTranscribedTextInto(container, str) {
      if (!container) return;
      container.textContent = '';
      if (!str || typeof str !== 'string') {
        if (str) container.textContent = String(str);
        return;
      }
      const re = /<abbr\b([^>]*)>([\s\S]*?)<\/abbr>/gi;
      let lastIndex = 0;
      let m;
      while ((m = re.exec(str)) !== null) {
        if (m.index > lastIndex) {
          this._appendTextWithBreaks(container, this._decodeHtmlEntities(str.slice(lastIndex, m.index)));
        }
        const attrs = m[1] || '';
        const inner = m[2] || '';
        const abbr = document.createElement('abbr');
        // Sanitise title — accept the only attribute transcribe emits.
        const titleMatch = attrs.match(/title=("([^"]*)"|'([^']*)')/i);
        if (titleMatch) {
          abbr.title = this._decodeHtmlEntities(titleMatch[2] || titleMatch[3] || '');
        }
        // Class is hard-coded by transcribe ("error"); copy verbatim if it
        // matches the expected shape, otherwise drop it.
        if (/class=("error"|'error')/i.test(attrs)) {
          abbr.className = 'risify-rb__elvish-error';
        }
        abbr.textContent = this._decodeHtmlEntities(inner);
        container.appendChild(abbr);
        lastIndex = m.index + m[0].length;
      }
      if (lastIndex < str.length) {
        this._appendTextWithBreaks(container, this._decodeHtmlEntities(str.slice(lastIndex)));
      }
      // Defensive — if any rogue tag fragments slipped through (unbalanced
      // or non-abbr markup transcribe doesn't currently emit), they'd
      // already be in text nodes, so this is a no-op for the canonical
      // output today.
    }

    _decodeHtmlEntities(str) {
      if (str == null) return '';
      // Cover the only entities transcribe emits (&quot;, &amp;) plus a
      // small safe set; full named-entity decode would need a DOM parse,
      // and we don't want to spawn a temp element for every input event.
      return String(str)
        .replace(/&quot;/g, '"')
        .replace(/&#34;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
    }

    /**
     * Legacy helper kept for callers that just need the plain text fallback
     * (e.g. summary rail labels, analytics). Always produces a string with
     * no markup; the placeholder `.` survives without the dotted underline.
     */
    _stripElvishErrorMarkup(str) {
      if (!str || typeof str !== 'string') return str || '';
      let cleaned = str.replace(/<abbr\b[^>]*>([\s\S]*?)<\/abbr>/gi, '$1');
      cleaned = cleaned.replace(/<\/?[a-z][^>]*>/gi, '');
      return this._decodeHtmlEntities(cleaned);
    }

    updateFontPreview(side) {
      const textarea = this.querySelector(`[data-${side}-text]`);
      const preview = this.querySelector(`[data-${side}-font-preview]`);
      const fontSelect = this.querySelector(`[data-${side}-font]`);
      if (!preview || !fontSelect) return;
      // Font family names must match the @font-face declarations loaded from
      // the live PDP engraving.css (linked in sections/risify-ring-builder.liquid).
      // Each `'Font Name'` here is the FIRST family in the stack, so the loaded
      // WOFF2 wins over any same-named system font.
      const fontMap = {
        'Bookman Old Style': "'Bookman Old Style', 'URW Bookman', serif",
        'Colonna MT': "'Colonna MT', serif",
        'Edwardian Script': "'Edwardian Script', 'Edwardian Script ITC', cursive",
        // RSFY-696: Kereru + Kereru Italic — already loaded via engraving.css
        // (live PDP). Kereru Italic is a separate @font-face family, not a
        // CSS style: we set the family directly, no `italic` keyword needed.
        'Kereru': "'Kereru', serif",
        'Kereru Italic': "'Kereru Italic', 'Kereru', serif",
        'Lucida Handwriting': "'Lucida Handwriting', cursive",
        'Lucida Calligraphy': "'Lucida Calligraphy', 'Lucida Handwriting', cursive",
        'Apple Chancery': "'Apple Chancery', cursive",
        'Old English': "'Old English', 'Old English Text MT', serif",
        'Script Bold': "'Script Bold', 'Script MT Bold', cursive",
        'Typewriter': "'Typewriter', 'American Typewriter', 'Courier New', monospace",
      };
      preview.style.fontFamily = fontMap[fontSelect.value] || fontSelect.value;
      // Mirror the live PDP attribute so engraving.css `[data-font="..."]`
      // size + line-height rules also apply to our preview node.
      preview.setAttribute('data-font', fontSelect.value);
      const text = textarea ? textarea.value : '';
      preview.textContent = text;
      preview.style.display = text ? '' : 'none';
    }

    deriveEngravingValue(ring) {
      // The Engraving option is a PRICED variant axis (None / Outside Only /
      // Inside Only / Both Outside & Inside, ~US$90 a side), so whatever this
      // returns decides the price and the "Engraving (…)" breakdown row.
      //
      // PRODUCT DECISION (PM, 2026-08-03), superseding the short-lived
      // "always Both" rule: the variant follows what the customer actually
      // chose. Skip engraving -> the None variant and the base price; the
      // engraving charge appears only when engraving is added. The live
      // Artistry builder instead charges the Both variant even when the
      // customer skips engraving, so for no-engraving configurations OURS IS
      // DELIBERATELY US\$180 CHEAPER THAN LIVE — that divergence is the point
      // of the decision, not a regression. (Verified live behaviour, all
      // Yellow Gold 18ct: hallmark-Yes cart US\$3070 "Both"; hallmark-No
      // price bar still US\$3070; its default flow pre-fills Elvish text on
      // both sides.)
      //
      // The hallmark stays a free production note and never moves the
      // variant.
      const d = ring.engravingDetails;
      if (!d || !d.hasEngraving) {
        return this.findBestEngravingMatch(ring, 'none');
      }
      const outside = d.outsideEngraving;
      const inside = d.insideEngraving;
      if (outside && inside) return this.findBestEngravingMatch(ring, 'both');
      if (inside) return this.findBestEngravingMatch(ring, 'inside');
      if (outside) return this.findBestEngravingMatch(ring, 'outside');
      return this.findBestEngravingMatch(ring, 'none');
    }

    findBestEngravingMatch(ring, type) {
      if (!ring.selectedProduct) {
        const defaults = { none: 'None', both: 'Both Inside and Outside', inside: 'Inside Only', outside: 'Outside Only' };
        return defaults[type] || 'None';
      }

      const product = ring.selectedProduct;
      const engravingIndex = this.findOptionIndex(product, 'engraving');
      if (engravingIndex < 0) return 'None';

      const optionKey = `option${engravingIndex + 1}`;
      const values = [...new Set(product.variants.map((v) => v[optionKey]).filter(Boolean))];

      for (const val of values) {
        const lower = val.toLowerCase();
        if (type === 'none' && (lower.includes('none') || lower.includes('blank') || lower === 'no engraving')) return val;
        if (type === 'both' && (lower.includes('both') || (lower.includes('inside') && lower.includes('outside')))) return val;
        if (type === 'inside' && lower.includes('inside') && !lower.includes('outside') && !lower.includes('both')) return val;
        if (type === 'outside' && lower.includes('outside') && !lower.includes('inside') && !lower.includes('both')) return val;
      }

      const defaults = { none: 'None', both: 'Both Inside and Outside', inside: 'Inside Only', outside: 'Outside Only' };
      return defaults[type] || 'None';
    }

    updateEngravingVariant() {
      const checkedRings = this.getCheckedRings('engraving');

      checkedRings.forEach((i) => {
        const ring = this.rings[i];
        ring.engraving = this.deriveEngravingValue(ring);
      });

      this.updateVariantMatchForRings(checkedRings);
      this.updateRingStatusDisplay();
    }

    // =========================================================================
    // Size Selection (Step 4 — NEW)
    // =========================================================================

    selectRingSize(size) {
      // Bug fix #6 — size is per-ring, not all rings. Only update the active ring.
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = this.rings[idx];
      if (ring) {
        ring.size = size;
        // Picking a concrete size clears the "I don't know my size" answer —
        // the two are mutually exclusive (inverse of `_setSizeRegion('idkms')`).
        ring.sizeUnknown = false;
      }

      // Highlight selected size — the canonical UK letter sits on the
      // button's `data-size-value`, so the comparison stays stable across
      // region label swaps.
      this.sizeGrid?.querySelectorAll('[data-size-value]').forEach((btn) => {
        btn.classList.toggle('risify-rb__size-btn--selected', btn.dataset.sizeValue === size);
      });

      // Repaint the defer-size controls from the now-cleared `sizeUnknown`
      // flag. The loop above only reaches buttons INSIDE the size grid and
      // only clears one class, so on its own it would leave the standalone
      // defer button (which sits beside the scale tabs) stuck highlighted
      // after the customer picked a real size. _paintSizeRegion is a pure
      // repaint from state and owns every defer control, so it is the single
      // place that decides their highlight.
      this._paintSizeRegion(ring);

      // Repaint the dimensions info strip (Inner diameter / Circumference
      // / region equivalent) from SIZE_TABLE.
      this._paintSizeDimensions(ring);

      // Bug D — ring size feeds directly into circumference; recalc the fit
      // metric on both engraving sides so a later visit to Engraving step
      // shows accurate numbers.
      this._recalcFitForRing(ring);

      // Clear the "Pick a ring size to continue." pill once the customer
      // makes a selection — mirrors the metal step's clear-on-select.
      this.clearStepWarning();

      this._updateRingTabSummaries && this._updateRingTabSummaries();
      this.updateSummaryRail();
    }

    selectBandWidth(width) {
      // RSFY-801 — the fork is gone: picking a width IS choosing the plain-
      // band path. The clicked width is written BEFORE the mode flip:
      // _setDiamondHas(false) resolves the band product with whatever
      // bandWidth the ring holds (its backfill only fills an empty one), and
      // the write below resolves again — two same-tick loads with different
      // widths would stack both band GLBs in the append-only viewer.
      {
        const ring = this.rings && this.rings[this.currentRingIndex || 0];
        if (ring && ring.hasDiamond !== false) {
          ring.bandWidth = width;
          this._setDiamondHas(false);
        }
      }
      // Bug fix #6 — band width is per-ring.
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = this.rings[idx];
      if (ring) ring.bandWidth = width;

      this.widthOptions?.querySelectorAll('[data-width-value]').forEach((btn) => {
        const isMatch = btn.dataset.widthValue === width;
        btn.classList.toggle('risify-rb__width-btn--selected', isMatch);
        btn.classList.toggle('risify-rb__width-card--selected', isMatch);
        btn.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
      });

      // RSFY-707 — update "Selected: …" label. The footer step-warning pill
      // is the sole validation surface (inline warning was removed per
      // feedback — duplicate signal was noisy).
      const selLabel = this.querySelector('[data-width-selected-label]');
      if (selLabel && width) {
        const labelMap = WIDTH_LABELS;
        selLabel.textContent = `Selected: ${labelMap[String(width)] || width + 'mm'}`;
      }

      // RSFY-707 — width is a product-axis selector for plain-band ("No
      // Diamond") flow: 3mm = `petite-movie-ring-3mm-*`, 4mm = `petite-movie-
      // ring-4mm-*`, 5mm = `little-moive-ring-*`, 7mm = `movie-ring-*`. Re-
      // resolve the product so the price/image/variant immediately reflect
      // the picked width.
      if (ring && ring.hasDiamond === false) {
        this.resolveProductForRings([idx]);
        this.updateVariantMatchForRings([idx]);
        // Refresh the Band Only summary card so the title/price/image
        // reflect the freshly resolved product. _renderDiamondStep is the
        // single source of truth for the Step 2 right-rail UI.
        this._renderDiamondStep && this._renderDiamondStep();
      }

      // Bug D — band width changes the usable letter height + outside
      // circumference (thicker band = larger outer radius). Recompute fit.
      this._recalcFitForRing(ring);

      this._updateRingTabSummaries && this._updateRingTabSummaries();
      this.updateSummaryRail();
      // RSFY-718 — the reference panel shows the band-width still on the Band
      // Only path; repaint it now that a width is chosen.
      this._renderSettingDetail();
    }

    /**
     * Bug D — recompute fit indicators for both engraving sides of a ring.
     * Used by ring size / band width / hallmark changes that don't go through
     * the engraving step's own text input handler.
     */
    _recalcFitForRing(ring) {
      if (!ring) return;
      const outsideTa = this.querySelector('[data-outside-text]');
      const insideTa = this.querySelector('[data-inside-text]');
      this._scheduleFitUpdate('outside', outsideTa ? outsideTa.value : (ring.engravingDetails?.outsideText || ''), ring);
      this._scheduleFitUpdate('inside', insideTa ? insideTa.value : (ring.engravingDetails?.insideText || ''), ring);
    }

    getSizeData(size) {
      return SIZE_TABLE_BY_UK[size] || null;
    }

    /**
     * Review slip + summary rail label for the picked size. Reads
     * `ring.sizeRegion` ('uk' | 'us' | 'eu' | 'idkms') and renders the
     * matching label from SIZE_TABLE. IDKMS falls back to UK because that's
     * the canonical state we ship to cart. Always appends the region tag
     * so the workshop slip is unambiguous ("57.5 (EU)" not just "57.5").
     */
    _formatSizeForReview(ring) {
      if (!ring) return '';
      // Echo the button the customer actually clicked. This used to return
      // "I don't know my size" to match the live PDP's cart wording, but the
      // client renamed that button to "I'll confirm my size later" (change doc
      // item 14.3), which left the summary rail and the review slip
      // contradicting the control the customer had just pressed.
      if ((ring.sizeUnknown || ring.sizeRegion === 'idkms')) return "I'll confirm my size later";
      if (!ring.size) return '';
      const row = SIZE_TABLE_BY_UK[ring.size];
      if (!row) return `${ring.size} (UK)`;
      const region = ring.sizeRegion === 'us' || ring.sizeRegion === 'eu' ? ring.sizeRegion : 'uk';
      const value = row[region] || row.uk;
      return `${value} (${region.toUpperCase()})`;
    }

    /**
     * Region tab handler — flips `ring.sizeRegion` between uk / us / eu.
     * IDKMS lives on a separate boolean (`ring.sizeUnknown`) so the
     * region tab selection stays sticky even after the customer presses
     * "I don't know my size". The canonical `ring.size` (UK letter) is
     * preserved across switches, so picking US 6½ → flipping to UK still
     * highlights the same letter.
     */
    _setSizeRegion(region) {
      if (!region) return;
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      const ring = this.rings[idx];
      if (!ring) return;
      const allowed = ['uk', 'us', 'eu', 'idkms'];
      if (!allowed.includes(region)) return;

      if (region === 'idkms') {
        // "I'll confirm my size later" is mutually exclusive with the whole
        // concrete-size path: it clears the chosen size AND the UK/US/EU tab
        // (client revision — the tab used to stay highlighted per RSFY-708,
        // but a lit scale tab beside a deferred answer read as two answers
        // at once). Un-deferring goes through a scale tab or the guidance
        // links, both of which land in the region branch below and restore
        // a clean scale-first flow.
        ring.sizeUnknown = true;
        ring.size = null;
        ring.sizeRegion = null;
        // Drop the highlight on any previously-selected letter/number button.
        this.sizeGrid?.querySelectorAll('[data-size-value]').forEach((btn) => {
          if (btn.dataset.sizeValue !== 'idkms') {
            btn.classList.remove('risify-rb__size-btn--selected');
          }
        });
        this._paintSizeRegion(ring);
        this._paintSizeDimensions(ring);
        this.clearStepWarning();
        this._updateRingTabSummaries && this._updateRingTabSummaries();
        this.updateSummaryRail();
        return;
      }

      // Region tab pressed — clear the IDKMS flag so the two states are
      // mutually exclusive only when the customer actively switches back.
      ring.sizeRegion = region;
      ring.sizeUnknown = false;
      this._paintSizeRegion(ring);
    }

    /**
     * Repaint every Size-step affordance from ring state — region tabs
     * (selected highlight), size button labels (UK letters vs US numeric
     * vs EU mm), grid-vs-guidance visibility, dimensions strip.
     */
    _paintSizeRegion(ring) {
      const region = ring && ring.sizeRegion;
      const isIdkms = !!(ring && ring.sizeUnknown);

      // Region tab highlight — sourced only from `sizeRegion` (uk/us/eu).
      // The IDKMS state is a peer choice, not a 4th region — so the tab
      // the customer last clicked stays highlighted even after they hit
      // "I don't know my size" (RSFY-708 follow-up).
      this.querySelectorAll('[data-size-region]').forEach((btn) => {
        // The standalone "confirm size later" button reuses this handler's
        // attribute so its click routes through _setSizeRegion for free, but
        // it is NOT a region — skip it here and paint it from sizeUnknown
        // below, together with its in-grid twin.
        if (btn.dataset.sizeRegion === 'idkms') return;
        const match = !!region && btn.dataset.sizeRegion === region;
        btn.classList.toggle('risify-rb__segmented-tab--selected', match);
        btn.classList.toggle('risify-rb__pill--selected', match);
      });

      // Paint EVERY defer-size control from the dedicated flag: the peer
      // button inside the size grid and the standalone one beside the scale
      // tabs. Previously scoped to this.sizeGrid, which would have left the
      // standalone button permanently unhighlighted. Each element only has
      // styles for its own class namespace, so toggling both is harmless.
      this.querySelectorAll('[data-size-value="idkms"], [data-size-region="idkms"]').forEach((btn) => {
        btn.classList.toggle('risify-rb__size-btn--selected', isIdkms);
        btn.classList.toggle('risify-rb__segmented-tab--selected', isIdkms);
      });

      // Toggle the size grid.
      const gridCard = this.querySelector('[data-size-grid-card]');
      const isGrid = region === 'uk' || region === 'us' || region === 'eu';
      // Grid visibility follows the SCALE choice only, not the defer answer.
      // It used to be `isGrid || isIdkms`, which made sense when the only defer
      // button lived inside the grid — the grid had to open for it to be
      // reachable. Now that a defer button sits beside the scale tabs, a
      // customer can answer "not yet" before picking a scale, and opening a
      // full grid of sizes in response contradicts what they just said.
      // Deferring after a scale is chosen leaves the grid where it is (isGrid
      // is already true), so nothing is yanked away mid-decision.
      const showGrid = isGrid;
      if (gridCard) gridCard.hidden = !showGrid;
      // Client revision 2026-09-04: the measuring-help tiles are gone from the
      // step altogether, and the sizing-kit promo — the one thing the client
      // wants under the deferred answer — now stays put in every state. The
      // "We craft in…" scale note and the Halfdan quote still belong to the
      // scale path, so the deferred answer keeps hiding those.
      const scaleNote = this.querySelector('.risify-rb__size-scale-note');
      const sizeQuote = this.querySelector('.risify-rb__size-quote');
      if (scaleNote) scaleNote.hidden = isIdkms;
      if (sizeQuote) sizeQuote.hidden = isIdkms;
      if (!isGrid) return;

      // Mirror the original Artistry builder size lists exactly:
      //   UK → every full + half value (H, H½, I, I½, … Z, Z½)
      //   US → whole + half only (4, 4½, … 13, 13½) — no ¼/¾ steps
      //   EU → whole numbers only (46, 47, … 70)
      // Buttons whose region equivalent isn't on the canonical list are
      // hidden in that region so the grid matches what the live Ring
      // Builder displays. The canonical UK letter is preserved as the
      // ring's `size` value so switching back doesn't lose state.
      const isCanonicalForRegion = (row, key) => {
        const v = row[key];
        if (!v) return false;
        if (key === 'us') {
          // Match `4`, `4½`, `5`, `5½`, … `13`, `13½` — whole or half only.
          return /^([4-9]|1[0-3])(½)?$/.test(v);
        }
        if (key === 'eu') {
          // Match whole numbers 46–70.
          return /^(4[6-9]|[5-6][0-9]|70)$/.test(v);
        }
        // UK — accept canonical letter sizes only (H–Z½). Past-Z½ filler
        // rows (Z+1/Z+2/Z+3 used to surface US 13½ + EU 68/69/70) are
        // excluded so the UK list still matches the live PDP chart.
        return /^[A-Z]½?$/.test(v);
      };

      // Swap button labels — keep `data-size-value` as the canonical UK
      // letter so click → selectRingSize() never depends on the visible
      // text.
      const region_key = region === 'us' ? 'us' : (region === 'eu' ? 'eu' : 'uk');
      this.sizeGrid?.querySelectorAll('[data-size-value]').forEach((btn) => {
        // RSFY-708 — IDKMS button lives inside the grid as a peer of the
        // letter buttons. It carries no SIZE_TABLE row (its label is
        // static), so always keep it visible across UK/US/EU views and
        // don't try to rewrite its text.
        if (btn.dataset.sizeValue === 'idkms') {
          btn.hidden = false;
          return;
        }
        const row = SIZE_TABLE_BY_UK[btn.dataset.sizeValue];
        const canonical = row && isCanonicalForRegion(row, region_key);
        btn.hidden = !canonical;
        if (canonical) btn.textContent = row[region_key] || row.uk;
      });

      // Refresh the dimensions strip in case region changed but selection
      // didn't (the "equivalent" column flips between US / EU / UK based
      // on which tab is active).
      this._paintSizeDimensions(ring);
    }

    /**
     * Paint Inner diameter / Circumference / region-equivalent from
     * SIZE_TABLE for the currently selected size. When the customer hasn't
     * picked a size yet, the strip stays hidden so the bordered card
     * collapses to just the grid.
     */
    _paintSizeDimensions(ring) {
      const strip = this.querySelector('[data-size-dimensions]');
      if (!strip) return;
      const size = ring && ring.size;
      const row = size ? SIZE_TABLE_BY_UK[size] : null;
      if (!row) {
        strip.hidden = true;
        return;
      }
      strip.hidden = false;

      const region = (ring && ring.sizeRegion) || 'uk';
      const equivLabel = this.querySelector('[data-size-dim-equiv-label]');
      const equivValue = this.querySelector('[data-size-dim-equiv]');
      const diamEl = this.querySelector('[data-size-dim-diameter]');
      const circEl = this.querySelector('[data-size-dim-circ]');
      if (diamEl) diamEl.textContent = `${row.diameter} mm`;
      if (circEl) circEl.textContent = `${row.circumference} mm`;

      // Equivalent column flips with region — when the customer is on UK
      // we show US; on US we show UK; on EU we show US (most useful
      // cross-reference for the average ring-builder customer).
      if (region === 'us') {
        if (equivLabel) equivLabel.textContent = 'UK equivalent';
        if (equivValue) equivValue.textContent = row.uk;
      } else if (region === 'eu') {
        if (equivLabel) equivLabel.textContent = 'US equivalent';
        if (equivValue) equivValue.textContent = row.us;
      } else {
        if (equivLabel) equivLabel.textContent = 'US equivalent';
        if (equivValue) equivValue.textContent = row.us;
      }
    }

    // =========================================================================
    // Option Index Helper
    // =========================================================================

    findOptionIndex(product, keyword) {
      if (!product || !product.options) return -1;
      const kw = keyword.toLowerCase();
      // Alias whitelist (RSFY-647) so variant option naming differences
      // across the catalog don't break matching.
      const aliases = {
        stone: ['stone', 'diamond', 'gemstone', 'gem'],
        engraving: ['engraving', 'inscription', 'engrave'],
        carat: ['carat', 'caret', 'gold quality', 'metal quality'],
      };
      const candidates = aliases[kw] || [kw];
      const opts = product.options.map(o => o.toLowerCase());
      for (const cand of candidates) {
        const idx = opts.findIndex(o => o.includes(cand));
        if (idx >= 0) {
          if (this._debug) console.log('[ringBuilder] findOptionIndex', { keyword: kw, matched: cand, idx, options: product.options });
          return idx;
        }
      }
      if (this._debug) console.log('[ringBuilder] findOptionIndex MISS', { keyword: kw, options: product.options });
      return -1;
    }

    // =========================================================================
    // Variant Matching
    // =========================================================================

    updateVariantMatchForRings(ringIndices) {
      // RSFY-685 — reconcile the Stone Quality label one last time so the
      // matcher works against colour/clarity that genuinely correspond to
      // the quality the UI is about to render selected. Without this, a
      // _setDiamondHas snap that pushed colour to D + clarity to VS (mapping
      // = Premium) while the displayed-quality reconcile would derive "High
      // Quality" (because pool axes prefer G/SI iteration order) leaves the
      // matcher locking onto a Premium variant under a High-labelled card.
      ringIndices.forEach((i) => {
        const ring = this.rings[i];
        if (ring && ring.hasDiamond === true) this._syncDiamondQuality(ring);
      });

      ringIndices.forEach((i) => {
        const ring = this.rings[i];
        if (!ring.selectedProduct) return;
        const variant = this.findMatchingVariant(ring);
        ring.selectedVariant = variant;
      });

      ringIndices.forEach((i) => {
        const ring = this.rings[i];
        if (ring && ring.selectedProduct) {
          this.updateProductImage(ring.selectedProduct, ring.selectedVariant, [i]);
        }
      });

      const displayRing = ringIndices.map((i) => this.rings[i]).find((r) => r.selectedVariant) || this.rings[ringIndices[0]];
      if (displayRing) {
        this.updatePriceDisplay(displayRing.selectedVariant);
      }

      // Progressive validation status (RSFY-652)
      const ring = this.rings[ringIndices[0]];
      if (!ring) return;

      // Metal status — only surface a problem. The green "Available — …"
      // confirmation card was removed per design (redundant noise); a
      // successful selection now shows nothing.
      if (ring.metalType && ring.carat) {
        if (ring.selectedProduct) {
          this.clearStepValidationStatus && this.clearStepValidationStatus('metal');
        } else {
          this.showStepValidationStatus('metal', 'warn', `No ${ring.metalType} ring is available in ${ring.carat}.`);
        }
      } else {
        this.clearStepValidationStatus && this.clearStepValidationStatus('metal');
      }

      // Diamond status — only after the customer has answered the toggle.
      if (ring.hasDiamond === null) {
        this.clearStepValidationStatus && this.clearStepValidationStatus('diamond');
      } else if (ring.hasDiamond === true) {
        // RSFY-647 P1(g) — same: only label with cut when catalogue carries cut data.
        const labelParts = [this._caratDisplayLabel(ring.diamondCarat)];
        if (this._hasCutData(ring) && ring.diamondCut) {
          labelParts.push(ring.diamondCut.replace(/^\w/, (c) => c.toUpperCase()));
        }
        labelParts.push(ring.diamondColor, ring.diamondClarity);
        const label = labelParts.join(' · ');
        if (ring.selectedVariant) {
          // Green "Available — …" confirmation removed per design; only
          // surface a problem below.
          this.clearStepValidationStatus && this.clearStepValidationStatus('diamond');
        } else {
          const reason = ring._lastVariantFailReason;
          const msg = reason === 'stone'
            ? `No variant matches ${label} for this metal/carat.`
            : 'No variant matches this diamond selection.';
          this.showStepValidationStatus('diamond', 'warn', msg);
        }
      } else if (ring.hasDiamond === false) {
        // The catalogue doesn't always carry a plain-band variant for every
        // metal/carat. Surface that in the diamond step itself instead of
        // letting the customer reach review before they see it.
        if (ring.selectedVariant) {
          // Green confirmation removed per design; only surface a problem.
          this.clearStepValidationStatus && this.clearStepValidationStatus('diamond');
        } else {
          this.showStepValidationStatus('diamond', 'warn',
            'No plain band is available for this metal/carat. Add a diamond or change metal.');
        }
      }

      // Engraving status — only surface a validation message once the customer
      // has actually completed the engraving sub-flow. `ring.engraving` is
      // populated as a side effect of `deriveEngravingValue` even when
      // `hasEngraving` is still null, which used to produce a false "No
      // matching variant" warn on every selection in earlier steps.
      const engravingComplete = this.isStepCompleteForRing('engraving', ring);
      if (!engravingComplete) {
        this.clearStepValidationStatus && this.clearStepValidationStatus('engraving');
      } else if (ring.engraving) {
        if (ring.selectedVariant && ring.selectedVariant.available) {
          // Green confirmation removed per design; only surface a problem.
          this.clearStepValidationStatus && this.clearStepValidationStatus('engraving');
        } else if (ring.selectedVariant && !ring.selectedVariant.available) {
          this.showStepValidationStatus('engraving', 'warn', 'This configuration is currently out of stock.');
        } else {
          // No variant matched. Only blame the engraving step when the
          // matcher itself flagged engraving as the failing axis; otherwise
          // the warning belongs to whichever earlier step actually broke.
          const reason = ring._lastVariantFailReason;
          if (reason === 'engraving') {
            this.showStepValidationStatus('engraving', 'warn', `No variant matches the “${ring.engraving}” engraving option.`);
          } else {
            // Upstream step is at fault — keep engraving's own status clean
            // so the customer isn't told two different things at once.
            this.clearStepValidationStatus && this.clearStepValidationStatus('engraving');
          }
        }
      }
    }

    /** Hide a step's validation status banner without changing its content. */
    clearStepValidationStatus(step) {
      const el = this.querySelector(`[data-validation-status="${step}"]`);
      if (!el) return;
      el.style.display = 'none';
      el.classList.remove('risify-rb__validation-status--ok', 'risify-rb__validation-status--warn');
      el.innerHTML = '';
    }

    /** Progressive validation UI helper (RSFY-652). */
    showStepValidationStatus(step, kind, message) {
      const el = this.querySelector(`[data-validation-status="${step}"]`);
      if (!el) return;
      el.classList.remove('risify-rb__validation-status--ok', 'risify-rb__validation-status--warn');
      el.classList.add(kind === 'ok' ? 'risify-rb__validation-status--ok' : 'risify-rb__validation-status--warn');
      const icon = kind === 'ok' ? '✓' : '!';
      el.innerHTML = `<span class="risify-rb__validation-status__icon">${icon}</span><span>${message}</span>`;
      el.style.display = '';
    }

    clearStepValidationStatus(step) {
      const el = this.querySelector(`[data-validation-status="${step}"]`);
      if (!el) return;
      el.style.display = 'none';
      el.innerHTML = '';
    }

    findMatchingVariant(ring) {
      if (!ring.selectedProduct) return null;

      const product = ring.selectedProduct;
      const stoneIndex = this.findOptionIndex(product, 'stone');
      const engravingIndex = this.findOptionIndex(product, 'engraving');
      const caratsIndex = this.findOptionIndex(product, 'carat');

      let lastFailReason = null;

      const match = product.variants.find((v) => {
        // Carat — numeric first, case-insensitive string fallback
        if (caratsIndex >= 0 && ring.carat) {
          const caratKey = `option${caratsIndex + 1}`;
          const variantCarat = v[caratKey];
          if (variantCarat) {
            const vNum = parseInt(variantCarat, 10);
            const rNum = parseInt(ring.carat, 10);
            if (vNum && rNum && vNum !== rNum) { lastFailReason = 'carat'; return false; }
            if (!vNum && variantCarat.toLowerCase() !== ring.carat.toLowerCase()) { lastFailReason = 'carat'; return false; }
          }
        }

        // Stone — D3 multi-axis matching (RSFY-642 / RSFY-647)
        if (stoneIndex >= 0 && ring.hasDiamond === true) {
          const stoneKey = `option${stoneIndex + 1}`;
          const parsed = v[stoneKey] ? this._parseStoneQualityString(v[stoneKey]) : null;
          if (!parsed) { lastFailReason = 'stone'; return false; }
          if (ring.diamondCarat != null && parsed.carat != null && parsed.carat !== ring.diamondCarat) {
            lastFailReason = 'stone'; return false;
          }
          // RSFY-647 P0(b) — range-aware match: the variant supports the
          // chosen colour/clarity if it appears anywhere in the parsed range.
          if (ring.diamondColor) {
            const variantColors = parsed.colors && parsed.colors.length ? parsed.colors : (parsed.color ? [parsed.color] : []);
            if (variantColors.length && !variantColors.includes(ring.diamondColor)) {
              lastFailReason = 'stone'; return false;
            }
          }
          if (ring.diamondClarity) {
            const variantClarities = parsed.clarities && parsed.clarities.length ? parsed.clarities : (parsed.clarity ? [parsed.clarity] : []);
            if (variantClarities.length && !variantClarities.includes(ring.diamondClarity)) {
              lastFailReason = 'stone'; return false;
            }
          }
          // diamondCut intentionally not matched — catalog has no cut data.
        } else if (stoneIndex >= 0 && ring.hasDiamond === false) {
          // Plain band — pick variants whose stone option is empty / "none".
          const stoneKey = `option${stoneIndex + 1}`;
          const sv = v[stoneKey] || '';
          if (sv && !/none|no\s*diamond|plain/i.test(sv)) {
            lastFailReason = 'stone'; return false;
          }
        } else if (stoneIndex >= 0 && ring.diamond && ring.diamond !== 'none') {
          // Legacy fallback: match against bundled string with normalisation.
          const stoneKey = `option${stoneIndex + 1}`;
          if (v[stoneKey]) {
            const a = this._normalizeStoneQuality(v[stoneKey]);
            const b = this._normalizeStoneQuality(ring.diamond);
            if (a !== b) { lastFailReason = 'stone'; return false; }
          }
        }

        // Engraving — case-insensitive. When `ring.engraving` hasn't been
        // committed yet (the customer hasn't visited Step 4 / Engraving),
        // derive the variant from the ring's engravingDetails state so the
        // matcher picks the Both/Outside/Inside/None variant that matches
        // the customer's current toggles. Without this fallback every new
        // selection on Step 1 lands on the "None" variant, breaking parity
        // with the original Ring Builder which surfaces "Both" by default.
        if (engravingIndex >= 0) {
          const engravingKey = `option${engravingIndex + 1}`;
          const targetEngraving = ring.engraving || this.deriveEngravingValue(ring);
          if (targetEngraving && v[engravingKey] && v[engravingKey].toLowerCase() !== targetEngraving.toLowerCase()) {
            lastFailReason = 'engraving'; return false;
          }
        }

        return true;
      });

      if (this._debug) {
        if (match) {
          console.log('[ringBuilder] findMatchingVariant matched', { product: product.handle, variant: match.id, ring: { carat: ring.carat, hasDiamond: ring.hasDiamond, diamondCarat: ring.diamondCarat, engraving: ring.engraving } });
        } else {
          console.log('[ringBuilder] findMatchingVariant MISS', { product: product.handle, lastFailReason, ring: { carat: ring.carat, hasDiamond: ring.hasDiamond, diamondCarat: ring.diamondCarat, engraving: ring.engraving } });
        }
      }
      ring._lastVariantFailReason = match ? null : lastFailReason;
      return match;
    }

    // =========================================================================
    // UI Updates
    // =========================================================================

    async updateProductImage(product, variant, ringIndices) {
      if (!product) return;
      const indices = ringIndices || this.getCheckedRings('metal');
      const modelUrl = this.get3DModelUrl(product, variant);

      for (const i of indices) {
        if (modelUrl && this.ringViewers && this.ringViewers[i]) {
          // RSFY-709 — clone the PDP's per-product engraving calibration onto
          // the builder viewer BEFORE the model swap, so the engraving size +
          // x/y offsets match what the customer sees on the product page.
          // The PDP snippet (`snippets/3d-ring-viewer.liquid`) reads the same
          // `outsidefontsizenoneelvish` / `insidefontsizenoneelvish` /
          // `override_*` metafields; emitting them on `window.ringBuilderProducts`
          // lets us mirror that here. Empty string = "no override" → fall
          // back to scale 1.0 and zero offsets (PDP default branch).
          this._applyEngravingCalibration(this.ringViewers[i], product);
          await this.show3DViewerForRing(i, modelUrl);
        }
      }
      // RSFY-718 — keep the Setting detail panel in sync. This is the single
      // fan-out point every metal/setting/weight/diamond change funnels
      // through; the panel self-hides unless the Stone step + diamond are live.
      this._renderSettingDetail();
    }

    /**
     * RSFY-709 — copy `outsideFontScale` / `insideFontScale` / `outsideXOffset`
     * / `outsideYOffset` / `insideYOffset` from a product's catalog payload
     * onto the <ring-viewer-3d> dataset. Triggers the viewer's internal
     * resize listener so the running engraving text immediately re-renders
     * at the new scale.
     */
    _applyEngravingCalibration(viewer, product) {
      if (!viewer || !product) return;
      // Per-product metafield overrides ONLY. If the metafield is blank
      // we leave the existing attribute alone — the builder default that
      // `createRingViewers` already wrote stays in force. (Removing the
      // attribute would force a FONT_SCALE_BY_NAME fallback that doesn't
      // match the builder's side-on camera angle and re-introduces the
      // outside-text-too-large symptom from RSFY-709 #1.)
      const apply = (attr, raw) => {
        const hasValue = raw !== null && raw !== undefined && String(raw).trim() !== '';
        if (hasValue) viewer.setAttribute(attr, String(raw));
      };
      apply('data-outside-font-scale', product.outsideFontScale);
      apply('data-inside-font-scale', product.insideFontScale);
      apply('data-outside-x-offset', product.outsideXOffset);
      apply('data-outside-y-offset', product.outsideYOffset);
      apply('data-inside-y-offset', product.insideYOffset);
    }

    // =========================================================================
    // 3D Viewer (Multi-Ring)
    // =========================================================================

    static METAL_MAP = {
      'Red Gold': 'Red Gold',
      'White Gold': 'White Gold/Platinum',
      'Yellow Gold': 'Yellow Gold',
      'Platinum': 'White Gold/Platinum',
      'Sterling Silver': 'Sterling Silver',
    };

    createRingViewers(ringCount) {
      if (!this.viewersContainer) return;
      this.viewersContainer.innerHTML = '';
      this.ringViewers = [];
      this.viewersContainer.classList.toggle('multi-ring', ringCount > 1);

      const activeIdx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;

      for (let i = 0; i < ringCount; i++) {
        const viewer = document.createElement('ring-viewer-3d');
        if (window.RISIFY_ENGRAVING_CSS_URL) viewer.dataset.engravingCssUrl = window.RISIFY_ENGRAVING_CSS_URL;
        viewer.setAttribute('data-manual-init', '');
        viewer.setAttribute('data-ring-index', i);
        // Engraving font size is matched to the original PDP ring viewer by
        // (a) using the PDP's base sizes inside the viewer (55 outside /
        // 28.05 inside for band-only) and (b) letting the viewer's per-font
        // EM-square correction (FONT_SCALE_BY_NAME) apply — exactly as the
        // PDP does — so Elvish and English fonts keep their correct relative
        // sizes. We therefore DO NOT hard-code a font scale here (a fixed
        // scale would override scaleForFont() and render every font at the
        // same size). Per-product metafields still override via
        // `_applyEngravingCalibration` when the merchant has set them.
        viewer.setAttribute('data-outside-x-offset', '0');
        viewer.setAttribute('data-outside-y-offset', '0');
        viewer.setAttribute('data-inside-y-offset', '0');
        viewer.classList.add('ring-viewer-3d');
        // Figma 6039:8573 — preview rail shows ONE viewer at a time. We
        // keep all ring viewers in the DOM so their canvases stay warm
        // (re-creating WebGL contexts on every tab swap is expensive),
        // but only the `--active` one is `display: block` via CSS. The
        // single-ring case still tags the lone viewer as active so the
        // same rule lights it up.
        viewer.classList.toggle('risify-rb__ring-viewer--active', i === activeIdx);
        viewer.innerHTML = `
          <div class="ring-viewer-3d__canvas-container">
            <canvas class="ring-viewer-3d__canvas"></canvas>
            <div class="ring-viewer-3d__loading">
              <div class="ring-viewer-3d__spinner"></div>
              <span class="ring-viewer-3d__loading-text">Loading 3D Model...</span>
            </div>
          </div>
        `;
        this.viewersContainer.appendChild(viewer);
        this.ringViewers.push(viewer);
      }
    }

    /**
     * Figma 6039:8573 — Sync the visible 3D viewer with the active ring tab.
     * Only the viewer carrying `--active` is shown via CSS; the rest stay
     * in the DOM but `display: none`. Called from `_switchToRing` and any
     * other path that mutates `currentRingIndex`.
     */
    _updateRingViewerActiveState() {
      if (!this.ringViewers || this.ringViewers.length === 0) return;
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      this.ringViewers.forEach((viewer, i) => {
        if (!viewer) return;
        viewer.classList.toggle('risify-rb__ring-viewer--active', i === idx);
        // Belt-and-braces: also clear the legacy `--dimmed` class if it was
        // applied by an older bundle — the new rail layout no longer uses it.
        viewer.classList.remove('risify-rb__ring-viewer--dimmed');
      });
    }

    // =========================================================================
    // 3D preview metal chip
    // =========================================================================

    /** The active ring (the one the preview reflects). */
    _settingDetailActiveRing() {
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      return this.rings[idx] || null;
    }

    /** Keep the 3D hero's metal-label chip ("Red Gold") in sync with the
     *  active ring. */
    _updatePreviewMetalLabel() {
      if (!this.previewMetalLabel) return;
      const ring = this._settingDetailActiveRing();
      this.previewMetalLabel.textContent = (ring && ring.metalType) || '';
    }

    /** Kept as the single fan-out point every metal/setting/weight/diamond
     *  change funnels through. The 2D "Setting detail" reference panel was
     *  retired — the 3D viewer is the sole hero preview — so this now only
     *  refreshes the metal chip over the 3D preview. Callers are unchanged. */
    _renderSettingDetail() {
      this._updatePreviewMetalLabel();
    }

    get3DModelUrl(product, variant) {
      if (!product) return null;

      // Find the active ring so we can branch on hasDiamond (plain band vs
      // diamond ring share the diamond-bearing GLB by default, but PDP plain
      // bands have their own setting-free GLB — see PLAIN_BAND_FALLBACK_URL).
      // `selectMetalType` clears selectedProduct momentarily before the new
      // resolve runs, so `rings.find(... selectedProduct === product)` can
      // miss. Fall back to the active ring's state directly so the preview
      // honours the customer's diamond choice even mid-resolve.
      let ring = this.rings.find((r) => r.selectedProduct === product);
      if (!ring) {
        const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
        ring = this.rings[idx];
      }
      // Catalogue hint: a `rb-diamond:no` tag is itself a plain-band marker,
      // so even if the ring state hasn't been updated yet we should treat
      // the product as plain (matches what `resolveProductForRings` will
      // pick once it runs).
      const productIsPlain = this._tagValue?.(product, 'rb-diamond') === 'no';
      const wantsPlainBand = productIsPlain || (ring && ring.hasDiamond === false);

      // Customer-supplied band-width GLB wins for the plain-band path. Without
      // this the band-only preview always showed the 7mm GLB regardless of the
      // customer's Band Width pick. Resolves to null for widths with no supplied
      // model, which then fall through to the legacy 7mm fallback below.
      if (wantsPlainBand && ring && ring.bandWidth != null && ring.bandWidth !== '') {
        const bandModel = resolveBandModelUrl(ring.bandWidth);
        if (bandModel) {
          if (this._debug) {
            console.log('[ringBuilder] get3DModelUrl → band model', {
              bandWidth: ring.bandWidth, url: bandModel,
            });
          }
          return bandModel;
        }
      }

      // Customer-supplied setting × carat GLB wins for the diamond path.
      // The catalogue ships no 3d_variant_* metafields, so without this the
      // preview never changed with the chosen setting. Resolves to null for
      // settings the sheet doesn't cover (Bezel/Crown/Gypsy), which then fall
      // through to the catalog metafields / legacy fallback below unchanged.
      if (!wantsPlainBand && ring && ring.settingStyle) {
        const settingModel = resolveSettingModelUrl(ring.settingStyle, ring.diamondCarat, this);
        if (settingModel) {
          if (this._debug) {
            console.log('[ringBuilder] get3DModelUrl → setting model', {
              setting: ring.settingStyle, carat: ring.diamondCarat, url: settingModel,
            });
          }
          return settingModel;
        }
      }

      // Variant B model — diamond-specific override (e.g. "with diamond" cut).
      if (!wantsPlainBand && product.model3dVariantB) {
        if (ring && ring.diamond && product.model3dVariantBLabel) {
          const diamondLower = ring.diamond.toLowerCase();
          const labelLower = product.model3dVariantBLabel.toLowerCase();
          if (diamondLower.includes(labelLower) || labelLower.includes(diamondLower)) {
            return product.model3dVariantB;
          }
        }
      }

      // Catalog-supplied model wins for the diamond path.
      if (!wantsPlainBand) {
        if (product.model3dVariantA) return product.model3dVariantA;
        if (product.model3dMedia) return product.model3dMedia;
      }

      // Fallbacks — when the catalogue hasn't filled in a model metafield
      // we use a well-known GLB URL. Diamond default still points at the
      // E-cut 0.33ct movie ring; "no diamond" uses the 7mm wide plain band
      // GLB that ships with an Outside_Engrave_Full mesh (the 5.5mm GLB
      // we used before lacks that mesh, so engravings rendered to nothing).
      if (this.ringViewers && this.ringViewers.length > 0) {
        if (wantsPlainBand) {
          return 'https://cdn.shopify.com/s/files/1/0212/3526/files/W_b7mm_s10_D_3v1.glb?v=1680583846';
        }
        return 'https://cdn.shopify.com/s/files/1/0212/3526/files/E_round_elvish_pt33ct_b3mm_s7.glb?v=1679936000';
      }

      return null;
    }

    async show3DViewerForRing(ringIndex, modelUrl) {
      const viewer = this.ringViewers?.[ringIndex];
      if (!viewer) return;
      try {
        await viewer.loadModelFromUrl(modelUrl);
        const ring = this.rings[ringIndex];
        if (ring && ring.metalType) {
          const mapped = RisifyRingBuilder.METAL_MAP[ring.metalType] || ring.metalType;
          await viewer.setMetal(mapped);
        }
        // RSFY-676 — hide the diamond mesh until the customer explicitly opts
        // in. The builder ships only diamond-bearing GLBs today, so this is
        // the cheapest way to honour Halfdan's "default to Movie Ring" ask
        // without authoring a separate plain-band model.
        const showDiamond = !!ring && ring.hasDiamond === true;
        await viewer.setDiamond(showDiamond);
        // Bug B — sync hallmark mesh with ring state on every model swap.
        // Default is on (rings init with hallmark:true) so the inside
        // hallmark is visible until the customer explicitly chooses "No".
        const showHallmark = !ring || ring.engravingDetails?.hallmark !== false;
        if (typeof viewer.setHallmark === 'function') viewer.setHallmark(showHallmark);
        viewer.style.display = '';
      } catch (e) {
        console.warn(`[Ring Builder] Viewer ${ringIndex} failed:`, e.message);
      }
    }

    show3DViewer(modelUrl) {
      const indices = this.getCheckedRings('metal');
      indices.forEach((i) => this.show3DViewerForRing(i, modelUrl));
    }

    hide3DViewer() {
      if (this.ringViewers) {
        this.ringViewers.forEach((v) => { v.style.display = 'none'; });
      }
    }

    _syncEngravingToViewer(side, ring) {
      const ringIndex = this.rings.indexOf(ring);
      if (ringIndex < 0 || !this.ringViewers?.[ringIndex]) return;
      const viewer = this.ringViewers[ringIndex];
      // The WebGI viewer boots asynchronously, and the engraving step is
      // reachable before it finishes. This used to return here and never
      // retry, which was invisible while engraving defaulted to empty — there
      // was nothing to push. Now that the step opens with the verse already
      // filled in, that silent return left the textareas populated while the
      // ring rendered bare. Wait for the viewer, then apply.
      if (!viewer.viewer) {
        if (typeof viewer._waitUntilReady === 'function') {
          viewer._waitUntilReady().then(() => {
            // Re-read state on the way back: the customer may have typed or
            // switched rings during the wait, and their input must win.
            if (viewer.viewer && this.rings.indexOf(ring) === ringIndex) {
              this._syncEngravingToViewer(side, ring);
            }
          });
        }
        return;
      }
      const d = ring.engravingDetails;
      const isOut = side === 'outside';
      const text = isOut ? d.outsideText : d.insideText;
      const lang = isOut ? d.outsideLanguage : d.insideLanguage;
      const font = lang === 'elvish' ? 'Elvish' : (isOut ? d.outsideFont : d.insideFont) || 'Kereru';
      viewer.updateEngravingFromBuilder(side, text, font);
    }

    /**
     * Bug fix #10 — Debounced engraving sync. Text input handlers should call
     * this instead of _syncEngravingToViewer directly so the 3D plugin's
     * expensive font/glyph rebuild only fires after the user stops typing.
     * Local state (textarea value, summary rail, fit indicator) still updates
     * synchronously on each keystroke; only the 3D writes get coalesced.
     */
    _scheduleViewerSync(side, ring) {
      if (!this._viewerSyncTimers) this._viewerSyncTimers = {};
      const ringIdx = this.rings.indexOf(ring);
      if (ringIdx < 0) return;
      const key = `${ringIdx}:${side}`;
      if (this._viewerSyncTimers[key]) clearTimeout(this._viewerSyncTimers[key]);
      this._viewerSyncTimers[key] = setTimeout(() => {
        this._viewerSyncTimers[key] = null;
        this._syncEngravingToViewer(side, ring);
      }, 140);
    }

    /**
     * Bug fix #10 — Debounced fit indicator update. EngravingFitEngine
     * recalculation is cheap but DOM writes + reflow accumulate when typing
     * fast. Coalesce to one update per ~100ms keystroke burst.
     */
    _scheduleFitUpdate(side, text, ring) {
      if (!this._fitIndicatorsEnabled) return; // RSFY-683 — de-scoped for v1
      if (!this._fitUpdateTimers) this._fitUpdateTimers = {};
      const key = `fit:${side}`;
      if (this._fitUpdateTimers[key]) clearTimeout(this._fitUpdateTimers[key]);
      this._fitUpdateTimers[key] = setTimeout(() => {
        this._fitUpdateTimers[key] = null;
        this._updateBuilderFit(side, text, ring);
      }, 100);
    }

    // =========================================================================
    // Engraving Fit Indicator (RB) — mirrors engraving-form.js on PDP
    // =========================================================================

    _initBuilderFitIndicators() {
      // RSFY-683 — fit indicator de-scoped for v1. Halfdan asked to remove
      // the "fits comfortably" feature from the builder; the underlying
      // EngravingFitEngine is still used by the PDP engraving-form so we
      // gate only the builder UI here. Flip `_fitIndicatorsEnabled` to true
      // (or remove this guard) when the feature ships post-v1.
      if (!this._fitIndicatorsEnabled) return;
      if (this._builderFitReady) return; // already initialised
      if (typeof EngravingFitEngine === 'undefined') return;
      EngravingFitEngine.init().then(() => {
        this._builderFitReady = true;
        if (this._debug) console.log('[RB] EngravingFitEngine ready');
        // Initial paint based on current state (textareas may already have default text)
        ['outside', 'inside'].forEach((side) => {
          const ta = this.querySelector(`[data-${side}-text]`);
          const ring = this.rings[this.currentRingIndex || 0];
          if (ta && ring) this._updateBuilderFit(side, ta.value, ring);
        });
      });
    }

    _updateBuilderFit(side, text, ring) {
      if (!this._fitIndicatorsEnabled) return; // RSFY-683 — de-scoped for v1
      if (typeof EngravingFitEngine === 'undefined') return;
      const indicator = this.querySelector(`[data-builder-fit="${side}"]`);
      const textarea = this.querySelector(`[data-${side}-text]`);
      if (!indicator) return;

      if (!text || !text.trim()) {
        indicator.hidden = true;
        if (textarea) textarea.style.borderColor = '';
        return;
      }

      // Pick font: Elvish for elvish language, otherwise the selected font
      const d = ring && ring.engravingDetails;
      const lang = d ? (side === 'outside' ? d.outsideLanguage : d.insideLanguage) : null;
      const font = lang === 'elvish'
        ? 'Elvish'
        : ((d && (side === 'outside' ? d.outsideFont : d.insideFont)) || 'Kereru');

      // Read live geometry from the ring state so the fit metric matches the
      // size and band width the customer actually picked in step 4. Hallmark
      // is only relevant on the inside surface and only counts when the
      // customer has opted in (default true, see Bug B).
      const bandWidth = (() => {
        // Effective width — the fit math must model the band the ring is
        // actually made on (diamond settings: fixed 4mm / Petite 3mm).
        const w = this._effectiveBandWidth(ring);
        return w ? parseFloat(w) || 7.2 : 7.2;
      })();
      const ringSize = (ring && ring.size) ? parseFloat(ring.size) || 7 : 7;
      const hasHallmark = side === 'inside'
        && (!d || d.hallmark !== false);

      let result;
      try {
        result = EngravingFitEngine.calculateFit({
          text: text,
          font: font,
          ringSize: ringSize,
          bandWidth: bandWidth,
          position: side,
          hasHallmark: hasHallmark,
        });
      } catch (e) {
        if (this._debug) console.warn('[RB] fit calc error', e);
        return;
      }

      const colors = { green: '#8a9d7e', amber: '#c48a1a', red: '#a13a1c' };
      const dotColor = colors[result.status] || '#6b6457';

      indicator.innerHTML = `
        <span class="risify-rb__fit-dot" style="background:${dotColor};"></span>
        <span class="risify-rb__fit-text">${result.message}</span>
        <span class="risify-rb__fit-pct">${result.fillPct}%</span>
      `;
      indicator.hidden = false;
      if (textarea) textarea.style.borderColor = dotColor;
    }

    // =========================================================================
    // Footer Total
    // =========================================================================

    updateFooterTotal() {
      let total = 0;
      let anyVariant = false;

      for (let i = 0; i < this.rings.length; i++) {
        const variant = this.rings[i].selectedVariant;
        if (variant && variant.price) {
          total += variant.price;
          anyVariant = true;
        }
      }

      const formatted = anyVariant ? this.formatMoney(total) : '';
      if (this.footerTotal) this.footerTotal.textContent = formatted;
      if (this.footerTotalMobile) this.footerTotalMobile.textContent = formatted;
      this.updateAddToCartVisibility();
    }

    updateAddToCartVisibility() {
      if (!this.addToCartBtn) return;

      const allComplete =
        this.isStepComplete('metal') &&
        this.isStepComplete('diamond') &&
        this.isStepComplete('engraving') &&
        this.isStepComplete('size');

      // Only hide/show the button when it's the dedicated review-slip add-to-cart.
      // If we fell back to the footer Continue, never hide it — it doubles as
      // the step-nav button and must stay visible across every step.
      if (this._addToCartBtnIsDedicated) {
        this.addToCartBtn.style.display = allComplete ? '' : 'none';
      }

      if (allComplete) {
        const hasUnavailable = this.rings.some((ring) => {
          const variant = ring.selectedVariant;
          return !variant || !variant.available;
        });

        if (hasUnavailable) {
          this.addToCartBtn.setAttribute('aria-disabled', 'true');
          this.addToCartBtn.style.opacity = '0.5';
          this.addToCartBtn.style.pointerEvents = 'none';
        } else {
          this.addToCartBtn.removeAttribute('aria-disabled');
          this.addToCartBtn.style.opacity = '';
          this.addToCartBtn.style.pointerEvents = '';
        }
      } else if (!this._addToCartBtnIsDedicated) {
        // Footer continue must stay clickable when the user is mid-flow.
        this.addToCartBtn.removeAttribute('aria-disabled');
        this.addToCartBtn.style.opacity = '';
        this.addToCartBtn.style.pointerEvents = '';
      }
    }

    updatePriceDisplay(variant) {
      this.updateFooterTotal();
    }

    formatMoney(cents) {
      if (!cents && cents !== 0) return '';

      const moneyFormat = this.config.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100).toString();
      const amountWithComma = amount.replace('.', ',');

      let formatted = moneyFormat
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount}}', amount);

      formatted = formatted.replace(/<[^>]*>/g, '').trim();
      return formatted;
    }

    clearStepSelections() {
      if (this.metalSelectedLabel) this.metalSelectedLabel.style.display = 'none';
      if (this.caratSection) this.caratSection.style.display = 'none';
    }

    // =========================================================================
    // Step Validation
    // =========================================================================

    isStepComplete(stepName) {
      for (let i = 0; i < this.ringCount; i++) {
        if (!this.isStepCompleteForRing(stepName, this.rings[i])) return false;
      }
      return true;
    }

    /**
     * Bug fix #8 — Sequential multi-ring: ara step navigation'ında sadece
     * **active** ring'in step'i kontrol edilsin. Cross-ring completeness
     * sadece review/add-to-cart anında bakılır (`isStepComplete`).
     */
    isStepCompleteForCurrentRing(stepName) {
      const idx = (typeof this.currentRingIndex === 'number') ? this.currentRingIndex : 0;
      return this.isStepCompleteForRing(stepName, this.rings[idx]);
    }

    isStepCompleteForRing(stepName, ring) {
      if (!ring) return false;

      switch (stepName) {
        case 'metal':
          return !!(ring.metalType && ring.carat);
        case 'diamond': {
          // RSFY-707 — Band Only path requires a band width pick (Standard
          // 7mm / Narrow 5mm / Slim 4mm / Petite 3mm). Without it the
          // resolved product silently defaults to the flagship 7mm, hiding
          // a real choice that affects price. Check this BEFORE the
          // "stone option absent → auto-complete" shortcut, otherwise
          // plain-band products (which never carry a stone option) skip
          // validation entirely.
          if (ring.hasDiamond === false && !ring.bandWidth) return false;
          if (ring.selectedProduct) {
            const stoneIndex = this.findOptionIndex(ring.selectedProduct, 'stone');
            if (stoneIndex < 0) return true;
          }
          // Diamond step is complete once the customer has answered the
          // toggle (No diamond / With diamond). The four axes have safe
          // defaults so they don't need individual validation.
          if (ring.hasDiamond === true) return true;
          if (ring.hasDiamond === false) return !!ring.bandWidth;
          return false;
        }
        case 'engraving': {
          if (ring.selectedProduct) {
            const engravingIndex = this.findOptionIndex(ring.selectedProduct, 'engraving');
            if (engravingIndex < 0) return true;
          }
          const d = ring.engravingDetails;
          if (d.hasEngraving === null) return false;
          if (!d.hasEngraving) return true;
          if (d.outsideEngraving === null || d.insideEngraving === null) return false;
          if (!d.outsideEngraving && !d.insideEngraving) return false;
          if (d.outsideEngraving && !d.outsideLanguage) return false;
          // Text is required for any picked side (English OR Elvish) — an
          // empty textarea on a chosen language means the workshop has
          // nothing to engrave.
          if (d.outsideEngraving && !(d.outsideText || '').trim()) return false;
          if (d.insideEngraving && !d.insideLanguage) return false;
          if (d.insideEngraving && !(d.insideText || '').trim()) return false;
          if (d.hallmark === null) return false;
          return true;
        }
        case 'size':
          // Size must be committed before review — either an explicit
          // UK/US/EU pick (`ring.size` set) OR the "I don't know my size"
          // tab, which delegates the decision to the workshop / sizing-
          // kit flow. Both states emit something meaningful to the cart
          // properties so the workshop knows what to do next.
          if ((ring.sizeUnknown || ring.sizeRegion === 'idkms')) return true;
          return !!ring.size;
        default:
          return true;
      }
    }

    canOpenStep(stepName) {
      const targetIndex = this.stepOrder.indexOf(stepName);
      if (targetIndex <= 0) return true;

      for (let i = 0; i < targetIndex; i++) {
        if (!this.isStepComplete(this.stepOrder[i])) return false;
      }
      return true;
    }

    showStepWarning(stepEl, message) {
      // The validation now lives in the footer next to the Continue CTA so
      // the warning sits exactly where the customer's attention is. The
      // legacy paragraph-under-the-step approach is gone — one pill that
      // stays visible until the customer makes the required selection
      // (`clearStepWarning` is called from selectMetalType / selectCarat /
      // _setDiamondHas / etc.). No auto-dismiss because a disappearing
      // warning leaves the customer wondering why the CTA doesn't work.
      const pill = this.querySelector('[data-step-warning]');
      if (!pill) return;
      const text = pill.querySelector('[data-step-warning-text]') || pill;
      const msg = message || (() => {
        const step = this.stepOrder[this.currentStepIndex];
        const ring = this.rings[(this.currentRingIndex || 0)];
        if (step === 'metal') {
          if (ring && ring.metalType && !ring.carat) return 'Pick a carat to continue.';
          if (ring && !ring.metalType) return 'Pick a metal to continue.';
        }
        if (step === 'size') {
          if (ring && !ring.size && ring.sizeRegion !== 'idkms') return 'Pick a ring size to continue.';
        }
        if (step === 'diamond') {
          // RSFY-707 — Band Only needs a width pick. Only the footer pill
          // is shown (inline warning was removed — duplicate signal was
          // noisy per RSFY-707 follow-up feedback).
          if (ring && ring.hasDiamond === false && !ring.bandWidth) {
            return 'Please select a band width to continue.';
          }
        }
        if (step === 'engraving') {
          const d = ring && ring.engravingDetails;
          if (d) {
            const outsideMissing = d.outsideEngraving && !(d.outsideText || '').trim();
            const insideMissing = d.insideEngraving && !(d.insideText || '').trim();
            if (outsideMissing && insideMissing) return 'Add engraving text on both sides to continue.';
            if (outsideMissing) return 'Add outside engraving text to continue.';
            if (insideMissing) return 'Add inside engraving text to continue.';
            if (d.outsideEngraving && !d.outsideLanguage) return 'Pick an outside language to continue.';
            if (d.insideEngraving && !d.insideLanguage) return 'Pick an inside language to continue.';
            if (d.hasEngraving && !d.outsideEngraving && !d.insideEngraving) return 'Pick outside, inside, or both to continue.';
          }
        }
        return 'Make a selection to continue.';
      })();
      text.textContent = msg;
      pill.hidden = false;

      // On mobile the pill is floated as a toast over the Total row so it
      // doesn't push the price off-screen. Auto-dismiss after 3s so it
      // doesn't linger over the running total once the customer has had
      // a chance to read it. Desktop keeps the persistent pill — there's
      // enough horizontal room for it to coexist with the Total.
      if (this._stepWarningTimer) {
        clearTimeout(this._stepWarningTimer);
        this._stepWarningTimer = null;
      }
      const isMobile = typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(max-width: 989px)').matches;
      if (isMobile) {
        this._stepWarningTimer = setTimeout(() => {
          this._stepWarningTimer = null;
          // Re-check the current ring state: if it's still incomplete we
          // leave the toast hidden anyway — the customer will see it
          // again the next time they tap Continue.
          pill.hidden = true;
        }, 3000);
      }
    }

    clearStepWarning() {
      const pill = this.querySelector('[data-step-warning]');
      if (!pill) return;
      pill.hidden = true;
      if (this._stepWarningTimer) {
        clearTimeout(this._stepWarningTimer);
        this._stepWarningTimer = null;
      }
    }

    // =========================================================================
    // Summary & Add to Cart
    // =========================================================================

    updateSummary() {
      this._renderInscriptionPreview();

      let html = '';
      const ringNames = ['Ring One', 'Ring Two', 'Ring Three'];

      for (let i = 0; i < this.rings.length; i++) {
        const ring = this.rings[i];
        const variant = this.findMatchingVariant(ring);
        ring.selectedVariant = variant;

        if (this.rings.length > 1) {
          html += `<div class="risify-rb__summary-ring-title">${ringNames[i] || 'Ring ' + (i + 1)}</div>`;
        }

        // Build the 8 spec rows the Figma slip carries (Figma 6019:6243).
        // Each row carries the source step so the Edit link can route
        // back to the right place. Diamond / Outside / Inside / Hallmark
        // appear only when relevant so the slip stays compact for plain
        // bands.
        const rows = [];

        if (ring.metalType) {
          const metal = ring.carat ? `${ring.metalType}, ${ring.carat}` : ring.metalType;
          rows.push({ label: 'METAL', value: this._escapeHtml(metal), step: 'metal' });
        }

        if (ring.hasDiamond === true && ring.settingStyle) {
          rows.push({
            label: 'SETTING',
            value: this._escapeHtml(ring.settingStyle.replace(/^\w/, (c) => c.toUpperCase())),
            step: 'diamond',
          });
        }

        if (ring.hasDiamond === true) {
          const dParts = [this._caratDisplayLabel(ring.diamondCarat)];
          if (this._hasCutData(ring) && ring.diamondCut) {
            dParts.push(ring.diamondCut.replace(/^\w/, (c) => c.toUpperCase()));
          }
          const map = QUALITY_MAPPING[ring.diamondQuality];
          if (map) {
            dParts.push(map.color);
            dParts.push(map.clarity);
          } else if (ring.diamondQuality) {
            dParts.push(ring.diamondQuality);
          }
          rows.push({ label: 'DIAMOND', value: this._escapeHtml(dParts.join(' · ')), step: 'diamond' });
        } else if (ring.hasDiamond === false) {
          rows.push({ label: 'DIAMOND', value: 'Plain band', step: 'diamond' });
        }

        const ed = ring.engravingDetails;
        if (ed && ed.hasEngraving) {
          if (ed.outsideEngraving && ed.outsideText) {
            const lang = ed.outsideLanguage === 'elvish' ? 'Tengwar' : 'English';
            rows.push({
              label: 'OUTSIDE',
              value: `${lang}: &ldquo;${this._escapeHtml(ed.outsideText)}&rdquo;`,
              step: 'engraving',
            });
          }
          if (ed.insideEngraving && ed.insideText) {
            const lang = ed.insideLanguage === 'elvish' ? 'Tengwar' : 'English';
            rows.push({
              label: 'INSIDE',
              value: `${lang}: &ldquo;${this._escapeHtml(ed.insideText)}&rdquo;`,
              step: 'engraving',
            });
          }
        } else if (ed && ed.hasEngraving === false) {
          rows.push({ label: 'ENGRAVING', value: 'None', step: 'engraving' });
        }

        if (ed && ed.hallmark != null) {
          rows.push({
            label: 'HALLMARK',
            value: ed.hallmark ? "JH maker's mark" : 'No hallmark',
            step: 'engraving',
          });
        }

        if (ring.size || (ring.sizeUnknown || ring.sizeRegion === 'idkms')) {
          rows.push({
            label: 'SIZE',
            value: this._escapeHtml(this._formatSizeForReview(ring)),
            step: 'size',
          });
        }

        // Forging is always last and is not editable — no step link.
        const forgingText = (this.config && this.config.forgingTimelineText) || 'About 2 to 3 weeks at the bench';
        rows.push({ label: 'FORGING', value: forgingText, step: null });

        // Render the spec table.
        rows.forEach((row, idx) => {
          if (idx > 0) {
            html += `<div class="risify-rb__review-spec-divider" aria-hidden="true"></div>`;
          }
          const editLink = row.step
            ? `<button type="button" class="risify-rb__review-spec-edit" data-review-edit-step="${row.step}">Edit</button>`
            : '';
          html += `
            <div class="risify-rb__review-spec-row">
              <span class="risify-rb__review-spec-label">${row.label}</span>
              <span class="risify-rb__review-spec-value">${row.value}</span>
              ${editLink}
            </div>
          `;
        });

        // Price breakdown — Figma 6019:6519. Always-on hairline + Milano
        // Red toggle. Breakdown body is hidden by default; JS toggles
        // `[data-review-breakdown-open]` on the spec card.
        const breakdownRows = this._buildPriceBreakdown(ring, variant);
        if (breakdownRows.length > 0) {
          html += `<div class="risify-rb__review-spec-divider" aria-hidden="true"></div>`;
          html += `<div class="risify-rb__review-breakdown" data-review-breakdown hidden>`;
          breakdownRows.forEach(([label, amount]) => {
            html += `
              <div class="risify-rb__review-breakdown-row">
                <span class="risify-rb__review-breakdown-label">${this._escapeHtml(label)}</span>
                <span class="risify-rb__review-breakdown-value">${amount}</span>
              </div>
            `;
          });
          html += `</div>`;
          html += `
            <button type="button" class="risify-rb__review-breakdown-toggle" data-review-breakdown-toggle aria-expanded="false">
              <span data-review-breakdown-toggle-label>View price breakdown</span>
            </button>
          `;
          html += `<div class="risify-rb__review-spec-divider" aria-hidden="true"></div>`;
        }

        if (variant) {
          html += `
            <div class="risify-rb__review-total-row">
              <span class="risify-rb__review-total-label">Total</span>
              <span class="risify-rb__review-total-value">${this.formatMoney(variant.price)}</span>
            </div>
          `;
          if (!variant.available) {
            html += `<div class="risify-rb__summary-warning">This variant is currently unavailable.</div>`;
          }
        } else if (ring.metalType) {
          html += `<div class="risify-rb__summary-warning">No matching variant found. Please check your selections.</div>`;
        }
      }

      if (this.cartSummary) {
        this.cartSummary.innerHTML = html;
        // Re-wire breakdown toggle + edit-link click handlers since the
        // markup was just replaced.
        this.cartSummary.querySelectorAll('[data-review-breakdown-toggle]').forEach((btn) => {
          btn.addEventListener('click', () => this._toggleReviewBreakdown(btn));
        });
        this.cartSummary.querySelectorAll('[data-review-edit-step]').forEach((btn) => {
          btn.addEventListener('click', () => this._jumpToStep(btn.dataset.reviewEditStep));
        });
      }
      this.updateFooterTotal();
    }

    /**
     * Minimal HTML escape for spec values (engraving text, recipient
     * names) that flow from user input. Allows entities already in
     * pre-built strings (`&ldquo;` etc.) by encoding only the dangerous
     * five characters first and unescaping the same characters that the
     * caller may have inserted intentionally.
     */
    _escapeHtml(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    /**
     * Build the price breakdown rows shown when the customer opens the
     * "View price breakdown" toggle.
     *
     * Per Halfdan + Onur (2026-06-10): do NOT itemize Band + Diamond as
     * separate lines. Listing the band price alone makes customers ask to
     * buy "just the band" with their own diamond, which is not the buying
     * flow we want. The catalog ships bundled (band + diamond) variants
     * and per-axis component prices don't cleanly map across configs.
     *
     *   ✅ allowed: total configured ring price, engraving option delta,
     *              diamond-quality upgrade delta
     *   ❌ NOT allowed: Band $X / Diamond $Y itemization
     *
     * We compute deltas against the *cheapest available* variant on the
     * same option axis (engraving = "none", quality = base tier) so each
     * delta represents how much the customer's choices ADD beyond a
     * baseline configuration.
     */
    _buildPriceBreakdown(ring, variant) {
      if (!ring || !variant || variant.price == null) return [];
      const rows = [];

      const engravingDelta = this._calcEngravingDelta(ring, variant);
      if (engravingDelta && engravingDelta.amount > 0) {
        rows.push([engravingDelta.label, `+ ${this.formatMoney(engravingDelta.amount)}`]);
      }

      const qualityDelta = this._calcQualityDelta(ring, variant);
      if (qualityDelta && qualityDelta.amount > 0) {
        rows.push([qualityDelta.label, `+ ${this.formatMoney(qualityDelta.amount)}`]);
      }

      return rows;
    }

    /**
     * Engraving add-on delta = active variant's price minus the cheapest
     * sibling variant that has "no engraving" on the same product. Yields
     * a label like "Engraving (one side)" or "Engraving (two sides)".
     */
    _calcEngravingDelta(ring, variant) {
      const product = ring?.selectedProduct;
      if (!product || !variant) return null;
      const engIdx = this.findOptionIndex(product, 'engraving');
      if (engIdx < 0) return null;
      const optKey = `option${engIdx + 1}`;
      const chosen = (variant[optKey] || '').toLowerCase();
      if (!chosen || /none|no engraving|blank/.test(chosen)) return null;

      // Find a "no engraving" sibling that matches the other selected axes.
      const baseline = product.variants.find((v) => {
        const optVal = (v[optKey] || '').toLowerCase();
        if (!/none|no engraving|blank/.test(optVal)) return false;
        return product.options.every((_, i) => {
          if (i === engIdx) return true;
          const k = `option${i + 1}`;
          return (v[k] || '') === (variant[k] || '');
        });
      });
      if (!baseline || baseline.price == null) return null;
      const amount = variant.price - baseline.price;
      if (amount <= 0) return null;

      // Friendly label — "Engraving (one side)" vs "Engraving (two sides)".
      let suffix = '';
      if (/both|inside.+outside|outside.+inside/.test(chosen)) suffix = '(two sides)';
      else if (/inside|outside/.test(chosen)) suffix = '(one side)';
      return { label: `Engraving ${suffix}`.trim(), amount };
    }

    /**
     * Diamond-quality upgrade delta = active variant's price minus the
     * cheapest sibling variant on the same product (so we only surface
     * an "Upgrade" row when the customer picked a non-base tier).
     */
    _calcQualityDelta(ring, variant) {
      const product = ring?.selectedProduct;
      if (!product || !variant) return null;
      const stoneIdx = this.findOptionIndex(product, 'stone');
      if (stoneIdx < 0) return null;
      const optKey = `option${stoneIdx + 1}`;
      const chosen = variant[optKey] || '';
      if (!chosen) return null;

      // Cheapest sibling with the same non-stone axes.
      let cheapest = null;
      for (const v of product.variants) {
        if (v.price == null) continue;
        const sameAxes = product.options.every((_, i) => {
          if (i === stoneIdx) return true;
          const k = `option${i + 1}`;
          return (v[k] || '') === (variant[k] || '');
        });
        if (!sameAxes) continue;
        if (cheapest == null || v.price < cheapest.price) cheapest = v;
      }
      if (!cheapest || cheapest.price === variant.price) return null;
      const amount = variant.price - cheapest.price;
      if (amount <= 0) return null;
      return { label: `Stone quality upgrade (${chosen})`, amount };
    }

    /**
     * Toggle the price-breakdown body open/closed and flip the label
     * between "View" and "Hide". Operates on the button that was
     * clicked so multi-ring slips can each toggle independently.
     */
    _toggleReviewBreakdown(btn) {
      if (!btn) return;
      const card = btn.closest('.risify-rb__review-card') || this.cartSummary;
      if (!card) return;
      const body = card.querySelector('[data-review-breakdown]');
      const labelEl = btn.querySelector('[data-review-breakdown-toggle-label]') || btn;
      if (!body) return;
      const open = !body.hidden;
      body.hidden = open;
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      labelEl.textContent = open ? 'View price breakdown' : 'Hide price breakdown';
    }

    /**
     * Edit-link handler — routes back to the source step (Figma 6019:6310
     * et al.). Falls back to the stepper navigation if a step isn't in
     * the canonical order array.
     */
    _jumpToStep(stepName) {
      if (!stepName) return;
      const idx = this.stepOrder.indexOf(stepName);
      if (idx < 0) return;
      this.currentStepIndex = idx;
      if (typeof this.showStep === 'function') {
        this.showStep(stepName);
      } else if (typeof this.goToStep === 'function') {
        this.goToStep(stepName);
      }
    }

    /**
     * RSFY-646 — Inline Tengwar inscription preview in the slip.
     * Reads ring 0's engraving text, runs it through window.transcribe
     * if Elvish, renders in the Tengwar Annatar font.
     */
    _renderInscriptionPreview() {
      const wrap = this.querySelector('[data-review-inscription]');
      if (!wrap) return;
      const outRow = this.querySelector('[data-review-inscription-outside-row]');
      const inRow = this.querySelector('[data-review-inscription-inside-row]');
      const out = this.querySelector('[data-review-inscription-outside]');
      const inn = this.querySelector('[data-review-inscription-inside]');
      const ring = this.rings[0];
      if (!ring) { wrap.hidden = true; return; }
      const d = ring.engravingDetails;
      const haveOut = d && d.hasEngraving && d.outsideEngraving && d.outsideText && d.outsideText.trim();
      const haveIn = d && d.hasEngraving && d.insideEngraving && d.insideText && d.insideText.trim();
      if (!haveOut && !haveIn) { wrap.hidden = true; return; }
      wrap.hidden = false;

      const renderSide = (row, target, text, lang) => {
        if (!row || !target) return;
        if (!text || !text.trim()) { row.hidden = true; return; }
        let render = text;
        if (lang === 'elvish' && typeof window.transcribe === 'function') {
          // Same options the canonical translator uses — see renderElvishPreview.
          try {
            render = window.transcribe(text, {
              noAchLaut: true,
              medialOre: true,
              language: 'english',
            });
          } catch (e) {}
          // Render into the target using our HTML-aware Elvish helper so
          // unsupported-character placeholders (ö, ş, …) keep their
          // browser-default dotted underline glyph and match the canonical
          // translator page byte-for-byte.
          this._renderTranscribedTextInto(target, render);
        } else {
          target.textContent = render;
        }
        target.classList.toggle('risify-rb__review-inscription--elvish', lang === 'elvish');
        row.hidden = false;
      };
      renderSide(outRow, out, haveOut ? d.outsideText : '', d && d.outsideLanguage);
      renderSide(inRow, inn, haveIn ? d.insideText : '', d && d.insideLanguage);
    }

    async addToCart() {
      const items = [];

      // Bug fix #8 — At add-to-cart time, surface any incomplete sibling rings
      // up-front (cross-ring completeness check only happens here).
      if (this.ringCount > 1) {
        const incompleteRings = [];
        for (let i = 0; i < this.ringCount; i++) {
          const ringIsComplete = ['metal', 'diamond', 'engraving', 'size']
            .every((step) => this.isStepCompleteForRing(step, this.rings[i]));
          if (!ringIsComplete) incompleteRings.push(i);
        }
        if (incompleteRings.length > 0) {
          const labels = incompleteRings.map((i) => `Ring ${i + 1}`).join(', ');
          this.showError(`${labels} still need${incompleteRings.length === 1 ? 's' : ''} to be completed.`);
          // Jump to the first incomplete ring so the customer can finish it.
          this._switchToRing(incompleteRings[0]);
          return;
        }
      }

      for (let i = 0; i < this.rings.length; i++) {
        const ring = this.rings[i];
        const variant = this.findMatchingVariant(ring);

        if (!variant) {
          this.showError(`Please complete all selections for ${this.rings.length > 1 ? 'Ring ' + (i + 1) : 'your ring'}.`);
          return;
        }

        if (!variant.available) {
          this.showError(`The selected option for ${this.rings.length > 1 ? 'Ring ' + (i + 1) : 'your ring'} is currently unavailable.`);
          return;
        }

        const properties = {};
        const d = ring.engravingDetails;
        const prefix = this.rings.length > 1 ? `Ring ${i + 1} - ` : 'Single Ring - ';

        if (d && d.hasEngraving !== null) {
          properties[`${prefix}Add Custom Engraving On The Outside?`] = d.outsideEngraving ? 'Yes' : 'No';
          if (d.outsideEngraving && d.outsideLanguage) {
            properties[`${prefix}Choose Outside Engraving Language`] = d.outsideLanguage === 'elvish' ? 'Elvish Text' : 'English Text';
            if (d.outsideText) {
              properties[`${prefix}Outside Engraving Text Input`] = d.outsideText;
            }
            if (d.outsideLanguage === 'english') {
              properties[`${prefix}Outside Engraving Font`] = d.outsideFont;
            }
          }
          properties[`${prefix}Add Custom Engraving On The Inside?`] = d.insideEngraving ? 'Yes' : 'No';
          if (d.insideEngraving && d.insideLanguage) {
            properties[`${prefix}Choose Inside Engraving Language`] = d.insideLanguage === 'elvish' ? 'Elvish Text' : 'English Text';
            if (d.insideText) {
              properties[`${prefix}Inside Engraving Text Input`] = d.insideText;
            }
            if (d.insideLanguage === 'english') {
              properties[`${prefix}Inside Engraving Font`] = d.insideFont;
            }
          }
        }
        // Bug J — hallmark is independent of engraving; emit the property
        // whether the customer engraves or not. Defaults to "Yes" per the
        // workshop signature convention; only set to "No" when the customer
        // explicitly picked the No Hallmark pill.
        if (d) {
          properties[`${prefix}Include Jens Hansen signature and gold quality hallmarks inside ring?`] = d.hallmark === false ? 'No' : 'Yes';
        }

        // RSFY-782 — the order used to carry TWO size rows: the canonical
        // UK letter in `Ring Size` plus the customer's scale in a second
        // `Ring Size (Selected)` row (order #22408 showed "Q½" and
        // "8½ (US)" and the client flagged it). One property now, rendered
        // by the same formatter the review slip and summary rail already
        // use, so the order records exactly what the customer picked —
        // scale tag included ("8½ (US)"), deferred wording preserved.
        // NOTE for support: builder orders say "I'll confirm my size
        // later" while PDP orders still say "I don't know my size" — same
        // meaning, two phrasings, until the PDP is aligned too (the
        // wording history lives on _formatSizeForReview).
        if (ring.size || ring.sizeUnknown || ring.sizeRegion === 'idkms') {
          properties[`${prefix}Ring Size`] = this._formatSizeForReview(ring);
        }
        // RSFY-801 — the effective width, not the picker's lingering value:
        // a diamond ring is made on its setting's fixed band (4mm / Petite
        // 3mm), and the step now says so on screen; the order must agree.
        const effectiveWidth = this._effectiveBandWidth(ring);
        if (effectiveWidth) {
          properties[`${prefix}Band Width`] = `${effectiveWidth} mm`;
        }
        // RSFY-641: setting style
        if (ring.settingStyle) {
          properties[`${prefix}Setting Style`] = ring.settingStyle.replace(/^\w/, (c) => c.toUpperCase());
        }
        items.push({ id: variant.id, quantity: 1, properties });
      }

      // Loading hook — apply to whichever button the customer actually used
      // to trigger the cart submission (dedicated review button if present,
      // otherwise the footer Continue, which doubles as Send to the workshop
      // on the Review step).
      const loadingBtn = this.addToCartBtn || this.navContinue;
      // RSFY-805 motion plan 007 — a busy state the customer can see: label,
      // spinner (CSS, on the --loading class) and aria-busy while the request
      // runs; everything restores in `finally`.
      const loadingLabel = loadingBtn ? (loadingBtn.querySelector('[data-nav-continue-label]') || loadingBtn) : null;
      const restingLabel = loadingLabel ? loadingLabel.textContent : '';
      if (loadingBtn) {
        loadingBtn.setAttribute('aria-disabled', 'true');
        loadingBtn.setAttribute('aria-busy', 'true');
        loadingBtn.classList.add('risify-rb__add-to-cart--loading');
        if (loadingLabel) loadingLabel.textContent = 'ADDING…';
      }
      this.hideError();

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        const data = await response.json();

        if (!response.ok) {
          this.showError(data.description || data.message || 'Could not add to cart.');
          return;
        }

        this.hideError();
        this.closeEditor();
        window.location = window.routes?.cart_url || '/cart';
      } catch (err) {
        console.error('Ring Builder: Add to cart failed', err);
        this.showError('Something went wrong. Please try again.');
      } finally {
        if (loadingBtn) {
          loadingBtn.removeAttribute('aria-disabled');
          loadingBtn.removeAttribute('aria-busy');
          loadingBtn.classList.remove('risify-rb__add-to-cart--loading');
          if (loadingLabel) loadingLabel.textContent = restingLabel;
        }
      }
    }

    showError(msg) {
      if (this.errorMessage) {
        this.errorMessage.textContent = msg;
        this.errorMessage.style.display = '';
      }
    }

    hideError() {
      if (this.errorMessage) {
        this.errorMessage.style.display = 'none';
      }
    }
  }

  customElements.define('risify-ring-builder', RisifyRingBuilder);
}
